
import { createClient } from "@supabase/supabase-js";
import UnbrokeLanding from "./UnbrokeLanding";
import { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from "react";
// ─── Design Tokens ────────────────────────────────────────────────────────────
const G = {
  gold: "#C9A84C", goldL: "#E2C069", goldD: "#8B6E28", goldDim: "#2E2514",
  bg0: "#07070A", bg1: "#0D0D11", bg2: "#131318", bg3: "#1A1A21", bg4: "#21212A",
  border: "#1E1E28", borderGold: "#2A2010",
  text: "#EDEDE6", muted: "#72727C", dim: "#333340",
  green: "#4EAD8A", greenD: "#2A6E55", greenDim: "#0E2219",
  red: "#C05A5A", redD: "#7A3333", redDim: "#1E0F0F",
  blue: "#5A8EC0", blueDim: "#0F1C2E",
  purple: "#9B7ECF", purpleDim: "#1A1030",
  orange: "#E8894A", orangeDim: "#2E1A0A",
};

// ─── Themes ───────────────────────────────────────────────────────────────────
const AURORA_TOKENS_AURORA = {
  au_bg0:"#121117", au_bg1:"#17161E", au_bg2:"#1D1C26", au_bg3:"#26242F", au_bg4:"#322F3D",
  au_line:"rgba(255,255,255,0.08)", au_lineStrong:"rgba(255,255,255,0.14)",
  au_ink:"#F7F4EE", au_inkSoft:"#A8A3B5", au_inkFaint:"#67637A", au_inkGhost:"#2A2834",
  au_amber:"#E8A85C", au_amberD:"#B5803E", au_amberL:"#F2C68A",
  au_mint:"#5FCB9A", au_mintD:"#3C8A6A",
  au_coral:"#E87E6E", au_coralD:"#B85647",
  au_cyan:"#E8A85C", au_cyanD:"#B5803E",
  au_violet:"#A8A3B5", au_violetD:"#67637A",
};

const THEMES = {
  aurora: {
    name: "Aurora", icon: "✦",
    gold: "#FFB561", goldL: "#FFD09E", goldD: "#D88A3E", goldDim: "#1A0F00",
    bg0: "#08070C", bg1: "#0E0C14", bg2: "#15121C", bg3: "#1D1828", bg4: "#2A2438",
    border: "rgba(255,255,255,0.06)", borderGold: "rgba(255,181,97,0.15)",
    text: "#F5EFE6", muted: "#B8B0A8", dim: "#2E2A38",
    green: "#6FE9B5", greenD: "#3FA67E", greenDim: "#091F18",
    red: "#FF8E7C", redD: "#C75A4D", redDim: "#200810",
    blue: "#7BB7FF", blueDim: "#0D1235",
    purple: "#B098FF", purpleDim: "#160B30",
    orange: "#FFAA60", orangeDim: "#281800",
    ...AURORA_TOKENS_AURORA,
  },
 
  unbroke: {
    name: "Unbroke", icon: "⚡",
    // Deep violet-indigo with soft lime + magenta
    gold: "#A78BFA",
    goldL: "#C4B5FD",
    goldD: "#7C3AED",
    goldDim: "#1E1040",
    bg0: "#0A0A12",
    bg1: "#10101E",
    bg2: "#17172A",
    bg3: "#1E1E36",
    bg4: "#282840",
    border: "#2A2A45",
    borderGold: "#3D2A70",
    text: "#F0EEFF",
    muted: "#9090B8",
    dim: "#3A3A58",
    green: "#86EFAC",
    greenD: "#4ADE80",
    greenDim: "#0A2010",
    red: "#F87171",
    redD: "#DC2626",
    redDim: "#220A0A",
    blue: "#60A5FA",
    blueDim: "#0A1428",
    purple: "#E879F9",
    purpleDim: "#2A0830",
    orange: "#FB923C",
    orangeDim: "#200C00",
    au_bg0: "#0A0A12",
    au_bg1: "#10101E",
    au_bg2: "#17172A",
    au_bg3: "#1E1E36",
    au_bg4: "#282840",
    au_line: "rgba(167,139,250,0.12)",
    au_lineStrong: "rgba(167,139,250,0.25)",
    au_ink: "#F0EEFF",
    au_inkSoft: "#9090B8",
    au_inkFaint: "#3A3A58",
    au_inkGhost: "#10101E",
    au_amber: "#A78BFA",
    au_amberD: "#7C3AED",
    au_amberL: "#C4B5FD",
    au_mint: "#86EFAC",
    au_mintD: "#4ADE80",
    au_coral: "#F87171",
    au_coralD: "#DC2626",
    au_cyan: "#60A5FA",
    au_cyanD: "#2563EB",
    au_violet: "#E879F9",
    au_violetD: "#A21CAF",
  },
 
  daylight: {
    name: "Daylight", icon: "☀️",
    // KEY FIX: Every single token is dark enough to read on cream.
    // The sidebar uses T.gold, T.muted, T.dim, T.text, T.bg1, T.bg3, T.border —
    // all of these MUST have sufficient contrast on cream (#FEF9EE).
 
    gold: "#92400E",        // dark amber — nav active color, very readable
    goldL: "#B45309",
    goldD: "#78350F",
    goldDim: "#FEF3C7",
    bg0: "#FEFCE8",
    bg1: "#F5ECD6",         // sidebar bg — warm tan, not pure white
    bg2: "#EDE0C4",
    bg3: "#E4D3B0",         // active nav item bg
    bg4: "#D9C49A",
    border: "#C4A96A",      // visible border on cream
    borderGold: "#B07820",
    text: "#1C0A00",        // near-black — nav labels, main text
    muted: "#4A3010",       // DARK warm brown — nav inactive labels (was too light)
    dim: "#7A5C28",         // medium brown — section headers, hints
    green: "#064E3B",       // dark forest green
    greenD: "#065F46",
    greenDim: "#ECFDF5",
    red: "#7F1D1D",         // dark crimson
    redD: "#991B1B",
    redDim: "#FEF2F2",
    blue: "#1E3A8A",        // dark navy
    blueDim: "#EFF6FF",
    purple: "#4C1D95",      // dark purple
    purpleDim: "#F5F3FF",
    orange: "#7C2D12",      // dark burnt orange
    orangeDim: "#FFF7ED",
    // Aurora tokens — same dark-on-cream treatment
    au_bg0: "#FEFCE8",
    au_bg1: "#F5ECD6",
    au_bg2: "#EDE0C4",
    au_bg3: "#E4D3B0",
    au_bg4: "#D9C49A",
    au_line: "rgba(100,65,10,0.18)",
    au_lineStrong: "rgba(100,65,10,0.35)",
    au_ink: "#1C0A00",          // dark — readable everywhere
    au_inkSoft: "#4A3010",      // dark brown — nav labels, subtitles
    au_inkFaint: "#7A5C28",     // medium brown — hints, secondary
    au_inkGhost: "#D9C49A",
    au_amber: "#92400E",
    au_amberD: "#78350F",
    au_amberL: "#B45309",
    au_mint: "#064E3B",
    au_mintD: "#065F46",
    au_coral: "#7F1D1D",
    au_coralD: "#991B1B",
    au_cyan: "#1E3A8A",
    au_cyanD: "#1E40AF",
    au_violet: "#4C1D95",
    au_violetD: "#5B21B6",
  },
};
// ─── Currency Config ──────────────────────────────────────────────────────────
const CURRENCIES = [
  { code: "USD", symbol: "$",  locale: "en-US", name: "US Dollar" },
  { code: "AUD", symbol: "A$", locale: "en-AU", name: "Australian Dollar" },
  { code: "NZD", symbol: "NZ$",locale: "en-NZ", name: "New Zealand Dollar" },
  { code: "INR", symbol: "₹",  locale: "en-IN", name: "Indian Rupee" },
  { code: "JPY", symbol: "¥",  locale: "ja-JP", name: "Japanese Yen" },
  { code: "EUR", symbol: "€",  locale: "de-DE", name: "Euro" },
];

// ─── Subscription Tiers ───────────────────────────────────────────────────────
const TIERS = [
  {
    id: 0, name: "Free", price: 0, color: "#72727C", colorDim: "#1A1A21",
    desc: "Start tracking for free — forever. No credit card.",
    features: [
      "Manual income & expense entry",
      "Dashboard, charts & cash flow",
      "3 months of history",
      "Up to 100 transactions / month",
      "FIRE Calculator & Debt Tracker",
      "Money Personality insight",
    ],
  },
  {
    id: 1, name: "Growth", price: 24, color: "#C9A84C", colorDim: "#2E2514", recommended: true,
    desc: "AI insights, unlimited history, and deep behavioral analysis.",
    features: [
      "Everything in Free",
      "Unlimited history & transactions",
      "AI Behavioral Analysis",
      "3-scenario wealth modeling",
      "Monthly behavioral reports",
      "Regret Tracker & Bank Import",
      "Strategic Freedom Map",
      "Behavioral Intelligence Score",
    ],
  },
  {
    id: 2, name: "Elite", price: 49, color: "#4EAD8A", colorDim: "#0E2219",
    desc: "Your private AI wealth advisor, always on call.",
    features: [
      "Everything in Growth",
      "AI Wealth Assistant (unlimited chat)",
      "Real-time portfolio Q&A",
      "Custom goal tracking",
      "Priority support",
    ],
  },
];

// ─── Categories ───────────────────────────────────────────────────────────────
const INCOME_CATS = [
  { name: "Salary", icon: "💼", color: G.blue },
  { name: "Business", icon: "🏢", color: G.gold },
  { name: "Rental", icon: "🏠", color: G.green },
  { name: "Dividends", icon: "📈", color: G.purple },
  { name: "Freelance", icon: "⚡", color: G.orange },
  { name: "Capital Gains", icon: "🚀", color: G.goldL },
  { name: "Other Income", icon: "💰", color: G.muted },
];

const EXPENSE_CATS = [
  { name: "Housing", icon: "🏠", color: "#C05A5A" },
  { name: "Food & Dining", icon: "🍽️", color: G.orange },
  { name: "Transport", icon: "🚗", color: G.blue },
  { name: "Healthcare", icon: "💊", color: G.green },
  { name: "Shopping", icon: "🛍️", color: G.purple },
  { name: "Entertainment", icon: "🎬", color: G.gold },
  { name: "Utilities", icon: "⚡", color: "#5DAED4" },
  { name: "Insurance", icon: "🔒", color: G.muted },
  { name: "Subscriptions", icon: "📱", color: G.purple },
  { name: "Travel", icon: "✈️", color: G.green },
  { name: "Education", icon: "📚", color: G.goldL },
  { name: "Investments", icon: "📊", color: G.gold },
  { name: "Savings", icon: "💾", color: G.green },
  { name: "Other", icon: "📦", color: G.dim },
];

// ─── Supabase Config — PASTE YOUR VALUES HERE ─────────────────────────────────
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// ─── Supabase REST Helpers ────────────────────────────────────────────────────
const sbHeaders = () => ({
  "apikey": SUPABASE_ANON_KEY,
  "Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
  "Content-Type": "application/json",
});

async function sbGetTxs(clientId) {
  try {
    const r = await fetch(
      `${SUPABASE_URL}/rest/v1/transactions?client_id=eq.${encodeURIComponent(clientId)}&order=created_at.asc`,
      { headers: sbHeaders() }
    );
    if (!r.ok) return [];
    return await r.json();
  } catch { return []; }
}

async function sbInsertTx(tx) {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/transactions`, {
      method: "POST",
      headers: { ...sbHeaders(), "Prefer": "return=minimal" },
      body: JSON.stringify(tx),
    });
    return r.ok;
  } catch { return false; }
}

async function sbDeleteTx(id) {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/transactions?id=eq.${id}`, {
      method: "DELETE",
      headers: sbHeaders(),
    });
    return r.ok;
  } catch { return false; }
}

async function sbUpdateTx(id, patch) {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/transactions?id=eq.${id}`, {
      method: "PATCH",
      headers: { ...sbHeaders(), "Prefer": "return=minimal" },
      body: JSON.stringify(patch),
    });
    return r.ok;
  } catch { return false; }
}

// ─── Supabase Debt Helpers ───────────────────────────────────────────────────
async function sbGetDebts(clientId) {
  try {
    const r = await fetch(
      `${SUPABASE_URL}/rest/v1/debts?client_id=eq.${encodeURIComponent(clientId)}&order=created_at.asc`,
      { headers: sbHeaders() }
    );
    if (!r.ok) return [];
    return await r.json();
  } catch { return []; }
}
async function sbInsertDebt(debt) {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/debts`, {
      method: "POST",
      headers: { ...sbHeaders(), "Prefer": "return=representation" },
      body: JSON.stringify(debt),
    });
    if (!r.ok) return null;
    const d = await r.json();
    return d[0] || null;
  } catch { return null; }
}
async function sbUpdateDebt(id, patch) {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/debts?id=eq.${id}`, {
      method: "PATCH",
      headers: { ...sbHeaders(), "Prefer": "return=minimal" },
      body: JSON.stringify(patch),
    });
    return r.ok;
  } catch { return false; }
}
async function sbDeleteDebt(id) {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/debts?id=eq.${id}`, {
      method: "DELETE",
      headers: sbHeaders(),
    });
    return r.ok;
  } catch { return false; }
}


// ─── Supabase Helpers: FIRE Settings ─────────────────────────────────────────
async function sbGetFireSettings(clientId) {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/fire_settings?client_id=eq.${encodeURIComponent(clientId)}&select=*`, { headers: sbHeaders() });
    if (!r.ok) return null;
    const rows = await r.json();
    return rows[0] || null;
  } catch { return null; }
}
async function sbSaveFireSettings(settings) {
  try {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/fire_settings`, {
      method: "POST",
      headers: { ...sbHeaders(), "Prefer": "resolution=merge-duplicates,return=minimal" },
      body: JSON.stringify(settings),
    });
    return r.ok;
  } catch { return false; }
}

async function sbSavePhoneLink(phone, clientId) {
  try {
    // 1. Save to phone_links (existing)
    const r1 = await fetch(`${SUPABASE_URL}/rest/v1/phone_links`, {
      method: "POST",
      headers: { ...sbHeaders(), "Prefer": "resolution=merge-duplicates,return=minimal" },
      body: JSON.stringify({ phone, client_id: clientId }),
    });

    // 2. Also update users.whatsapp_phone so the backend can find this user
    const r2 = await fetch(
      `${SUPABASE_URL}/rest/v1/users?client_id=eq.${encodeURIComponent(clientId)}`,
      {
        method: "PATCH",
        headers: { ...sbHeaders(), "Prefer": "return=minimal" },
        body: JSON.stringify({ whatsapp_phone: phone }),
      }
    );

    return r1.ok && r2.ok;
  } catch { return false; }
}

// ─── localStorage Helpers (for user auth only) ────────────────────────────────
async function sGet(key) {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
async function sSet(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) { console.error("storage:", e); }
}

// ─── Supabase Auth Client ─────────────────────────────────────────────────────
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Find an existing user row by email, or create a new one with an auto-assigned Client ID.
async function findOrCreateUserByEmail(email, displayName) {
  try {
    // 1. Always look up first — never mint a new ID if the user already exists
    const r = await fetch(
      `${SUPABASE_URL}/rest/v1/users?email=eq.${encodeURIComponent(email)}&select=*`,
      { headers: sbHeaders() }
    );
    const rows = await r.json();
    if (rows && rows[0]) {
      const u = rows[0];
      return { clientId: u.client_id, name: u.name, email: u.email, goal: u.goal||"", plan: u.plan ?? 0, pin: u.pin||"", createdAt: u.created_at, trialStart: u.trial_start };
    }
    // 2. New user — mint a Client ID and insert (with explicit id to avoid missing-default errors)
    const newId = genClientId();
    const now = new Date().toISOString();
    const name = displayName || email.split("@")[0];
    const insertPayload = {
      id: crypto.randomUUID(),
      client_id: newId,
      name,
      email,
      plan: 0,
      goal: "",
      pin: "",
    };
    const ins = await fetch(`${SUPABASE_URL}/rest/v1/users`, {
      method: "POST",
      headers: { ...sbHeaders(), "Prefer": "resolution=ignore-duplicates,return=representation" },
      body: JSON.stringify(insertPayload),
    });
    const insData = await ins.json();
    console.log("INSERT users response:", ins.status, insData);

    // 3. Re-fetch regardless — if insert was a no-op due to duplicate email, this returns the existing row
    const r2 = await fetch(
      `${SUPABASE_URL}/rest/v1/users?email=eq.${encodeURIComponent(email)}&select=*`,
      { headers: sbHeaders() }
    );
    const rows2 = await r2.json();
    if (rows2 && rows2[0]) {
      const u = rows2[0];
      return { clientId: u.client_id, name: u.name, email: u.email, goal: u.goal||"", plan: u.plan ?? 0, pin: u.pin||"", createdAt: u.created_at, trialStart: u.trial_start };
    }
    // Fallback: return what we tried to insert (DB might not have the columns created_at/trial_start)
    return { clientId: newId, name, email, goal: "", plan: 0, pin: "" };
  } catch (e) {
    console.error("findOrCreateUserByEmail error:", e);
    return null;
  }
}

// ─── Utility Helpers ──────────────────────────────────────────────────────────
const genClientId = () => "PA-" + Math.random().toString(36).toUpperCase().slice(2, 7);
const genTxId = () => Date.now() + "-" + Math.random().toString(36).slice(2, 6);
const TRIAL_DAYS = 7;
const trialDaysLeft = (user) => {
  if (!user?.trialStart) return 0;
  const start = new Date(user.trialStart);
  const diff = Math.ceil(TRIAL_DAYS - (Date.now() - start.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
};
const isInTrial = (user) => trialDaysLeft(user) > 0;
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; };
const fmtMk = k => { if (!k) return ""; const [y,m] = k.split("-"); return `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][+m-1]} ${y}`; };
const fmt = n => n >= 1e6 ? `$${(n/1e6).toFixed(2)}M` : `$${(n/1000).toFixed(0)}K`;
const fmtN = n => `$${(n||0).toLocaleString()}`;
const getMonthOpts = () => { const opts=[]; const now=new Date(); for(let i=0;i<24;i++){const d=new Date(now.getFullYear(),now.getMonth()-i,1); opts.push({key:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`,label:fmtMk(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`)});} return opts; };
const tod = () => { const h=new Date().getHours(); return h<12?"morning":h<17?"afternoon":"evening"; };
// ─── Strategic Category Meta-Layer ────────────────────────────────────────────
const STRATEGIC_GROUPS = {
  "Fixed Obligations":  ["Housing", "Insurance", "Utilities", "Healthcare"],
  "Lifestyle Spending": ["Food & Dining", "Entertainment", "Shopping", "Travel", "Subscriptions"],
  "Wealth Building":    ["Investments", "Savings"],
  "Tax-Sensitive":      ["Investments"],
  "Variable":           ["Transport", "Education", "Other"],
};
const STRATEGIC_COLORS = {
  "Fixed Obligations":"#5A8EC0", "Lifestyle Spending":"#E8894A", "Wealth Building":"#4EAD8A",
  "Tax-Sensitive":"#9B7ECF", "Variable":"#72727C", "Financial Leakage":"#C05A5A",
};
const getStrategicGroup = (cat) => {
  for (const [g, cats] of Object.entries(STRATEGIC_GROUPS)) if (cats.includes(cat)) return g;
  return "Variable";
};
function computeStrategicBreakdown(transactions) {
  const groups = {};
  transactions.filter(t => t.type === "expense").forEach(t => {
    const g = getStrategicGroup(t.category);
    groups[g] = (groups[g] || 0) + t.amount;
  });
  const leakage = transactions
    .filter(t => t.type === "expense" && t.regret !== undefined && t.regret >= 2)
    .reduce((s,t) => s+t.amount, 0);
  if (leakage > 0) groups["Financial Leakage"] = leakage;
  return Object.entries(groups)
    .map(([name,value]) => ({ name, value, color: STRATEGIC_COLORS[name] || "#72727C" }))
    .sort((a,b) => b.value - a.value);
}

// ─── Named Behavior Pattern Engine ───────────────────────────────────────────
const PATTERN_DEFS = {
  lifestyle_creep:     { name:"Lifestyle Creep",            icon:"📈", description:"Your spending is increasing as your income increases, reducing your ability to build long-term wealth." },
  financial_leakage:   { name:"Financial Leakage",          icon:"💧", description:"You are spending consistently in areas that may not align with your priorities." },
  inconsistent_saving: { name:"Inconsistent Saving",        icon:"〰️", description:"Your saving and investing patterns are irregular and reactive rather than systematic." },
  emotional_spending:  { name:"Emotional Spending",         icon:"🌀", description:"Certain spending patterns appear tied to short-term decisions rather than planned behavior." },
  underutilized_income:{ name:"Underutilized Income",       icon:"💼", description:"Your income supports more growth than your current financial structure reflects." },
  tax_inefficiency:    { name:"Tax Inefficiency (Educational)", icon:"📋", description:"There may be opportunities to structure your finances more efficiently for tax purposes." },
};

function detectPatterns(monthly, transactions) {
  const flags = [];
  if (!monthly.length) return flags;

  // 1) Lifestyle Creep — income up ≥10% over 3 months, savings rate flat or down
  if (monthly.length >= 3) {
    const r = monthly.slice(-3);
    const inc0 = r[0].income, inc2 = r[2].income;
    const incGr = inc0 > 0 ? (inc2 - inc0) / inc0 : 0;
    const sr0 = inc0 > 0 ? r[0].savings/inc0 : 0;
    const sr2 = inc2 > 0 ? r[2].savings/inc2 : 0;
    if (incGr >= 0.10 && sr2 <= sr0) flags.push({ ...PATTERN_DEFS.lifestyle_creep, severity:"warning",
      evidence:`Income grew ${(incGr*100).toFixed(1)}% over 3 months while savings rate held at ${(sr2*100).toFixed(1)}%.` });
  }

  // 2) Financial Leakage — regret-rated waste OR Other-category bloat
  const rated = transactions.filter(t => t.type === "expense" && t.regret !== undefined);
  if (rated.length >= 3) {
    const regretSum = rated.filter(t => t.regret >= 2).reduce((s,t) => s+t.amount, 0);
    const totalRated = rated.reduce((s,t) => s+t.amount, 0);
    if (totalRated > 0 && regretSum / totalRated >= 0.20) flags.push({ ...PATTERN_DEFS.financial_leakage, severity:"warning",
      evidence:`${((regretSum/totalRated)*100).toFixed(0)}% of rated spending was marked moderate-to-strong regret.` });
  }
  const otherExp = transactions.filter(t => t.type==="expense" && t.category==="Other").reduce((s,t)=>s+t.amount,0);
  const totalExp = transactions.filter(t => t.type==="expense").reduce((s,t)=>s+t.amount,0);
  if (totalExp > 0 && otherExp/totalExp >= 0.15 && !flags.some(f => f.name === PATTERN_DEFS.financial_leakage.name)) {
    flags.push({ ...PATTERN_DEFS.financial_leakage, severity:"warning",
      evidence:`${((otherExp/totalExp)*100).toFixed(0)}% of expenses are uncategorized ("Other") — a typical leakage signal.` });
  }

  // 3) Inconsistent Saving — coefficient of variation > 0.5 on monthly wealth contributions
  if (monthly.length >= 3) {
    const arr = monthly.map(m => m.savings + m.investments);
    const mean = arr.reduce((s,v) => s+v, 0) / arr.length;
    const std = Math.sqrt(arr.reduce((s,v) => s + (v-mean)**2, 0) / arr.length);
    const cv = mean > 0 ? std/mean : 0;
    if (cv > 0.5) flags.push({ ...PATTERN_DEFS.inconsistent_saving, severity:"info",
      evidence:`Month-to-month wealth-building swings by ${(cv*100).toFixed(0)}% — beyond a disciplined pattern.` });
  }

  // 4) Emotional Spending — peak discretionary month >60% above average
  if (monthly.length >= 3) {
    const disc = ["Shopping","Entertainment","Food & Dining"];
    const byM = {};
    transactions.filter(t => t.type==="expense" && disc.includes(t.category))
      .forEach(t => { byM[t.month] = (byM[t.month]||0) + t.amount; });
    const vals = Object.values(byM);
    if (vals.length >= 3) {
      const mean = vals.reduce((s,v)=>s+v,0)/vals.length;
      const max = Math.max(...vals);
      if (mean > 0 && max/mean >= 1.6) flags.push({ ...PATTERN_DEFS.emotional_spending, severity:"info",
        evidence:`Peak discretionary month was ${((max/mean-1)*100).toFixed(0)}% above your typical level.` });
    }
  }

  // 5) Underutilized Income — strong income, wealth rate <15%
  const n = monthly.length;
  const avgInc = monthly.reduce((s,m)=>s+m.income,0)/Math.max(n,1);
  const avgInv = monthly.reduce((s,m)=>s+m.investments,0)/Math.max(n,1);
  const avgSav = monthly.reduce((s,m)=>s+m.savings,0)/Math.max(n,1);
  if (avgInc > 5000) {
    const wealth = (avgSav + avgInv) / avgInc;
    if (wealth < 0.15) flags.push({ ...PATTERN_DEFS.underutilized_income, severity:"warning",
      evidence:`Avg income ~${Math.round(avgInc).toLocaleString()}/mo, but only ${(wealth*100).toFixed(1)}% routed to wealth building.` });
  }

  // 6) Tax Inefficiency — educational, high income + low investment %
  if (avgInc > 8000 && (avgInv/avgInc) < 0.10) {
    flags.push({ ...PATTERN_DEFS.tax_inefficiency, severity:"info",
      evidence:`At your income level, investment activity at ${((avgInv/avgInc)*100).toFixed(1)}% may leave tax-advantaged growth on the table.` });
  }
  return flags;
}

// ─── PDF Export Loader (free, CDN) ────────────────────────────────────────────
let _html2pdf = null;
async function getHtml2Pdf() {
  if (_html2pdf) return _html2pdf;
  if (window.html2pdf) { _html2pdf = window.html2pdf; return _html2pdf; }
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
    s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
  _html2pdf = window.html2pdf;
  return _html2pdf;
}

// ─── Month Completeness Validator ─────────────────────────────────────────────
function validateMonthData(transactions, monthKey) {
  const tx = transactions.filter(t => t.month === monthKey);
  const errors = [];
  if (!tx.some(t => t.type === "income"))  errors.push(`No income recorded for ${fmtMk(monthKey)}.`);
  if (!tx.some(t => t.type === "expense")) errors.push(`No expenses recorded for ${fmtMk(monthKey)}.`);
  return { valid: errors.length === 0, errors };
}
// ─── Currency + Theme Context ─────────────────────────────────────────────────
const AppCtx = createContext({ currency: CURRENCIES[0], theme: THEMES.aurora });
const useCurrency = () => useContext(AppCtx).currency;
const useTheme = () => useContext(AppCtx).theme;
const useG = () => useContext(AppCtx).theme; // drop-in replacement for G in components
const useAURORA = () => {
  const t = useContext(AppCtx).theme;
  return {
    bg0: t.au_bg0, bg1: t.au_bg1, bg2: t.au_bg2, bg3: t.au_bg3, bg4: t.au_bg4,
    line: t.au_line, lineStrong: t.au_lineStrong,
    ink: t.au_ink, inkSoft: t.au_inkSoft, inkFaint: t.au_inkFaint, inkGhost: t.au_inkGhost,
    amber: t.au_amber, amberD: t.au_amberD, amberL: t.au_amberL,
    mint: t.au_mint, mintD: t.au_mintD,
    coral: t.au_coral, coralD: t.au_coralD,
    cyan: t.au_cyan, cyanD: t.au_cyanD,
    violet: t.au_violet, violetD: t.au_violetD,
  };
};
const useFmt = () => {
  const cur = useCurrency();
  const sym = cur.symbol;
  return {
    fmtN: n => `${sym}${(n||0).toLocaleString()}`,
    fmt: n => n >= 1e6 ? `${sym}${(n/1e6).toFixed(2)}M` : `${sym}${(n/1000).toFixed(0)}K`,
    sym,
  };
};

// ─── Data Computation ─────────────────────────────────────────────────────────
function computeMonthly(txs) {
  const by = {};
  txs.forEach(t => {
    if (!by[t.month]) by[t.month] = { income:0, expenses:0, investments:0, savings:0 };
    if (t.type === "income") by[t.month].income += t.amount;
    else if (t.category === "Investments") by[t.month].investments += t.amount;
    else if (t.category === "Savings") by[t.month].savings += t.amount;
    else by[t.month].expenses += t.amount;
  });
  return Object.entries(by).sort(([a],[b]) => a.localeCompare(b)).map(([month, v]) => ({
    month: fmtMk(month), income: v.income,
    expenses: v.expenses + v.investments + v.savings,
    savings: v.savings, investments: v.investments,
  }));
}
function computeExpensePie(txs) {
  const by = {}; const catColor = EXPENSE_CATS.reduce((m,c)=>{m[c.name]=c.color;return m;},{});
  txs.filter(t=>t.type==="expense").forEach(t=>{by[t.category]=(by[t.category]||0)+t.amount;});
  return Object.entries(by).map(([name,value])=>({name,value,color:catColor[name]||G.muted})).sort((a,b)=>b.value-a.value);
}
function computeScores(monthly) {
  if (monthly.length === 0) return [];
  const n = monthly.length;
  const ai = monthly.reduce((s,m)=>s+m.income,0)/n;
  const as = monthly.reduce((s,m)=>s+m.savings,0)/n;
  const av = monthly.reduce((s,m)=>s+m.investments,0)/n;
  const ae = monthly.reduce((s,m)=>s+m.expenses,0)/n;
  const sr = ai>0?as/ai:0, ir = ai>0?av/ai:0, er = ai>0?Math.max(0,1-(ae/ai)):0;
  return [
    {label:"Savings Discipline", score:Math.min(100,Math.round(sr*200)), color:G.green},
    {label:"Investment Rate", score:Math.min(100,Math.round(ir*300)), color:G.gold},
    {label:"Expense Control", score:Math.min(100,Math.round(er*100)), color:G.blue},
    {label:"Data Consistency", score:Math.min(100,n*10), color:G.purple},
    {label:"Wealth Momentum", score:Math.min(100,Math.round((sr+ir)*150)), color:G.orange},
    {label:"Overall Health", score:Math.min(100,Math.round((sr*100+ir*120+er*60)/2.8)), color:G.goldL},
  ];
}
function calculateFinancialAge(scores) {
  if (!scores.length) return 18

  const overall =
    scores.reduce((s,x)=>s+x.score,0)/scores.length

  if (overall >= 85) return 42
  if (overall >= 70) return 35
  if (overall >= 55) return 28
  if (overall >= 40) return 22

  return 18
}
function buildScenarios(monthly, savingsMultiplier=1, sym="$") {
  const baseInv = monthly.length>0?(monthly[monthly.length-1].investments||2000):2000;
  const baseSav = monthly.length>0?(monthly[monthly.length-1].savings||1000):1000;
  const totalSaved = monthly.reduce((s,m)=>s+(m.savings||0)+(m.investments||0),0);
  const baseNW = Math.max(totalSaved+50000, 100000);
  const monthlyContrib = (baseInv + baseSav) * savingsMultiplier;
  const project = (rate,yrs=7)=>{let arr=[],val=baseNW;for(let i=0;i<yrs;i++){arr.push({year:new Date().getFullYear()+i,value:Math.round(val)});val=val*(1+rate)+monthlyContrib*12;}return arr;};
  const f=v=>v>=1e6?`${sym}${(v/1e6).toFixed(2)}M`:`${sym}${(v/1000).toFixed(0)}K`;  // ← fixed
  const con=project(0.05),mod=project(0.08),agg=project(0.13);
  return {
    conservative:{label:"Conservative",rate:"5% annual return",color:G.blue,data:con,end:f(con[6].value),growth:`${Math.round(((con[6].value-baseNW)/baseNW)*100)}%`},
    moderate:{label:"Moderate",rate:"8% annual return",color:G.gold,data:mod,end:f(mod[6].value),growth:`${Math.round(((mod[6].value-baseNW)/baseNW)*100)}%`},
    aggressive:{label:"Aggressive",rate:"13% annual return",color:G.green,data:agg,end:f(agg[6].value),growth:`${Math.round(((agg[6].value-baseNW)/baseNW)*100)}%`},
  };
}

// ─── AI (OpenRouter) ──────────────────────────────────────────────────────────
const AI_PROXY_URL = "https://unbroke-ai-proxy.purelyautomatedcf.workers.dev/";
const OR_MODEL = "deepseek/deepseek-chat";
async function callAI(system, userMsg, history = []) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Please sign in to use AI features.");

  const res = await fetch(AI_PROXY_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-User-Token": token,
    },
    body: JSON.stringify({
      model: OR_MODEL,
      max_tokens: 1800,
      messages: [
        { role: "system", content: system },
        ...history,
        { role: "user", content: userMsg },
      ],
    }),
  });

  const d = await res.json();
  if (!res.ok) throw new Error(d.error || `AI proxy returned ${res.status}`);
  if (d.error) throw new Error(typeof d.error === "string" ? d.error : d.error.message);
  return d.choices?.[0]?.message?.content || "No response.";
}
function buildAnalysisPrompt(client, monthly, scores, sym="$") {
  const anonName = "Client";
  if (monthly.length===0) return `${anonName} has no financial data yet. Encourage them to enter their first income and expense entries.`;
  const latest=monthly[monthly.length-1], n=monthly.length;
  const ai=Math.round(monthly.reduce((s,m)=>s+m.income,0)/n);
  const ae=Math.round(monthly.reduce((s,m)=>s+m.expenses,0)/n);
  const as=Math.round(monthly.reduce((s,m)=>s+m.savings,0)/n);
  const av=Math.round(monthly.reduce((s,m)=>s+m.investments,0)/n);
  const sr=latest.income>0?((latest.savings/latest.income)*100).toFixed(1):"N/A";
  const scText=scores.length>0?scores.map(s=>`${s.label}: ${s.score}/100`).join(" | "):"N/A";
  const overall=scores.length>0?(scores.reduce((s,sc)=>s+sc.score,0)/scores.length).toFixed(1):"N/A";
  return `Client: ${anonName} | Plan: Tier ${client.plan} | Goal: ${client.goal||"Not set"}
OVERALL SCORE: ${overall}/100 | SCORES: ${scText}
LATEST MONTH: Income ${sym}${(latest.income||0).toLocaleString()} | Expenses ${sym}${(latest.expenses||0).toLocaleString()} | Savings ${sr}% | Investments ${sym}${(latest.investments||0).toLocaleString()}
${n}-MONTH AVERAGES: Income ${sym}${ai.toLocaleString()} | Expenses ${sym}${ae.toLocaleString()} | Savings ${sym}${as.toLocaleString()} | Investments ${sym}${av.toLocaleString()}
RAW: ${monthly.map(m=>`${m.month}: Inc ${sym}${(m.income||0).toLocaleString()} Exp ${sym}${(m.expenses||0).toLocaleString()} Sav ${sym}${(m.savings||0).toLocaleString()} Inv ${sym}${(m.investments||0).toLocaleString()}`).join(" | ")}
Provide: behavioral pattern analysis, optimization opportunities, risk alerts, 90-day actions. Be surgical and data-anchored. No generic advice.`;
}
function buildChatCtx(client, monthly, scores, sym="$") {
  const ai=monthly.length>0?Math.round(monthly.reduce((s,m)=>s+m.income,0)/monthly.length):0;
  const overall=scores.length>0?(scores.reduce((s,sc)=>s+sc.score,0)/scores.length).toFixed(1):"N/A";
    return `You are an elite private wealth AI advisor for a private client (Plan: Tier ${client.plan}).
Monthly income avg: ${sym}${ai.toLocaleString()} | Behavioral Score: ${overall}/100 | Goal: ${client.goal||"Not set"} | ${monthly.length} months on file.
Be specific, strategic, data-referenced. Private banking caliber. Concise and actionable.`;
}

// ╔══════════════════════════════════════════════════════════════════════════╗
// ║                                                                          ║
// ║                   A U R O R A   V A U L T                                ║
// ║                                                                          ║
// ║   New dark-luxury design layer. Replaces the dashboard chrome (sidebar,  ║
// ║   topbar, hero, key cards). Consumes the SAME real data the rest of the  ║
// ║   app uses — no mock data, no new deps. All existing views (Add Income,  ║
// ║   Regret, Debt, FIRE, Analysis, …) continue to render unchanged inside   ║
// ║   the new shell.                                                         ║
// ║                                                                          ║
// ╚══════════════════════════════════════════════════════════════════════════╝

// ─── Aurora palette (fixed; independent of THEMES) ───────────────────────────
// The Aurora dashboard intentionally uses its own palette so the dark-luxury
// aesthetic stays cohesive. The user's theme choice still drives non-dashboard
// views (Add Income, Regret, FIRE, …) via the existing G/useG() system.


// ─── useCountUp — quiet "alive" reveal for numbers ───────────────────────────
function useCountUp(target, durationMs = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const safe = Number.isFinite(target) ? target : 0;
    if (safe === 0) { setVal(0); return; }
    let raf, start;
    const tick = t => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(safe * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return val;
}

// ─── Format helpers scoped to Aurora (use the user's currency symbol) ────────
const auroraK = (n, sym) => `${sym || "$"}${((n || 0) / 1000).toFixed(1)}K`;
const auroraN = (n, sym) => `${sym || "$"}${Math.round(n || 0).toLocaleString()}`;

// ─── Background — animated aurora mesh + grain ───────────────────────────────
const AuroraBackground = () => {
  const AURORA = useAURORA();
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
      background: `
        radial-gradient(ellipse 70% 50% at 50% -10%, ${AURORA.amber}0E, transparent 55%),
        ${AURORA.bg0}
      `,
    }}/>
  );
};
const PATTERN_COLOR_MAP = (AURORA) => ({
  coral:  { bg: `${AURORA.coral}12`, border: `${AURORA.coral}33`, text: AURORA.coral },
  amber:  { bg: `${AURORA.amber}12`, border: `${AURORA.amber}33`, text: AURORA.amber },
  mint:   { bg: `${AURORA.mint}12`,  border: `${AURORA.mint}33`,  text: AURORA.mint },
  // remapped:
  cyan:   { bg: `${AURORA.amber}12`, border: `${AURORA.amber}33`, text: AURORA.amber },
  violet: { bg: `${AURORA.bg3}`,     border: AURORA.line,         text: AURORA.inkSoft },
});
// ─── AI Behavior Pattern Cards ────────────────────────────────────────────────
const AIBehaviorPatternCards = ({ transactions, currency, client }) => {
  const sym = currency?.symbol || "$";
  const AURORA = useAURORA(); 
  const G = useG();

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const generatePatterns = async () => {
    if (transactions.length < 5) return;
    setLoading(true);

    // Build rich transaction summary for AI
    const txData = transactions
      .filter(t => t.type === "expense" && t.created_at)
      .slice(-120) // last 120 transactions
      .map(t => {
        const d = new Date(t.created_at);
        return {
          note: t.note || t.category,
          category: t.category,
          amount: t.amount,
          day: ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.getDay()],
          hour: d.getHours(),
          month: t.month,
        };
      });

    const SYSTEM = `You are a personal finance behavioral analyst. 
You analyze spending transactions and detect real, specific, human behavioral patterns.

Rules:
- Be SPECIFIC. Name the merchant, time, day. Not "you spend on food" but "you order from Uber Eats mostly on Sunday nights after 9PM"
- Be BRUTALLY honest but not mean. Like a smart friend who sees your patterns
- Focus on: time-of-day habits, day-of-week habits, repeated merchants, emotional spending signals, impulse patterns
- Each card should feel like a personal observation, not a generic tip
- Vary the tone: some observations are neutral, some are a gentle flag, some are positive

Return ONLY a valid JSON array, no markdown:
[
  {
    "icon": "emoji",
    "title": "1 punchy sentence describing the pattern — name specifics like merchant/time/day",
    "sub": "1 sentence of context or implication",
    "stat": "a relevant number e.g. '8 times' or '$340/mo' or '11PM average'",
    "statLabel": "short label for the stat",
    "color": "one of: coral | amber | mint | cyan | violet",
    "type": "habit | flag | positive"
  }
]

Generate 4-6 cards. Only surface patterns you're confident about from the data.`;

    const userMsg = `Currency symbol: ${sym}
Analyze these ${txData.length} expense transactions and find behavioral patterns:

${JSON.stringify(txData, null, 2)}`;

  try {
    const raw = await callAI(SYSTEM, userMsg);
    const clean = raw.replace(/```json|```/g, "").trim();
    const parsed = JSON.parse(clean);
    setCards(parsed);
    setGenerated(true);
  } catch(e) {
    console.error("Pattern analysis failed:", e);
    // ADD THIS:
    setCards([]);
    setGenerated(true);
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    if (transactions.length >= 10 && !generated) {
      generatePatterns();
    }
  }, [transactions.length]);

const colorMap = PATTERN_COLOR_MAP(AURORA);
  if (transactions.filter(t => t.created_at).length < 10) return null;

  return (
    <div style={{ marginTop: 24 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", 
        alignItems: "baseline", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 9, letterSpacing: "0.28em", color: AURORA.amberD,
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 4 }}>
            ◈ BEHAVIORAL PATTERNS
          </div>
          <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif",
            fontSize: 22, fontWeight: 300, color: AURORA.ink, letterSpacing: "-0.02em" }}>
            What your habits are{" "}
            <span style={{ fontStyle: "italic", color: AURORA.amber }}>actually saying</span>
          </div>
        </div>
        <button onClick={generatePatterns} disabled={loading}
          style={{ padding: "7px 16px",
            background: loading ? "transparent" : `${AURORA.amber}12`,
            border: `1px solid ${loading ? AURORA.line : `${AURORA.amber}40`}`,
            borderRadius: 20, color: loading ? AURORA.inkFaint : AURORA.amber,
            fontSize: 10, cursor: loading ? "not-allowed" : "pointer",
            fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.12em",
            transition: "all 0.15s" }}>
          {loading ? "ANALYSING…" : "↻ REFRESH"}
        </button>
      </div>

      {/* Loading state */}
      {loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ background: "rgba(255,255,255,0.02)",
              border: `1px solid ${AURORA.line}`, borderRadius: 16,
              padding: "22px 24px", height: 140,
              animation: "auroraPulse 1.5s ease-in-out infinite" }}/>
          ))}
        </div>
      )}

      {/* Cards grid */}
      {!loading && cards.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          {cards.map((card, i) => {
            const c = colorMap[card.color] || colorMap.amber;
            return (
              <div key={i} style={{
                background: c.bg,
                border: `1px solid ${c.border}`,
                borderRadius: 16, padding: "20px 22px",
                display: "flex", flexDirection: "column", gap: 10,
                transition: "transform 0.2s",
                animation: `auroraRise 0.4s ease ${i * 0.08}s both`,
              }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                
                {/* Top row */}
                <div style={{ display: "flex", justifyContent: "space-between",
                  alignItems: "flex-start", gap: 10 }}>
                  <div style={{ fontSize: 28, lineHeight: 1 }}>{card.icon}</div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 300, color: c.text,
                      fontFamily: "Fraunces, 'Cormorant Garamond', serif",
                      letterSpacing: "-0.02em", lineHeight: 1 }}>
                      {card.stat}
                    </div>
                    <div style={{ fontSize: 8, color: AURORA.inkFaint, marginTop: 3,
                      fontFamily: "'JetBrains Mono', monospace",
                      letterSpacing: "0.12em", textTransform: "uppercase" }}>
                      {card.statLabel}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif",
                  fontSize: 15, fontWeight: 400, color: AURORA.ink,
                  lineHeight: 1.4, letterSpacing: "-0.01em" }}>
                  {card.title}
                </div>

                {/* Sub */}
                <div style={{ fontSize: 11, color: AURORA.inkSoft, lineHeight: 1.55 }}>
                  {card.sub}
                </div>

                {/* Type badge */}
                <div style={{ marginTop: "auto" }}>
                  <span style={{ fontSize: 8, letterSpacing: "0.14em",
                    color: c.text, fontFamily: "'JetBrains Mono', monospace",
                    textTransform: "uppercase",
                    padding: "3px 8px", borderRadius: 20,
                    background: `${c.border}`, border: `1px solid ${c.border}` }}>
                    {card.type === "flag" ? "⚠ FLAG"
                      : card.type === "positive" ? "✦ STRENGTH"
                      : "◎ HABIT"}
                  </span>
                </div>
              </div>
              
            );
          })}
        </div>
      )}

      {/* Empty */}
      {!loading && cards.length === 0 && generated && (
        <div style={{ textAlign: "center", padding: "40px",
          color: AURORA.inkFaint, fontSize: 12,
          fontFamily: "Fraunces, serif", fontStyle: "italic" }}>
          Not enough timestamped data yet — keep logging and patterns will emerge.
        </div>
      )}
    </div>
  );
};
// ─── Glass card — the new card primitive ─────────────────────────────────────
const Glass = ({ children, style = {}, glow = false, padding = "24px 26px", hover = true, onClick }) => {
  const AURORA = useAURORA();
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        background: AURORA.bg1,
        border: `1px solid ${hover && hov ? AURORA.lineStrong : AURORA.line}`,
        borderRadius: 14,
        padding,
        transition: "border-color 0.2s, transform 0.2s",
        transform: hover && hov ? "translateY(-1px)" : "translateY(0)",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}>
      {glow && (
        <div style={{
          position: "absolute", inset: 0, borderRadius: 14, pointerEvents: "none",
          background: `radial-gradient(ellipse at top right, ${AURORA.amber}10, transparent 55%)`,
        }}/>
      )}
      <div style={{ position: "relative", zIndex: 1 }}>{children}</div>
    </div>
  );
}

// ─── OrbitalScore — the centerpiece dial ─────────────────────────────────────
const OrbitalScore = ({ value, size = 200 }) => {
  const AURORA = useAURORA();
  const animatedVal = useCountUp(value, 1400);
  const r = (size / 2) - 16;
  const c = 2 * Math.PI * r;
  const pct = Math.min(Math.max(animatedVal / 100, 0), 1);
  const cx = size / 2, cy = size / 2;
  const tier = value >= 80 ? { label: "ELITE",      color: AURORA.mint }
             : value >= 65 ? { label: "STRONG",     color: AURORA.amber }
             : value >= 50 ? { label: "DEVELOPING", color: AURORA.amberD }
             : value > 0   ? { label: "FOCUS",      color: AURORA.coral }
             :               { label: "AWAITING",   color: AURORA.inkFaint };
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={AURORA.bg4} strokeWidth={8}/>
        <circle cx={cx} cy={cy} r={r} fill="none"
          stroke={tier.color} strokeWidth={8} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - pct * c}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(0.4, 0, 0.2, 1)" }}/>
        {pct > 0.02 && (() => {
          const angle = -Math.PI/2 + pct * 2 * Math.PI;
          return <circle cx={cx + r * Math.cos(angle)} cy={cy + r * Math.sin(angle)}
            r={4} fill={tier.color}/>;
        })()}
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex",
        flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontFamily: "Fraunces, serif", fontSize: 54, fontWeight: 300,
          color: AURORA.ink, lineHeight: 1, letterSpacing: "-0.04em" }}>
          {value > 0 ? Math.round(animatedVal) : "—"}
        </div>
        <div style={{ fontSize: 9, letterSpacing: "0.28em", color: tier.color,
          fontFamily: "'JetBrains Mono', monospace", marginTop: 6 }}>
          {tier.label}
        </div>
      </div>
    </div>
  );
};
 

// ─── Aurora Sidebar — nav groups mapped 1:1 to existing view IDs ─────────────
const AURORA_NAV = [
  { label: "TRACK", items: [
    { id: "dashboard",  icon: "◐", label: "Pulse" },
    { id: "income",     icon: "↑", label: "Add Income" },
    { id: "expenses",   icon: "↓", label: "Add Expense" },
    { id: "bankimport", icon: "⊞", label: "Bank Import",  minPlan: 1 },
  ]},
  { label: "INSIGHT", items: [
    { id: "analysis",      icon: "✦", label: "AI Analysis",      minPlan: 1 },
    { id: "personality",   icon: "◈", label: "Money Type" },
    { id: "shockinsights", icon: "⚡", label: "Shock Insights" },
    { id: "benchmark",     icon: "◎", label: "Compare Yourself" },
    { id: "sharecards",    icon: "◇", label: "Share Cards" },
    { id: "regret",        icon: "○", label: "Regret Map",       minPlan: 1 },
    { id: "report",        icon: "≡", label: "Monthly Report",   minPlan: 1 },
  ]},
  { label: "PLAN", items: [
    { id: "scenarios", icon: "△", label: "Scenarios",    minPlan: 1 },
    { id: "strategic", icon: "✧", label: "Freedom Map",  minPlan: 1 },
    { id: "fire",      icon: "▲", label: "FIRE Calc" },
    { id: "debt",      icon: "▭", label: "Debt Tracker" },
  ]},
  { label: "CONCIERGE", items: [
    { id: "assistant", icon: "◉", label: "AI Assistant", minPlan: 2 },
    { id: "privacy",   icon: "⊕", label: "Privacy" },
  ]},
];

const AuroraNavItem = ({ item, active, locked, onClick }) => {
  const AURORA = useAURORA(); 
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={() => !locked && onClick(item.id)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      disabled={locked}
      style={{
        position: "relative", display: "flex", alignItems: "center", gap: 14,
        width: "100%", padding: "11px 14px",
        background: active ? `linear-gradient(90deg, ${AURORA.amber}18, transparent)`
                  : hov && !locked ? "rgba(255,255,255,0.03)" : "transparent",
        border: "none", borderRadius: 10,
        color: active ? AURORA.amber
              : locked ? AURORA.inkGhost
              : hov ? AURORA.ink : AURORA.inkSoft,
        cursor: locked ? "not-allowed" : "pointer",
        textAlign: "left",
        fontFamily: "Geist, Geist, sans-serif", fontSize: 13,
        fontWeight: active ? 500 : 400, letterSpacing: "0.005em",
        opacity: locked ? 0.55 : 1,
        transition: "all 0.18s",
      }}>
      {active && (
        <span style={{
          position: "absolute", left: -10, top: "50%", transform: "translateY(-50%)",
          width: 3, height: 22, background: AURORA.amber, borderRadius: "0 4px 4px 0",
          boxShadow: `0 0 12px ${AURORA.amber}`,
        }}/>
      )}
      <span style={{ fontSize: 15, width: 16, textAlign: "center" }}>{item.icon}</span>
      <span style={{ flex: 1 }}>{item.label}</span>
      {locked && (
        <span style={{ fontSize: 8, color: AURORA.inkFaint,
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace", letterSpacing: "0.14em" }}>
          T{item.minPlan}
        </span>
      )}
    </button>
  );
};
// FIXED
const auroraFooterBtn = (active, AURORA) => ({
  flex: 1,
  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
  padding: "7px 8px",
  background: active ? `${AURORA.amber}15` : "transparent",
  border: `1px solid ${active ? `${AURORA.amber}40` : AURORA.line}`,
  borderRadius: 7,
  color: active ? AURORA.amber : AURORA.inkFaint,
  fontSize: 11, cursor: "pointer",
  fontFamily: "Geist, Geist, sans-serif",
  transition: "all 0.15s",
});
const AuroraSidebar = ({ view, setView, client, tier, plan, overallScore,
                         onSettings, onSignOut, settingsOpen }) => {
  const AURORA = useAURORA();
 return (
    <div style={{
      width: 232, position: "fixed", top: 18, bottom: 18, left: 18, zIndex: 20,
    display: "flex", flexDirection: "column",
    background: "linear-gradient(180deg, rgba(20,17,28,0.7), rgba(14,12,20,0.85))",
    backdropFilter: "blur(24px) saturate(140%)",
    WebkitBackdropFilter: "blur(24px) saturate(140%)",
    border: `1px solid ${AURORA.line}`, borderRadius: 22, padding: "22px 14px",
    fontFamily: "Geist, Geist, sans-serif",
  }}>
    {/* Brand */}
    <div style={{ padding: "0 8px 18px", borderBottom: `1px solid ${AURORA.line}` }}>
      <div style={{ fontSize: 9, letterSpacing: "0.32em", color: AURORA.amberD,
        fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 4 }}>
        UNBROKE
      </div>
      <div style={{ fontSize: 18, fontWeight: 300, color: AURORA.amber,
        fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontStyle: "italic", letterSpacing: "0.01em" }}>
        wealth, alive
      </div>

    </div>

    {/* Nav */}
    <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden",
      paddingTop: 16, display: "flex", flexDirection: "column", gap: 18 }}>
      {AURORA_NAV.map(g => (
        <div key={g.label}>
          <div style={{ fontSize: 9, letterSpacing: "0.22em", color: AURORA.inkFaint,
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace", padding: "0 12px 8px" }}>
            {g.label}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {g.items.map(item => (
              <AuroraNavItem key={item.id} item={item}
                active={view === item.id}
                locked={item.minPlan && plan < item.minPlan}
                onClick={setView}/>
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* Client footer */}
    <div style={{ paddingTop: 14, borderTop: `1px solid ${AURORA.line}` }}>
      <div style={{ padding: "10px 12px 10px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10,
            background: `linear-gradient(135deg, ${AURORA.amber}, ${AURORA.coral})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "Fraunces, serif", fontWeight: 500, color: AURORA.bg0, fontSize: 14 }}>
            {(client?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, color: AURORA.ink, fontWeight: 500,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {client?.name || "—"}
            </div>
            <div style={{ fontSize: 10, color: AURORA.inkFaint,
              fontFamily: "'JetBrains Mono', 'DM Mono', monospace" }}>
              {client?.clientId || ""}
            </div>
          </div>
        </div>
        <div style={{ display: "inline-flex", padding: "3px 10px",
          background: `${AURORA.amber}15`, border: `1px solid ${AURORA.amber}30`,
          borderRadius: 20, fontSize: 9, color: AURORA.amber,
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace", letterSpacing: "0.14em" }}>
          T{plan} · {(tier?.name || "").toUpperCase()}
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 9, color: AURORA.inkFaint,
              fontFamily: "'JetBrains Mono', 'DM Mono', monospace", letterSpacing: "0.14em" }}>SCORE</span>
            <span style={{ fontSize: 10, color: AURORA.amber,
              fontFamily: "'JetBrains Mono', 'DM Mono', monospace" }}>{overallScore}</span>
          </div>
          <div style={{ background: AURORA.bg3, borderRadius: 2, height: 2, overflow: "hidden" }}>
            <div style={{ width: `${Math.min(100, parseFloat(overallScore) || 0)}%`, height: "100%",
              background: `linear-gradient(90deg, ${AURORA.amberD}, ${AURORA.amber})`,
              borderRadius: 2, transition: "width 1.2s ease" }}/>
          </div>
        </div>
      </div>

      {/* Footer action buttons */}
      <div style={{ display: "flex", gap: 6, padding: "0 4px" }}>
        <button onClick={() => setView("howto")}
          style={auroraFooterBtn(view === "howto",AURORA)} title="Help">
          <span style={{ fontSize: 13 }}>?</span><span>Help</span>
        </button>
        <button onClick={onSettings}
          style={auroraFooterBtn(settingsOpen,AURORA)} title="Settings">
          <span style={{ fontSize: 13 }}>⚙</span><span>Settings</span>
        </button>
      </div>
      <button onClick={onSignOut}
        onMouseEnter={e => { e.currentTarget.style.borderColor = `${AURORA.coral}60`; e.currentTarget.style.color = AURORA.coral; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = AURORA.line; e.currentTarget.style.color = AURORA.inkFaint; }}
        style={{
          marginTop: 6, width: "calc(100% - 8px)", marginLeft: 4,
          padding: "8px 12px",
          background: "transparent", border: `1px solid ${AURORA.line}`, borderRadius: 8,
          color: AURORA.inkFaint, fontSize: 11, cursor: "pointer",
          fontFamily: "Geist, Geist, sans-serif", letterSpacing: "0.02em",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          transition: "all 0.15s",
        }}>
        <span style={{ fontSize: 12 }}>⎋</span> Sign out
      </button>
    </div>
  </div>
);
}; 


// ─── AuroraTopBar — editorial greeting ───────────────────────────────────────
const AuroraTopBar = ({ client, monthly, currency, setCurrencyCode, viewTitle }) => {
  const AURORA = useAURORA(); 
  const sym = currency?.symbol || "$";
  const greet = (() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  })();
  const dateLabel = new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "short", day: "numeric", year: "numeric",
  }).toUpperCase().replace(/,/g, " ·");

  // Real income change vs prior month (when on dashboard)
  const latest = monthly[monthly.length - 1];
  const prior  = monthly[monthly.length - 2];
  const incomeChange = latest && prior && prior.income > 0
    ? ((latest.income - prior.income) / prior.income) * 100
    : null;

    const subtitle = viewTitle && viewTitle !== "Financial Overview"
      ? `${viewTitle} · ${monthly.length} months on file`
      : incomeChange !== null
        ? <>Find where your money is secretly disappearing. Income {incomeChange >= 0 ? "up" : "down"} <span style={{ color: incomeChange >= 0 ? AURORA.mint : AURORA.coral }}>{Math.abs(incomeChange).toFixed(1)}%</span> vs prior month{monthly.length >= 6 ? " — across your last 6-month window." : "."}</>
        : "Add your first income & expenses to populate the dashboard.";

    
  return (
    <div style={{ display: "flex", justifyContent: "space-between",
      alignItems: "flex-end", marginBottom: 32, gap: 24, flexWrap: "wrap" }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.28em", color: AURORA.inkFaint,
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 8 }}>
          {dateLabel}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
          <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 36, fontWeight: 300,
            color: AURORA.ink, letterSpacing: "-0.02em", lineHeight: 1, fontVariationSettings: "'opsz' 144" }}>
            {greet},
          </div>
          <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 36, fontWeight: 300,
            color: AURORA.amber, fontStyle: "italic", letterSpacing: "-0.02em", lineHeight: 1,
            fontVariationSettings: "'opsz' 144, 'SOFT' 100" }}>
            {(client?.name || "there").split(" ")[0]}
          </div>
        </div>
        <div style={{ fontSize: 13, color: AURORA.inkSoft, marginTop: 10,
          maxWidth: 560, lineHeight: 1.6 }}>
          {subtitle}
        </div>
      </div>
      <div style={{
        marginTop:16,
        display:"flex",
        gap:12,
        flexWrap:"wrap"
      }}>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "7px 14px",
          background: `${AURORA.mint}10`, border: `1px solid ${AURORA.mint}30`, borderRadius: 100,
          fontSize: 10, color: AURORA.mint,
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace", letterSpacing: "0.14em" }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: AURORA.mint,
            animation: "auroraPulse 1.8s ease-in-out infinite" }}/>
          LIVE · SYNCING
        </div>
        <select
          value={currency?.code}
          onChange={e => setCurrencyCode && setCurrencyCode(e.target.value)}
          style={{ padding: "7px 14px", background: AURORA.bg2,
            border: `1px solid ${AURORA.line}`, borderRadius: 100,
            fontSize: 11, color: AURORA.inkSoft, cursor: "pointer",
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace", outline: "none" }}>
          {CURRENCIES.map(c => (
            <option key={c.code} value={c.code} style={{ background: AURORA.bg2, color: AURORA.ink }}>
              {c.symbol} {c.code}
            </option>
          ))}
        </select>
      </div>
      </div>
    </div>
  );
};

// ─── Aurora Hero — wealth pulse + orbital score (REAL DATA) ──────────────────
const AuroraMiniStat = ({ label, value, change, hint, color, invert }) => {
  const AURORA = useAURORA(); 
  const isGood = invert ? (change ?? 0) < 0 : (change ?? 0) > 0;
  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: "0.18em", color: AURORA.inkFaint,
        fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 28,
        fontWeight: 400, color: AURORA.ink, letterSpacing: "-0.02em", lineHeight: 1 }}>
        {value}
      </div>
      {change !== undefined && change !== null && (
        <div style={{ fontSize: 11, color: isGood ? AURORA.mint : AURORA.coral,
          marginTop: 6, fontFamily: "'JetBrains Mono', 'DM Mono', monospace" }}>
          {change > 0 ? "↑" : "↓"} {Math.abs(change).toFixed(1)}% prior
        </div>
      )}
      {hint && <div style={{ fontSize: 11, color: AURORA.inkFaint, marginTop: 6 }}>{hint}</div>}
    </div>
  );
};

const AuroraHero = ({ monthly, scores, transactions, currency }) => {
  const AURORA = useAURORA(); 
  const sym = currency?.symbol || "$";
  const latest = monthly[monthly.length - 1] || { income: 0, expenses: 0, savings: 0, investments: 0 };
  const prior  = monthly[monthly.length - 2] || { income: 0, expenses: 0, savings: 0, investments: 0 };

  // Real "wealth accumulated" — sum of all savings + investments contributions (+ a baseline floor for new users)
  const wealthAccumulated = monthly.reduce((s, m) => s + (m.savings || 0) + (m.investments || 0), 0);

  // 6-month wealth growth (current sum vs sum at -6mo point)
  const sixMoAgo = monthly.length >= 6
    ? monthly.slice(0, monthly.length - 6).reduce((s, m) => s + (m.savings || 0) + (m.investments || 0), 0)
    : 0;
  const wealthGrowthPct = sixMoAgo > 0 ? ((wealthAccumulated - sixMoAgo) / sixMoAgo) * 100 : null;

  const netFlow = (latest.income || 0) - (latest.expenses || 0);
  const incomeChange  = prior.income > 0
    ? ((latest.income - prior.income) / prior.income) * 100 : null;
  const expenseChange = prior.expenses > 0
    ? ((latest.expenses - prior.expenses) / prior.expenses) * 100 : null;

  const overall = scores.length > 0
    ? Math.round(scores.reduce((s, sc) => s + sc.score, 0) / scores.length) : 0;

  const wealth = useCountUp(wealthAccumulated);
  const income = useCountUp(latest.income || 0);
  const exp    = useCountUp(latest.expenses || 0);
  const flow   = useCountUp(netFlow);

  const hasData = monthly.length > 0;
  const goalLine = hasData
    ? <>vs 6 months ago — on track for <span style={{ color: AURORA.amber,
        fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontStyle: "italic" }}>your freedom number</span></>
    : "Add income & expenses to begin tracking your freedom number.";

  return (
    <Glass padding="36px 40px" glow accent={AURORA.amber} hover={false}
      style={{ marginBottom: 24, overflow: "hidden", position: "relative" }}>
      <div style={{
        position: "absolute", top: -120, right: -80, width: 360, height: 360,
        background: `radial-gradient(circle, ${AURORA.amber}22, transparent 65%)`,
        pointerEvents: "none", animation: "auroraFloat 14s ease-in-out infinite alternate",
      }}/>

      <div style={{ display: "grid",
        gridTemplateColumns: "1fr auto", gap: 48, alignItems: "center" }}>
        {/* Left — editorial wealth statement */}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 9, letterSpacing: "0.28em", color: AURORA.amberD,
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 16 }}>
            ESTATE PULSE · {new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" }).toUpperCase()}
          </div>

          <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 18,
            color: AURORA.inkSoft, fontStyle: "italic", marginBottom: 6,
            fontVariationSettings: "'opsz' 14" }}>
            Total wealth accumulated
          </div>

          <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 88,
            fontWeight: 300, color: AURORA.ink,
            letterSpacing: "-0.04em", lineHeight: 0.95, fontVariationSettings: "'opsz' 144" }}>
            {hasData
              ? <>{sym}{(wealth/1000).toFixed(1)}<span style={{ color: AURORA.amber }}>K</span></>
              : <span style={{ color: AURORA.inkFaint }}>—</span>}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            {wealthGrowthPct !== null && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4,
                padding: "3px 10px",
                background: `${wealthGrowthPct >= 0 ? AURORA.mint : AURORA.coral}15`,
                border: `1px solid ${wealthGrowthPct >= 0 ? AURORA.mint : AURORA.coral}30`,
                borderRadius: 20, fontSize: 11,
                color: wealthGrowthPct >= 0 ? AURORA.mint : AURORA.coral,
                fontFamily: "'JetBrains Mono', 'DM Mono', monospace" }}>
                {wealthGrowthPct >= 0 ? "↑" : "↓"} {Math.abs(wealthGrowthPct).toFixed(1)}%
              </span>
            )}
            <span style={{ fontSize: 12, color: AURORA.inkSoft }}>{goalLine}</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24,
            marginTop: 32, paddingTop: 28, borderTop: `1px solid ${AURORA.line}` }}>
            <AuroraMiniStat label="Income"
              value={hasData ? auroraN(income, sym) : "—"}
              change={incomeChange} color={AURORA.mint}/>
            <AuroraMiniStat label="Outflow"
              value={hasData ? auroraN(exp, sym) : "—"}
              change={expenseChange} color={AURORA.coral} invert/>
            <AuroraMiniStat label="Net Flow"
              value={hasData ? auroraN(flow, sym) : "—"}
              hint={hasData ? (flow >= 0 ? "kept this month" : "overspent this month") : ""}
              color={AURORA.amber}/>
          </div>
        </div>

        {/* Right — orbital score */}
        <div style={{ display: "flex", flexDirection: "column",
          alignItems: "center", gap: 12, position: "relative" }}>
          <div style={{ fontSize: 9, letterSpacing: "0.22em", color: AURORA.inkFaint,
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace" }}>
            BEHAVIORAL INTELLIGENCE
          </div>
          <OrbitalScore value={overall}/>
          <div style={{ fontSize: 11, color: AURORA.inkSoft, textAlign: "center",
            maxWidth: 200, lineHeight: 1.5, fontStyle: "italic",
            fontFamily: "Fraunces, 'Cormorant Garamond', serif" }}>
            {overall >= 80 ? "\"Your discipline compounds. Keep the rhythm.\""
              : overall >= 65 ? "\"Your discipline is real. Now make it compound.\""
              : overall >= 50 ? "\"Foundation laid. Time to lift the rate.\""
              : overall > 0 ? "\"Small shifts here move everything else.\""
              : "\"Add a month of data to unlock your score.\""}
          </div>
        </div>
      </div>
    </Glass>
  );
};

// ─── Aurora Cash Flow Chart — pure SVG, no recharts dependency ───────────────
const AuroraCashFlow = ({ monthly, currency }) => {
  const AURORA = useAURORA(); 
  const sym = currency?.symbol || "$";
  const [tip, setTip] = useState(null);
  const W = 760, H = 240, P = { top: 16, right: 14, bottom: 28, left: 50 };
  const iW = W - P.left - P.right, iH = H - P.top - P.bottom;

  // Show last 6 months only for clarity
  const data = monthly.slice(-6);
  const hasData = data.length > 0;

  const series = [
    { key: "income",   color: AURORA.mint,  fill: true,  glow: true,  dashed: false, label: "IN" },
    { key: "expenses", color: AURORA.coral, fill: false, glow: false, dashed: true,  label: "OUT" },
    { key: "savings",  color: AURORA.amber, fill: true,  glow: true,  dashed: false, label: "SAVED" },
  ];

  const allV = data.flatMap(d => series.map(s => d[s.key] || 0));
  const maxV = Math.max(...allV, 1) * 1.1;
  const xS = i => P.left + (i / Math.max(data.length - 1, 1)) * iW;
  const yS = v => P.top + iH - (v / maxV) * iH;
  const mkP = key => data.map((d, i) => `${i === 0 ? "M" : "L"}${xS(i).toFixed(1)},${yS(d[key] || 0).toFixed(1)}`).join(" ");
  const mkA = key => {
    const l = mkP(key); const last = data.length - 1;
    return `${l} L${xS(last).toFixed(1)},${(P.top + iH).toFixed(1)} L${P.left.toFixed(1)},${(P.top + iH).toFixed(1)} Z`;
  };

  return (
    <Glass padding="24px 26px" accent={AURORA.cyan}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 9, letterSpacing: "0.22em", color: AURORA.inkFaint,
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 6 }}>
            {Math.min(monthly.length, 6)}-MONTH CASH FLOW
          </div>
          <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 22,
            fontWeight: 400, color: AURORA.ink, letterSpacing: "-0.01em" }}>
            The river of your <span style={{ fontStyle: "italic", color: AURORA.amber }}>money</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, fontSize: 11, color: AURORA.inkSoft,
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace" }}>
          {series.map(s => (
            <span key={s.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color,
                boxShadow: s.glow ? `0 0 8px ${s.color}` : "none" }}/>
              {s.label}
            </span>
          ))}
        </div>
      </div>

      <div style={{ width: "100%", height: H, marginTop: 14, position: "relative" }}>
        {hasData ? (
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "100%" }} preserveAspectRatio="none">
            <defs>
              {series.map(s => (
                <linearGradient key={s.key} id={`aur_ga_${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor={s.color} stopOpacity={s.fill ? 0.45 : 0}/>
                  <stop offset="100%" stopColor={s.color} stopOpacity={0}/>
                </linearGradient>
              ))}
              <filter id="aur_line_glow">
                <feGaussianBlur stdDeviation="2.5" result="b"/>
                <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            </defs>
            {/* Grid */}
            {Array.from({ length: 5 }, (_, i) => {
              const v = (maxV * i) / 4, y = yS(v);
              return (
                <g key={i}>
                  <line x1={P.left} x2={W - P.right} y1={y} y2={y}
                    stroke={AURORA.line} strokeWidth={0.5} strokeDasharray="2 4"/>
                  <text x={P.left - 8} y={y + 3} textAnchor="end" fontSize={9}
                    fill={AURORA.inkFaint} fontFamily="'JetBrains Mono', monospace">
                    {auroraK(v, sym)}
                  </text>
                </g>
              );
            })}
            {/* X axis labels */}
            {data.map((d, i) => (
              <text key={i} x={xS(i)} y={H - 6} textAnchor="middle" fontSize={9}
                fill={AURORA.inkFaint} fontFamily="'JetBrains Mono', monospace">
                {d.month}
              </text>
            ))}
            {/* Series */}
            {series.map(s => (
              <g key={s.key}>
                {s.fill && <path d={mkA(s.key)} fill={`url(#aur_ga_${s.key})`}/>}
                <path d={mkP(s.key)} fill="none" stroke={s.color} strokeWidth={2}
                  strokeDasharray={s.dashed ? "5 4" : undefined}
                  filter={s.glow ? "url(#aur_line_glow)" : undefined}
                  strokeLinejoin="round" strokeLinecap="round"/>
              </g>
            ))}
            {/* Hover hit areas */}
            {data.map((d, i) => (
              <rect key={i} x={xS(i) - (iW / Math.max(data.length, 1)) / 2} y={P.top}
                width={iW / Math.max(data.length, 1)} height={iH}
                fill="transparent"
                onMouseEnter={() => setTip({ i, d, x: xS(i) })}
                onMouseLeave={() => setTip(null)}
                style={{ cursor: "crosshair" }}/>
            ))}
            {tip && (
              <line x1={tip.x} x2={tip.x} y1={P.top} y2={P.top + iH}
                stroke={AURORA.amber} strokeWidth={0.5} strokeDasharray="3 3"/>
            )}
            {tip && series.map(s => (
              <circle key={s.key} cx={tip.x} cy={yS(tip.d[s.key] || 0)} r={4}
                fill={s.color}
                style={{ filter: `drop-shadow(0 0 6px ${s.color})` }}/>
            ))}
          </svg>
        ) : (
          <div style={{ height: "100%", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 8 }}>
            <span style={{ fontSize: 22, color: AURORA.inkFaint }}>◎</span>
            <span style={{ fontSize: 11, color: AURORA.inkFaint }}>
              Enter income & expenses to see your river of money
            </span>
          </div>
        )}
        {tip && (
          <div style={{ position: "absolute", left: `${(tip.x / W) * 100}%`,
            top: 8, transform: "translateX(-50%)",
            background: "rgba(20,17,28,0.95)", border: `1px solid ${AURORA.amber}40`,
            borderRadius: 10, padding: "8px 12px",
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace", fontSize: 11,
            color: AURORA.ink, pointerEvents: "none", whiteSpace: "nowrap",
            backdropFilter: "blur(8px)" }}>
            <div style={{ color: AURORA.inkFaint, marginBottom: 4 }}>{tip.d.month}</div>
            {series.map(s => (
              <div key={s.key} style={{ color: s.color, lineHeight: 1.5 }}>
                {s.label}: {auroraN(tip.d[s.key] || 0, sym)}
              </div>
            ))}
          </div>
        )}
      </div>
    </Glass>
  );
};

// ─── Aurora Score Factors — animated horizontal bars (REAL DATA) ─────────────
const AuroraScoreBar = ({ label, score, color, delay }) => {
  const AURORA = useAURORA(); 
  const w = useCountUp(score, 1200 + delay);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7 }}>
        <span style={{ fontSize: 12, color: AURORA.inkSoft }}>{label}</span>
        <span style={{ fontSize: 11, color,
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace" }}>{Math.round(w)}</span>
      </div>
      <div style={{ background: AURORA.bg3, borderRadius: 100, height: 5,
        overflow: "hidden", position: "relative" }}>
        <div style={{
          width: `${w}%`, height: "100%",
          background: `linear-gradient(90deg, ${color}66, ${color})`,
          borderRadius: 100, boxShadow: `0 0 12px ${color}80`,
          transition: "width 0.1s linear",
        }}/>
      </div>
    </div>
  );
};

const AuroraScoreFactors = ({ scores }) => {
  const AURORA = useAURORA(); 
  // Map the app's score palette to Aurora's accent colors for cohesion
  const remap = {
    "Savings Discipline": AURORA.mint,
    "Investment Rate":    AURORA.amber,
    "Expense Control":    AURORA.cyan,
    "Data Consistency":   AURORA.violet,
    "Wealth Momentum":    AURORA.coral,
    "Overall Health":     AURORA.amberL,
  };
  // Filter out "Overall Health" — it's already in the OrbitalScore
  const items = scores.filter(s => s.label !== "Overall Health");

  return (
    <Glass padding="22px 24px" accent={AURORA.violet}>
      <div style={{ fontSize: 9, letterSpacing: "0.22em", color: AURORA.inkFaint,
        fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 4 }}>
        SCORE ANATOMY
      </div>
      <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 20,
        fontWeight: 400, color: AURORA.ink, marginBottom: 22, letterSpacing: "-0.01em" }}>
        Five forces shaping <span style={{ fontStyle: "italic", color: AURORA.violet }}>your number</span>
      </div>
      {items.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {items.map((s, i) => (
            <AuroraScoreBar key={s.label} label={s.label} score={s.score}
              color={remap[s.label] || AURORA.amber} delay={i * 100}/>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: AURORA.inkFaint,
          textAlign: "center", padding: "40px 0" }}>
          Scores reveal themselves once a month of data is on file.
        </div>
      )}
    </Glass>
  );
};

// ─── Aurora Strategic Bar — segmented allocation (REAL DATA) ─────────────────
const AuroraStrategicBar = ({ transactions, currency }) => {
  const AURORA = useAURORA(); 
  const sym = currency?.symbol || "$";
  // Reuse existing computeStrategicBreakdown but remap colors to Aurora palette
  const colorMap = {
    "Fixed Obligations":  AURORA.cyan,
    "Lifestyle Spending": AURORA.coral,
    "Wealth Building":    AURORA.mint,
    "Tax-Sensitive":      AURORA.violet,
    "Variable":           AURORA.inkSoft,
    "Financial Leakage":  AURORA.coralD,
  };
  const data = computeStrategicBreakdown(transactions || []).map(d => ({
    ...d, color: colorMap[d.name] || AURORA.inkSoft,
  }));
  const total = data.reduce((s, x) => s + x.value, 0);

  return (
    <Glass padding="22px 24px" accent={AURORA.mint}>
      <div style={{ fontSize: 9, letterSpacing: "0.22em", color: AURORA.inkFaint,
        fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 4 }}>
        STRATEGIC ALLOCATION
      </div>
      <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 20,
        fontWeight: 400, color: AURORA.ink, marginBottom: 20, letterSpacing: "-0.01em" }}>
        Where every dollar <span style={{ fontStyle: "italic", color: AURORA.mint }}>actually went</span>
      </div>

      {data.length > 0 ? (
        <>
          <div style={{ display: "flex", height: 14, borderRadius: 100,
            overflow: "hidden", gap: 2, marginBottom: 22 }}>
            {data.map(s => (
              <div key={s.name} style={{
                flex: s.value,
                background: `linear-gradient(180deg, ${s.color}, ${s.color}aa)`,
                boxShadow: `inset 0 1px 0 ${s.color}`,
              }}/>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {data.map(s => {
              const pct = total > 0 ? ((s.value / total) * 100).toFixed(1) : "0.0";
              return (
                <div key={s.name} style={{ display: "flex", justifyContent: "space-between",
                  alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color,
                      boxShadow: `0 0 8px ${s.color}` }}/>
                    <span style={{ fontSize: 12, color: AURORA.inkSoft }}>{s.name}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ fontSize: 13, color: AURORA.ink,
                      fontFamily: "'JetBrains Mono', 'DM Mono', monospace" }}>{auroraN(s.value, sym)}</span>
                    <span style={{ fontSize: 10, color: AURORA.inkFaint,
                      fontFamily: "'JetBrains Mono', 'DM Mono', monospace" }}>{pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div style={{ fontSize: 12, color: AURORA.inkFaint,
          textAlign: "center", padding: "40px 0" }}>
          Add expenses to see your strategic allocation.
        </div>
      )}
    </Glass>
  );
};

// ─── Aurora Today's Signal — pulls from real detectPatterns() ────────────────
const AuroraTodaySignal = ({ patterns, monthly, transactions }) => {
  // Pick the highest-severity pattern; fall back to a derived insight; fall back to onboarding
  const AURORA = useAURORA(); 
  const top = patterns.find(p => p.severity === "warning") || patterns[0];

  let title, body, accent;
  if (top) {
    accent = top.severity === "warning" ? AURORA.coral : AURORA.amber;
    title = <>{top.name} <span style={{ fontStyle: "italic", color: accent }}>detected</span></>;
    body = top.evidence;
  } else if (monthly.length >= 2) {
    // Derived signal: shopping spend change
    const shopThis = transactions
      .filter(t => t.type === "expense" && t.category === "Shopping" && t.month === Object.keys(
        transactions.reduce((acc, t) => { acc[t.month] = true; return acc; }, {})
      ).sort().slice(-1)[0])
      .reduce((s, t) => s + t.amount, 0);
    accent = AURORA.amber;
    title = <>Your <span style={{ fontStyle: "italic", color: accent }}>patterns</span> look healthy.</>;
    body = shopThis > 0
      ? "No flagged behaviors this cycle. Keep the rhythm — discipline compounds quietly."
      : "Steady savings, steady spending, no anomalies. Boring is beautiful in finance.";
  } else {
    accent = AURORA.amber;
    title = <>Your signals are <span style={{ fontStyle: "italic", color: accent }}>warming up</span>.</>;
    body = "Once you log a month of activity, this card will flag the patterns shaping your behavior.";
  }

  return (
    <Glass padding="24px 26px" accent={accent}
      style={{ position: "relative", overflow: "hidden",
        background: `linear-gradient(135deg, ${accent}0E, rgba(255,255,255,0.02))` }}>
      <div style={{
        position: "absolute", top: -20, right: 14, fontSize: 180,
        fontFamily: "Fraunces, 'Cormorant Garamond', serif", color: `${accent}10`, lineHeight: 1,
        fontStyle: "italic", pointerEvents: "none", fontVariationSettings: "'opsz' 144",
      }}>"</div>

      <div style={{ fontSize: 9, letterSpacing: "0.22em", color: accent,
        fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 14, position: "relative" }}>
        ⚡ TODAY'S SIGNAL
      </div>

      <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 22,
        fontWeight: 400, color: AURORA.ink, lineHeight: 1.35, letterSpacing: "-0.01em",
        marginBottom: 14, position: "relative" }}>
        {title}
      </div>

      <div style={{ fontSize: 13, color: AURORA.inkSoft, lineHeight: 1.65, position: "relative" }}>
        {body}
      </div>
    </Glass>
  );
};

// ─── Aurora Pulse Feed — recent transactions (REAL DATA) ─────────────────────
const AuroraPulseFeed = ({ transactions, currency }) => {
  const AURORA = useAURORA(); 
  const sym = currency?.symbol || "$";
  // Sort newest first (created_at desc), take last 6
  const feed = [...transactions]
    .sort((a, b) => {
      const at = a.created_at || a.id || "";
      const bt = b.created_at || b.id || "";
      return String(bt).localeCompare(String(at));
    })
    .slice(0, 6)
    .map(t => {
      const isIncome = t.type === "income";
      const color = isIncome ? AURORA.mint
        : t.category === "Investments" || t.category === "Savings" ? AURORA.amber
        : AURORA.coral;
      const ago = (() => {
        if (!t.created_at) return t.month || "—";
        const diff = Date.now() - new Date(t.created_at).getTime();
        const h = Math.floor(diff / (1000 * 60 * 60));
        const d = Math.floor(h / 24);
        return d > 0 ? `${d}d` : h > 0 ? `${h}h` : `${Math.max(1, Math.floor(diff / (1000 * 60)))}m`;
      })();
      return {
        icon: isIncome ? "↑" : "↓",
        label: t.note || t.category || (isIncome ? "Income" : "Expense"),
        category: t.category,
        amount: `${isIncome ? "+" : "-"}${auroraN(t.amount, sym)}`,
        time: ago,
        color,
      };
    });

  return (
    <Glass padding="22px 26px" accent={AURORA.cyan}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "baseline", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 9, letterSpacing: "0.22em", color: AURORA.inkFaint,
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 4 }}>
            LIVE PULSE
          </div>
          <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 18,
            fontWeight: 400, color: AURORA.ink }}>
            Recent <span style={{ fontStyle: "italic", color: AURORA.cyan }}>movements</span>
          </div>
        </div>
        <span style={{ fontSize: 10, color: AURORA.inkFaint,
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace" }}>
          {feed.length} {feed.length === 1 ? "entry" : "entries"}
        </span>
      </div>

      {feed.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {feed.map((p, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "12px 0",
              borderBottom: i < feed.length - 1 ? `1px solid ${AURORA.line}` : "none",
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 10,
                background: `${p.color}15`, border: `1px solid ${p.color}30`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: p.color, fontSize: 14, flexShrink: 0,
              }}>{p.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: AURORA.ink, fontWeight: 400,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.label}
                </div>
                <div style={{ fontSize: 10, color: AURORA.inkFaint,
                  fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginTop: 2 }}>
                  {p.time} ago{p.category && p.label !== p.category ? ` · ${p.category}` : ""}
                </div>
              </div>
              <div style={{ fontSize: 13, color: p.color,
                fontFamily: "'JetBrains Mono', 'DM Mono', monospace", fontWeight: 500 }}>
                {p.amount}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: AURORA.inkFaint,
          textAlign: "center", padding: "32px 0" }}>
          Your first transactions will appear here.
        </div>
      )}
    </Glass>
  );
};

// ─── Aurora Dashboard — orchestrates the bento grid ──────────────────────────
const AuroraDashboard = ({ monthly, scores, transactions, currency, client }) => {
  const patterns = detectPatterns(monthly, transactions);
  return (
    <>
      <AuroraHero monthly={monthly} scores={scores}
        transactions={transactions} currency={currency}/>

      {/* Bento grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1.6fr 1fr",
        gridTemplateRows: "auto auto",
        gap: 20,
      }}>
        <div style={{ gridColumn: "1", gridRow: "1" }}>
          <AuroraCashFlow monthly={monthly} currency={currency}/>
        </div>
        <div style={{ gridColumn: "2", gridRow: "1 / 3" }}>
          <AuroraScoreFactors scores={scores}/>
        </div>
        <div style={{ gridColumn: "1", gridRow: "2",
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <AuroraStrategicBar transactions={transactions} currency={currency}/>
          <AuroraTodaySignal patterns={patterns}
            monthly={monthly} transactions={transactions}/>
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <AuroraPulseFeed transactions={transactions} currency={currency}/>
      </div>
        <AIBehaviorPatternCards 
        transactions={transactions} 
        currency={currency}
        client={client}
          />
    </>
  );
};

// ─── Aurora DashboardTimeAware — wraps AuroraDashboard with time machine ─────
// Preserves the existing TimeMachineSlider + CompareView + Sankey flow while
// rendering the new Aurora components for the "single month" view.
const AuroraDashboardTimeAware = ({ monthly, expenses, scores, transactions, currency, client }) => {

  const allMonthKeys = uniqMonthKeys(transactions);
  const latestKey = allMonthKeys[allMonthKeys.length - 1] || thisMonth();
  const priorKey  = allMonthKeys[allMonthKeys.length - 2] || latestKey;
  const [selectedMonth, setSelectedMonth] = useState(latestKey);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareMonth, setCompareMonth] = useState(priorKey);

  useEffect(() => {
    if (allMonthKeys.length > 0 && !allMonthKeys.includes(selectedMonth)) {
      setSelectedMonth(allMonthKeys[allMonthKeys.length - 1]);
    }
    if (compareEnabled && allMonthKeys.length > 0 && !allMonthKeys.includes(compareMonth)) {
      setCompareMonth(allMonthKeys[Math.max(0, allMonthKeys.length - 2)]);
    }
  }, [allMonthKeys.join("|")]); // eslint-disable-line

  const txsThrough = txsThroughMonth(transactions, selectedMonth);
  const monthlyT   = computeMonthly(txsThrough);
  const scoresT    = computeScores(monthlyT);
  const sankeyTxs  = transactions.filter(t => t.month === selectedMonth);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Keep existing TimeMachineSlider; it inherits the user's theme styling */}
      {allMonthKeys.length > 1 && (
        <TimeMachineSlider
          monthKeys={allMonthKeys}
          selected={selectedMonth}
          onSelect={setSelectedMonth}
          compareEnabled={compareEnabled}
          onCompareToggle={setCompareEnabled}
          compareWith={compareMonth}
          onCompareChange={setCompareMonth}
        />
      )}

      {compareEnabled && allMonthKeys.length >= 2 ? (
        <CompareView transactions={transactions}
          monthA={selectedMonth} monthB={compareMonth}/>
      ) : (
        <>
          <AuroraDashboard
            monthly={monthlyT}
            scores={scoresT}
            transactions={txsThrough}
            currency={currency}
            client={client}/>

          {/* Sankey — preserved beneath the new bento grid */}
          {sankeyTxs.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <SankeyCashFlow transactions={sankeyTxs}/>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── End of Aurora module ────────────────────────────────────────────────────


// ╔══════════════════════════════════════════════════════════════════════════╗
// ║   D E M O   M O D E                                                      ║
// ║   No Supabase writes. Generates a realistic 6-month account so prospects ║
// ║   can experience the full app before signing up.                         ║
// ╚══════════════════════════════════════════════════════════════════════════╝

const DEMO_CLIENT = {
  clientId: "DEMO-AURORA",
  name: "Aurora Demo",
  email: "demo@purelyautomated.com",
  plan: 2,                                                  // unlock everything
  goal: "Reach financial independence by 50 with $1.5M net worth",
  pin: "0000",
  createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 200).toISOString(),                   
  score: 76,
  isDemo: true,                                             // the flag every guard checks
};

// Deterministic-ish randomness so demos feel consistent across reloads in a session
const _seed = { v: 0x1abc };
const rnd = () => {
  _seed.v = (_seed.v * 9301 + 49297) % 233280;
  return _seed.v / 233280;
};
const rndInt = (min, max) => Math.floor(min + rnd() * (max - min + 1));
const pick = (arr) => arr[Math.floor(rnd() * arr.length)];

function generateDemoData() {
  _seed.v = 0x1abc;                                         // reset every generation
  const txs = [];
  const now = new Date();
  const mKey = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const iso  = (year, mo, day) => new Date(year, mo, day, 10 + rndInt(0, 9), rndInt(0, 59)).toISOString();
  let id = 9000;
  const nextId = () => `demo-${id++}`;
  const push = (t) => txs.push({ id: nextId(), client_id: "DEMO-AURORA", ...t });

  for (let mAgo = 5; mAgo >= 0; mAgo--) {
    const d = new Date(now.getFullYear(), now.getMonth() - mAgo, 1);
    const y = d.getFullYear(), mo = d.getMonth();
    const month = mKey(d);

    // RECURRING INCOME — salary with gentle creep (triggers Lifestyle Creep pattern detection)
    const salary = 8800 + (5 - mAgo) * 220 + rndInt(0, 180);
    push({ type: "income", category: "Salary", amount: salary, month,
      note: "Monthly salary · TechCorp", created_at: iso(y, mo, 1) });

    // Occasional freelance & dividends (variety)
    if (mAgo % 2 === 0) {
      push({ type: "income", category: "Freelance",
        amount: 420 + rndInt(0, 480), month,
        note: "Side project — automation consulting", created_at: iso(y, mo, 14) });
    }
    if (mAgo === 4 || mAgo === 1) {
      push({ type: "income", category: "Dividends",
        amount: 140 + rndInt(0, 90), month,
        note: "Index fund quarterly dividend", created_at: iso(y, mo, 22) });
    }

    // RECURRING FIXED EXPENSES
    push({ type: "expense", category: "Housing", amount: 2200, month,
      note: "Rent · Bandra apt", created_at: iso(y, mo, 1) });
    push({ type: "expense", category: "Utilities",
      amount: 145 + rndInt(0, 65), month,
      note: "Electricity + internet", created_at: iso(y, mo, 3) });
    push({ type: "expense", category: "Subscriptions", amount: 38, month,
      note: "Netflix + Spotify + iCloud", created_at: iso(y, mo, 5) });
    push({ type: "expense", category: "Insurance", amount: 195, month,
      note: "Health insurance premium", created_at: iso(y, mo, 7) });

    // WEALTH BUILDING — SIP increasing over time
    push({ type: "expense", category: "Investments",
      amount: 1100 + (5 - mAgo) * 50, month,
      note: "Vanguard SIP", created_at: iso(y, mo, 2) });

    // SAVINGS — variable, with one month skipped (Inconsistent Saving pattern trigger)
    if (mAgo !== 3) {
      push({ type: "expense", category: "Savings",
        amount: 400 + rndInt(0, 600), month,
        note: "Emergency fund top-up", created_at: iso(y, mo, 4) });
    }

    // GROCERIES — 4–5 entries
    const groceryPlaces = ["Whole Foods", "Trader Joe's", "Costco run", "Local market", "DMart weekly"];
    for (let i = 0, n = 4 + rndInt(0, 1); i < n; i++) {
      push({ type: "expense", category: "Food & Dining",
        amount: 60 + rndInt(0, 100), month,
        note: pick(groceryPlaces),
        created_at: iso(y, mo, 5 + Math.floor((24 / n) * i) + rndInt(0, 2)) });
    }

    // DINING OUT — 3–6 entries (some regret-rated for variety)
    const diningPlaces = ["Sushi place", "Thai takeout", "Coffee at Blue Bottle",
      "Brunch w/ friends", "Pizza night", "Bagel + latte", "Burger joint",
      "Vada pav lunch", "Truffle dinner — anniversary"];
    for (let i = 0, n = 3 + rndInt(0, 3); i < n; i++) {
      const amt = 14 + rndInt(0, 60);
      const regret = amt > 50 && rnd() < 0.3 ? 2 : undefined;
      push({ type: "expense", category: "Food & Dining", amount: amt, month,
        note: pick(diningPlaces),
        created_at: iso(y, mo, 4 + rndInt(0, 24)), regret });
    }

    // SHOPPING — 2–4 entries, recent month spikes (triggers Today's Signal)
    const shopPlaces = ["Amazon", "Uniqlo", "Apple Store", "REI", "IKEA", "Etsy", "Zara"];
    const shopN = mAgo === 0 ? 5 : 2 + rndInt(0, 2);
    for (let i = 0; i < shopN; i++) {
      const amt = 32 + rndInt(0, 220);
      const regret = amt > 150 && rnd() < 0.5 ? 2 : (rnd() < 0.18 ? 3 : undefined);
      push({ type: "expense", category: "Shopping", amount: amt, month,
        note: pick(shopPlaces),
        created_at: iso(y, mo, 8 + rndInt(0, 19)), regret });
    }

    // TRANSPORT — 2–3 entries
    for (let i = 0, n = 2 + rndInt(0, 1); i < n; i++) {
      push({ type: "expense", category: "Transport",
        amount: 22 + rndInt(0, 55), month,
        note: pick(["Uber", "Gas station", "Lyft to airport", "Metro pass"]),
        created_at: iso(y, mo, 6 + rndInt(0, 21)) });
    }

    // ENTERTAINMENT — 1–3 entries
    for (let i = 0, n = 1 + rndInt(0, 2); i < n; i++) {
      push({ type: "expense", category: "Entertainment",
        amount: 18 + rndInt(0, 60), month,
        note: pick(["Movie night", "Concert tickets", "Board game cafe", "Climbing gym day pass"]),
        created_at: iso(y, mo, 10 + rndInt(0, 17)) });
    }

    // TRAVEL — sparse but big when it lands
    if (mAgo === 4 || mAgo === 2) {
      push({ type: "expense", category: "Travel",
        amount: 380 + rndInt(0, 600), month,
        note: mAgo === 4 ? "Weekend trip — Goa" : "Flight + hotel — Bangalore",
        created_at: iso(y, mo, 12 + rndInt(0, 11)) });
    }

    // HEALTHCARE — occasional
    if (rnd() < 0.35) {
      push({ type: "expense", category: "Healthcare",
        amount: 40 + rndInt(0, 175), month,
        note: "Dentist / prescription",
        created_at: iso(y, mo, 16 + rndInt(0, 10)) });
    }
  }

  return txs;
}

// ─── DemoBanner — sticky strip at the top of main content ────────────────────
const DemoBanner = ({ onSignup, onExit }) => {
  const AURORA = useAURORA();
  return (
  <div style={{
    position: "sticky", top: 0, zIndex: 30,
    margin: "-32px -40px 28px",
    padding: "12px 40px",
    background: `linear-gradient(90deg, ${AURORA.amber}28, ${AURORA.coral}1E, ${AURORA.amber}28)`,
    borderBottom: `1px solid ${AURORA.amber}40`,
    backdropFilter: "blur(20px) saturate(140%)",
    WebkitBackdropFilter: "blur(20px) saturate(140%)",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 16, flexWrap: "wrap",
    fontFamily: "Geist, Geist, sans-serif",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
        padding: "3px 10px",
        background: `${AURORA.amber}25`, border: `1px solid ${AURORA.amber}50`,
        borderRadius: 20, fontSize: 9, color: AURORA.amber,
        fontFamily: "'JetBrains Mono', 'DM Mono', monospace", letterSpacing: "0.18em",
        flexShrink: 0 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: AURORA.amber,
          animation: "auroraPulse 1.8s ease-in-out infinite" }}/>
        DEMO
      </span>
      <span style={{ fontSize: 13, color: AURORA.ink, lineHeight: 1.4 }}>
        <span style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif",
          fontStyle: "italic", color: AURORA.amber }}>You're exploring sample data.</span>
        <span style={{ color: AURORA.inkSoft }}> Nothing you change is saved.</span>
      </span>
    </div>
    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
      <button onClick={onExit} style={{
        padding: "7px 14px", background: "transparent",
        border: `1px solid ${AURORA.line}`, borderRadius: 100,
        color: AURORA.inkSoft, fontSize: 12, cursor: "pointer",
        fontFamily: "Geist, Geist, sans-serif",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = AURORA.lineStrong; e.currentTarget.style.color = AURORA.ink; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = AURORA.line;       e.currentTarget.style.color = AURORA.inkSoft; }}>
        Exit demo
      </button>
      <button onClick={onSignup} style={{
        padding: "7px 18px",
        background: `linear-gradient(135deg, ${AURORA.amber}, ${AURORA.amberD})`,
        border: `1px solid ${AURORA.amber}`, borderRadius: 100,
        color: AURORA.bg0, fontSize: 12, fontWeight: 500, cursor: "pointer",
        fontFamily: "Geist, Geist, sans-serif",
        boxShadow: `0 0 16px ${AURORA.amber}40`,
      }}>
        Sign up to save your data →
      </button>
    </div>
  </div>
  );
};

// ─── End of Demo module ──────────────────────────────────────────────────────


// ─── SVG Charts ───────────────────────────────────────────────────────────────
const SVGAreaChart = ({ data, series, height=190 }) => {
  const { fmt, sym } = useFmt(); 
  const G = useG();
  const yFormatter = v => fmt(v);
  const [tip, setTip] = useState(null);
  const W=560,H=height,P={top:8,right:8,bottom:24,left:44};
  const iW=W-P.left-P.right, iH=H-P.top-P.bottom;
  const allV=data.flatMap(d=>series.map(s=>d[s.key]||0));
  const maxV=Math.max(...allV,1)*1.1;
  const xS=i=>P.left+(i/Math.max(data.length-1,1))*iW;
  const yS=v=>P.top+iH-((v)/(maxV))*iH;
  const mkP=key=>data.map((d,i)=>`${i===0?"M":"L"}${xS(i).toFixed(1)},${yS(d[key]||0).toFixed(1)}`).join(" ");
  const mkA=key=>{const l=mkP(key),last=data.length-1;return `${l} L${xS(last).toFixed(1)},${(P.top+iH).toFixed(1)} L${P.left.toFixed(1)},${(P.top+iH).toFixed(1)} Z`;};
  return (
    <div style={{position:"relative",width:"100%",height:H}}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"100%"}}>
        <defs>{series.map(s=><linearGradient key={s.key} id={`ga_${s.key}`} x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={s.color} stopOpacity={s.fill?0.22:0}/><stop offset="95%" stopColor={s.color} stopOpacity={0}/></linearGradient>)}</defs>
        {Array.from({length:5},(_,i)=>{const v=(maxV*i)/4,y=yS(v);return(<g key={i}><line x1={P.left} x2={W-P.right} y1={y} y2={y} stroke={G.border} strokeWidth={0.5}/><text x={P.left-6} y={y+4} textAnchor="end" fontSize={9} fill={G.muted}>{yFormatter(v)}</text></g>);})}
        {data.map((d,i)=><text key={i} x={xS(i)} y={H-4} textAnchor="middle" fontSize={9} fill={G.muted}>{d.month}</text>)}
        {series.map(s=><g key={s.key}>{s.fill&&<path d={mkA(s.key)} fill={`url(#ga_${s.key})`}/>}<path d={mkP(s.key)} fill="none" stroke={s.color} strokeWidth={1.5} strokeDasharray={s.dashed?"4 3":undefined}/></g>)}
        {data.map((d,i)=><rect key={i} x={xS(i)-(iW/data.length)/2} y={P.top} width={iW/data.length} height={iH} fill="transparent" onMouseEnter={()=>setTip({i,d,x:xS(i)})} onMouseLeave={()=>setTip(null)} style={{cursor:"crosshair"}}/>)}
        {tip&&<line x1={tip.x} x2={tip.x} y1={P.top} y2={P.top+iH} stroke={G.gold} strokeWidth={0.5} strokeDasharray="2 2"/>}
      </svg>
      {tip&&<div style={{position:"absolute",left:`${(tip.x/W)*100}%`,top:8,transform:tip.x>W*0.6?"translateX(-110%)":"translateX(8px)",background:G.bg3,border:`0.5px solid ${G.border}`,borderRadius:8,padding:"8px 12px",fontSize:11,pointerEvents:"none",zIndex:10,minWidth:110}}>
        <div style={{color:G.gold,marginBottom:5,fontFamily:"'JetBrains Mono', monospace"}}>{tip.d.month}</div>
        {series.map(s=><div key={s.key} style={{color:s.color,marginBottom:2}}>{s.label}: <span style={{color:G.text}}>{fmt(tip.d[s.key]||0)}</span></div>)}
      </div>}
    </div>
  );
};

const SVGPieChart = ({ data, height=130 }) => {
  const { fmtN } = useFmt();
  const G = useG();
  const [hov, setHov] = useState(null);
  const cx=80,cy=height/2,r=52,ri=34;
  const total=data.reduce((s,d)=>s+d.value,0)||1;
  let angle=-Math.PI/2;
  const slices=data.map(d=>{const sweep=(d.value/total)*2*Math.PI,start=angle;angle+=sweep;return{...d,start,sweep,end:angle};});
  const donut=(cx,cy,ro,ri,start,end)=>{const x1o=cx+ro*Math.cos(start),y1o=cy+ro*Math.sin(start),x2o=cx+ro*Math.cos(end),y2o=cy+ro*Math.sin(end),x1i=cx+ri*Math.cos(end),y1i=cy+ri*Math.sin(end),x2i=cx+ri*Math.cos(start),y2i=cy+ri*Math.sin(start),lg=end-start>Math.PI?1:0;return `M${x1o},${y1o} A${ro},${ro} 0 ${lg} 1 ${x2o},${y2o} L${x1i},${y1i} A${ri},${ri} 0 ${lg} 0 ${x2i},${y2i} Z`;};
  return (
    <div style={{width:"100%",height}}>
      <svg viewBox={`0 0 260 ${height}`} style={{width:"100%",height:"100%"}}>
        {slices.map((s,i)=>{const ma=s.start+s.sweep/2,ox=hov===i?Math.cos(ma)*3:0,oy=hov===i?Math.sin(ma)*3:0;return(<path key={i} d={donut(cx+ox,cy+oy,r,ri,s.start,s.end)} fill={s.color} opacity={hov!==null&&hov!==i?0.5:1} style={{cursor:"pointer",transition:"opacity 0.15s"}} onMouseEnter={()=>setHov(i)} onMouseLeave={()=>setHov(null)}/>);})}
        {hov!==null?(<><text x={cx} y={cy-6} textAnchor="middle" fontSize={10} fill={G.text} fontFamily="'JetBrains Mono', monospace">{slices[hov].name}</text><text x={cx} y={cy+9} textAnchor="middle" fontSize={10} fill={slices[hov].color} fontFamily="'JetBrains Mono', monospace">{fmtN(slices[hov].value)}</text></>):(<text x={cx} y={cy+5} textAnchor="middle" fontSize={10} fill={G.muted} fontFamily="'JetBrains Mono', monospace">{fmtN(total)}</text>)}
      </svg>
    </div>
  );
};

const SVGScenChart = ({ data, color, height=230 }) => {
  const [tip, setTip] = useState(null);
  const { fmt } = useFmt();
  const G = useG();
  const W=660,H=height,P={top:12,right:12,bottom:28,left:56};
  const iW=W-P.left-P.right,iH=H-P.top-P.bottom;
  const vals=data.map(d=>d.value),maxV=Math.max(...vals,1)*1.1;
  const xS=i=>P.left+(i/Math.max(data.length-1,1))*iW;
  const yS=v=>P.top+iH-((v)/maxV)*iH;
  const line=data.map((d,i)=>`${i===0?"M":"L"}${xS(i).toFixed(1)},${yS(d.value).toFixed(1)}`).join(" ");
  const area=`${line} L${xS(data.length-1).toFixed(1)},${(P.top+iH).toFixed(1)} L${P.left.toFixed(1)},${(P.top+iH).toFixed(1)} Z`;
  return (
    <div style={{position:"relative",width:"100%",height:H}}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"100%"}}>
        <defs><linearGradient id="sg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={color} stopOpacity={0.25}/><stop offset="95%" stopColor={color} stopOpacity={0}/></linearGradient></defs>
        {Array.from({length:6},(_,i)=>{const v=(maxV*i)/5,y=yS(v);return(<g key={i}><line x1={P.left} x2={W-P.right} y1={y} y2={y} stroke={G.border} strokeWidth={0.5}/><text x={P.left-6} y={y+4} textAnchor="end" fontSize={10} fill={G.muted}>{fmt(v)}</text></g>);})}
        {data.map((d,i)=><text key={i} x={xS(i)} y={H-6} textAnchor="middle" fontSize={10} fill={G.muted}>{d.year}</text>)}
        <path d={area} fill="url(#sg)"/>
        <path d={line} fill="none" stroke={color} strokeWidth={2}/>
        {data.map((d,i)=><circle key={i} cx={xS(i)} cy={yS(d.value)} r={4} fill={color} stroke={G.bg2} strokeWidth={1.5} onMouseEnter={()=>setTip({i,d})} onMouseLeave={()=>setTip(null)} style={{cursor:"pointer"}}/>)}
      </svg>
      {tip&&<div style={{position:"absolute",left:`${(xS(tip.i)/W)*100}%`,top:16,transform:tip.i>data.length*0.6?"translateX(-110%)":"translateX(10px)",background:G.bg3,border:`0.5px solid ${G.border}`,borderRadius:8,padding:"8px 12px",fontSize:11,pointerEvents:"none",zIndex:10}}>
        <div style={{color,marginBottom:4,fontFamily:"'JetBrains Mono', monospace"}}>{tip.d.year}</div>
        <div style={{color:G.text}}>Net Worth: <span style={{color}}>{fmt(tip.d.value)}</span></div>
      </div>}
    </div>
  );
};

// ─── Reusable UI ──────────────────────────────────────────────────────────────
const KPI = ({label,value,sub,gold}) => {
  const G = useG();
  return (
  <div style={{background:G.bg2,border:`0.5px solid ${gold?G.borderGold:G.border}`,borderRadius:10,padding:"16px 20px"}}>
    <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>{label}</div>
    <div style={{fontSize:22,fontWeight:500,color:gold?G.gold:G.text,fontFamily:"'JetBrains Mono', monospace",letterSpacing:"-0.02em"}}>{value}</div>
    {sub&&<div style={{fontSize:11,color:G.muted,marginTop:5}}>{sub}</div>}
  </div>
);};

const ScoreBar = ({label,score,color}) => {
  const G = useG();
  return (
  <div style={{display:"flex",flexDirection:"column",gap:5}}>
    <div style={{display:"flex",justifyContent:"space-between"}}>
      <span style={{fontSize:12,color:G.muted}}>{label}</span>
      <span style={{fontSize:12,color,fontFamily:"'JetBrains Mono', monospace"}}>{score}</span>
    </div>
    <div style={{background:G.bg4,borderRadius:2,height:3}}>
      <div style={{width:`${score}%`,height:"100%",background:color,borderRadius:2}}/>
    </div>
  </div>
);};

const NavBtn = ({id,label,icon,active,locked,onClick}) => {
  const G = useG();
  return (
  <button onClick={()=>onClick(id)} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"9px 14px",background:active?G.bg3:"transparent",border:"none",borderLeft:`2px solid ${active?G.gold:"transparent"}`,borderRadius:"0 8px 8px 0",color:active?G.gold:locked?G.dim:G.muted,cursor:locked?"not-allowed":"pointer",textAlign:"left",fontSize:13,fontFamily:"inherit",fontWeight:active?500:400,opacity:locked?0.5:1}}>
    <span style={{fontSize:14}}>{icon}</span>{label}{locked&&<span style={{marginLeft:"auto",fontSize:9,color:G.dim,letterSpacing:"0.1em"}}>UPGRADE</span>}
  </button>
);};


const NAV_GROUPS = [
  { label: "Track", items: [
    { id:"dashboard",  label:"Dashboard",   icon:"dashboard" },
    { id:"income",     label:"Add Income",  icon:"income" },
    { id:"expenses",   label:"Add Expense", icon:"expense" },
    { id:"bankimport", label:"Bank Import", icon:"bankimport" },
  ]},
  { label: "Insights", items: [
    { id:"analysis",    label:"AI Analysis",       icon:"analysis",    minPlan:1 },
    { id:"personality", label:"Money Personality", icon:"personality" },
    { id:"regret",      label:"Regret Tracker",    icon:"regret",      minPlan:1 },
    { id:"report",      label:"Monthly Report",    icon:"report",      minPlan:1 },
  ]},
  { label: "Plan", items: [
    { id:"scenarios", label:"Scenarios",       icon:"scenarios", minPlan:1 },
    { id:"strategic", label:"Freedom Map",     icon:"freedom",   minPlan:1 },
    { id:"fire",      label:"FIRE Calculator", icon:"fire" },
    { id:"debt",      label:"Debt Tracker",    icon:"debt" },
  ]},
  { label: "Concierge", items: [
    { id:"assistant", label:"AI Assistant", icon:"assistant", minPlan:2 },
  ]},
];

const Sidebar = ({ view, setView, client, tier, plan, overallScore, daysLeft, inTrial,
                   collapsed, setCollapsed, onSettings, onSignOut, settingsOpen }) => {
  const T = useG();
  const W = collapsed ? 68 : 232;

  const NavBtnNew = ({ item }) => {
    const active = view === item.id;
    const locked = item.minPlan && plan < item.minPlan;
    return (
      <button onClick={() => !locked && setView(item.id)} title={collapsed ? item.label : ""}
        style={{
          display:"flex", alignItems:"center", gap:12, width:"100%",
          padding: collapsed ? "10px" : "9px 12px",
          background: active ? `${T.gold}14` : "transparent",
          border:"none", borderRadius:8,
          color: active ? T.gold : locked ? T.dim : T.muted,
          cursor: locked ? "not-allowed" : "pointer",
          textAlign:"left", fontSize:13, fontFamily:"inherit",
          fontWeight: active ? 500 : 400, opacity: locked ? 0.5 : 1,
          position:"relative",
          justifyContent: collapsed ? "center" : "flex-start",
          transition: "background 0.15s, color 0.15s",
        }}
        onMouseEnter={e => { if (!active && !locked) e.currentTarget.style.background = T.bg3; }}
        onMouseLeave={e => { if (!active && !locked) e.currentTarget.style.background = "transparent"; }}>
        {active && !collapsed && (
          <span style={{ position:"absolute", left:-6, top:"50%", transform:"translateY(-50%)",
            width:3, height:18, background:T.gold, borderRadius:"0 2px 2px 0" }}/>
        )}
        <Icon name={item.icon} size={collapsed ? 18 : 16}/>
        {!collapsed && <span style={{ flex:1, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.label}</span>}
        {!collapsed && locked && <span style={{ fontSize:8, color:T.dim, letterSpacing:"0.1em" }}>PRO</span>}
      </button>
    );
  };

  const iconBtn = (active) => ({
    flex: collapsed ? "0 0 auto" : 1,
    display:"flex", alignItems:"center", justifyContent:"center", gap:6,
    padding: collapsed ? "8px" : "7px 8px",
    background: active ? T.bg3 : "transparent",
    border:`0.5px solid ${active ? T.gold : T.border}`,
    borderRadius:7,
    color: active ? T.gold : T.muted,
    fontSize:11, cursor:"pointer", fontFamily:"inherit",
    transition:"all 0.15s",
  });

  return (
    <div style={{
      width:W, background:T.bg1, borderRight:`0.5px solid ${T.border}`,
      padding:"20px 12px 16px", display:"flex", flexDirection:"column",
      position:"fixed", height:"100vh", top:0, left:0, zIndex:10,
      transition:"width 0.2s ease",
    }}>

      <div style={{ padding: collapsed ? "0 4px 18px" : "0 6px 18px",
        display:"flex", flexDirection:"column",
        alignItems: collapsed ? "center" : "flex-start",
        borderBottom: `0.5px solid ${T.border}` }}>
        {collapsed ? (
          <div style={{ width:36, height:36, borderRadius:9, background:T.goldDim,
            border:`0.5px solid ${T.gold}`, display:"flex", alignItems:"center", justifyContent:"center",
            color:T.gold, fontFamily:"Fraunces, serif", fontSize:18, fontWeight:300 }}>P</div>
        ) : (
          <>
            <div style={{ fontSize:9, letterSpacing:"0.22em", color:T.goldD,
              textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:3 }}>Purely Automated</div>
            <div style={{ fontSize:15, fontWeight:300, color:T.gold,
              fontFamily:"Fraunces, serif", letterSpacing:"0.04em" }}>Financial Intelligence</div>
          </>
        )}
        {inTrial && !collapsed && (
          <div style={{ marginTop:10, padding:"3px 9px", background:T.greenDim,
            border:`0.5px solid ${T.green}50`, borderRadius:11, fontSize:9, color:T.green,
            fontFamily:"'JetBrains Mono', monospace", letterSpacing:"0.1em" }}>
            ✦ TRIAL · {daysLeft}D LEFT
          </div>
        )}
      </div>

      <div style={{ flex:1, overflowY:"auto", overflowX:"hidden", paddingTop:14,
        paddingBottom:8, display:"flex", flexDirection:"column", gap:2 }}>
        {NAV_GROUPS.map((g, gi) => (
          <div key={g.label} style={{ marginBottom: 10 }}>
            {!collapsed && (
              <div style={{ fontSize:9, letterSpacing:"0.18em", color:T.dim,
                textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", padding:"6px 12px 4px" }}>
                {g.label}
              </div>
            )}
            {collapsed && gi > 0 && <div style={{ height:1, background:T.border, margin:"8px 6px" }}/>}
            <div style={{ display:"flex", flexDirection:"column", gap:1 }}>
              {g.items.map(item => <NavBtnNew key={item.id} item={item}/>)}
            </div>
          </div>
        ))}
      </div>

      <div style={{ paddingTop:12, borderTop:`0.5px solid ${T.border}`,
        display:"flex", flexDirection:"column", gap:6 }}>
        {!collapsed && (
          <div style={{ padding:"8px 10px 10px" }}>
            <div style={{ fontSize:9, color:T.dim, fontFamily:"'JetBrains Mono', monospace",
              letterSpacing:"0.12em", marginBottom:5 }}>CLIENT</div>
            <div style={{ fontSize:12, color:T.text, fontWeight:500, marginBottom:2,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{client.name}</div>
            <div style={{ fontSize:9, color:T.muted, fontFamily:"'JetBrains Mono', monospace",
              marginBottom:7 }}>{client.clientId}</div>
            <div style={{ display:"inline-flex", padding:"2px 8px",
              background:`${tier.color}18`, border:`0.5px solid ${tier.color}50`, borderRadius:10,
              fontSize:8, color:tier.color, letterSpacing:"0.1em", fontFamily:"'JetBrains Mono', monospace" }}>
              T{plan} · {tier.name.toUpperCase()}
            </div>
            <div style={{ marginTop:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ fontSize:10, color:T.muted }}>Score</span>
                <span style={{ fontSize:10, color:T.gold, fontFamily:"'JetBrains Mono', monospace" }}>{overallScore}</span>
              </div>
              <div style={{ background:T.bg4, borderRadius:2, height:2 }}>
                <div style={{ width:`${overallScore}%`, height:"100%",
                  background:`linear-gradient(90deg,${T.goldD},${T.gold})`, borderRadius:2 }}/>
              </div>
            </div>
          </div>
        )}

        <div style={{ display:"flex", flexDirection: collapsed ? "column" : "row", gap:6 }}>
          <button onClick={() => setView("howto")} title="How to Use" style={iconBtn(view === "howto")}>
            <Icon name="howto" size={16}/>
            {!collapsed && <span>Help</span>}
          </button>
          <button onClick={onSettings} title="Settings" style={iconBtn(settingsOpen)}>
            <Icon name="settings" size={16}/>
            {!collapsed && <span>Settings</span>}
          </button>
        </div>

        <button onClick={onSignOut} title="Sign Out" style={{
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          padding: collapsed ? "8px" : "8px 12px",
          background:"transparent", border:`0.5px solid ${T.border}`, borderRadius:7,
          color:T.muted, fontSize:11, cursor:"pointer", fontFamily:"inherit",
          transition:"all 0.15s",
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.red; e.currentTarget.style.color = T.red; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}>
          <Icon name="logout" size={14}/>
          {!collapsed && <span>Sign Out</span>}
        </button>

        <button onClick={() => setCollapsed(c => !c)} style={{
          marginTop:4, padding:"6px",
          background:"transparent", border:`0.5px dashed ${T.border}`, borderRadius:6,
          color:T.dim, cursor:"pointer", fontFamily:"inherit",
          display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontSize:10,
        }}
          onMouseEnter={e => e.currentTarget.style.color = T.muted}
          onMouseLeave={e => e.currentTarget.style.color = T.dim}>
          <Icon name={collapsed ? "chevronRight" : "chevronLeft"} size={12}/>
          {!collapsed && <span style={{ letterSpacing:"0.1em", textTransform:"uppercase" }}>Collapse</span>}
        </button>
      </div>
    </div>
  );
};
const renderMD = (text, G) => text.split("\n").map((line,i) => {
  if (line.startsWith("## ")) return <div key={i} style={{fontSize:11,fontWeight:600,color:G.gold,letterSpacing:"0.14em",textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginTop:20,marginBottom:10,paddingBottom:8,borderBottom:`0.5px solid ${G.borderGold}`}}>{line.slice(3)}</div>;
  if (line.startsWith("• ")||line.startsWith("- ")) return <div key={i} style={{display:"flex",gap:10,marginBottom:6}}><span style={{color:G.goldD,flexShrink:0,marginTop:1}}>▸</span><span style={{fontSize:13,color:G.text,lineHeight:1.65}}>{line.slice(2)}</span></div>;
  if (!line.trim()) return <div key={i} style={{height:6}}/>;
  return <div key={i} style={{fontSize:13,color:G.muted,lineHeight:1.65,marginBottom:3}}>{line}</div>;
});
const Icon = ({ name, size = 18 }) => {
  const P = {
    dashboard:    <><path d="M3 12L12 3l9 9"/><path d="M5 10v10h5v-6h4v6h5V10"/></>,
    income:       <><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></>,
    expense:      <><path d="M12 5v14"/><path d="M5 12l7 7 7-7"/></>,
    walog:        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>,
    bankimport:   <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></>,
    analysis:     <><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></>,
    personality:  <><circle cx="12" cy="12" r="10"/><path d="M9 9h.01M15 9h.01"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/></>,
    regret:       <><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></>,
    report:       <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M8 13h8M8 17h8M8 9h2"/></>,
    scenarios:    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>,
    freedom:      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>,
    fire:         <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>,
    debt:         <><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></>,
    assistant:    <><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/></>,
    howto:        <><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01"/></>,
    settings:     <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>,
    logout:       <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5M21 12H9"/></>,
    chevronLeft:  <path d="M15 18l-6-6 6-6"/>,
    chevronRight: <path d="M9 18l6-6-6-6"/>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {P[name] || P.dashboard}
    </svg>
  );
};

// ─── Upgrade Gate Card ────────────────────────────────────────────────────────
const UpgradeGate = ({tier, requiredTier, feature}) => {
  const G = useG();
  const req = TIERS.find(t => t.id === requiredTier);
  const cur = TIERS.find(t => t.id === tier);
  return (
  <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60vh",gap:20,textAlign:"center"}}>
    <div style={{fontSize:36,color:G.goldD}}>◈</div>
    <div style={{fontSize:22,fontWeight:300,color:G.gold,fontFamily:"Fraunces, serif"}}>{feature}</div>
    <div style={{fontSize:13,color:G.muted,maxWidth:380}}>This feature is available on the <strong style={{color:G.text}}>{req?.name}</strong> plan (${req?.price}/mo) and above.</div>
    <div style={{padding:"6px 18px",background:G.goldDim,border:`0.5px solid ${G.gold}`,borderRadius:20,fontSize:11,color:G.gold,letterSpacing:"0.1em"}}>You are on Tier {tier} — {cur?.name || "Free"}</div>
    <div style={{fontSize:12,color:G.dim}}>Contact Purely Automated to upgrade your subscription.</div>
  </div>
);};

// ─── Payment Links (Stripe) ──────────────────────────────────────────────────
// Replace these with your Stripe Payment Link URLs from dashboard.stripe.com → Payment Links
const PAYMENT_LINKS = {
  1: "https://rzp.io/rzp/aHjsmH4",
  2: "https://rzp.io/rzp/59psuh7",
};

// ─── Trial Expired Modal ──────────────────────────────────────────────────────
const TrialExpiredModal = ({client, onDismiss}) => {
  const G = useG();
  const tier = TIERS.find(t => t.id === (client?.plan ?? 0));
  const upgradeTiers = TIERS.filter(t => t.id > 0); // Free isn't an upgrade option
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(7,7,10,0.88)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(6px)"}}>
      <div style={{width:480,background:G.bg1,border:`0.5px solid ${G.gold}`,borderRadius:20,padding:"40px 36px",textAlign:"center",fontFamily:"Geist, sans-serif",color:G.text,boxShadow:`0 0 60px ${G.goldDim}`}}>
        <div style={{fontSize:10,letterSpacing:"0.22em",color:G.goldD,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:16}}>Purely Automated</div>
        <div style={{fontSize:30,fontWeight:300,fontFamily:"Fraunces, serif",color:G.gold,lineHeight:1.2,marginBottom:10}}>Your free trial has ended</div>
        <div style={{fontSize:13,color:G.muted,lineHeight:1.7,marginBottom:28,maxWidth:360,margin:"0 auto 28px"}}>You had full access to the <strong style={{color:G.text}}>{tier?.name}</strong> plan. To continue tracking your wealth and accessing AI insights, choose a plan below — or stay on Free with the essentials.</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:24}}>
          {upgradeTiers.map(t=>(
            <div key={t.id} style={{padding:"16px 12px",background:t.recommended?G.goldDim:G.bg2,border:`0.5px solid ${t.recommended?G.gold:G.border}`,borderRadius:12,cursor:"pointer",transition:"border-color 0.15s"}} onClick={()=>window.open(PAYMENT_LINKS[t.id],"_blank")}>
              {t.recommended&&<div style={{fontSize:8,letterSpacing:"0.14em",color:G.gold,marginBottom:6,fontFamily:"'JetBrains Mono', monospace"}}>POPULAR</div>}
              <div style={{fontSize:14,fontWeight:500,color:t.color,marginBottom:4}}>{t.name}</div>
              <div style={{fontSize:22,color:t.color,fontFamily:"Fraunces, serif",fontWeight:300}}>${t.price}<span style={{fontSize:11,color:G.muted}}>/mo</span></div>
              <div style={{marginTop:8,fontSize:10,color:G.gold,fontFamily:"'JetBrains Mono', monospace",letterSpacing:"0.08em"}}>Subscribe →</div>
            </div>
          ))}
        </div>
        <div style={{fontSize:11,color:G.dim,marginBottom:18}}>Secure checkout via <span style={{color:G.muted}}>Stripe</span> · Cards, Apple Pay, Google Pay accepted</div>
        <button onClick={onDismiss} style={{padding:"10px 24px",background:"transparent",border:`0.5px solid ${G.border}`,borderRadius:8,color:G.muted,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Continue on Free plan</button>
      </div>
    </div>
  );
};


const ProductPreview = () => (
  <div style={{ position:"relative", maxWidth:920, margin:"60px auto 0", padding:"0 24px" }}>
    <div style={{ position:"absolute", top:-60, left:"50%", transform:"translateX(-50%)",
      width:700, height:350,
      background:"radial-gradient(ellipse 60% 40% at 50% 50%,rgba(255,181,97,0.14),transparent)",
      pointerEvents:"none", zIndex:0 }}/>
    <div style={{ position:"relative", zIndex:1, borderRadius:20, overflow:"hidden",
      border:"1px solid rgba(255,255,255,0.08)",
      boxShadow:"0 40px 120px rgba(0,0,0,0.75),0 0 0 1px rgba(255,255,255,0.04)" }}>
      {/* Browser bar */}
      <div style={{ background:"#0A0812", padding:"11px 18px",
        borderBottom:"1px solid rgba(255,255,255,0.06)",
        display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ display:"flex", gap:6 }}>
          {["#FF5F57","#FEBC2E","#28C840"].map(c =>
            <div key={c} style={{ width:9, height:9, borderRadius:"50%", background:c }}/>)}
        </div>
        <div style={{ flex:1, background:"rgba(255,255,255,0.04)", borderRadius:6,
          padding:"4px 14px", fontSize:10, color:"rgba(255,255,255,0.25)",
          fontFamily:"monospace", letterSpacing:"0.08em" }}>
          ● app.purelyautomated.com
        </div>
        <div style={{ width:26, height:6, background:"rgba(255,255,255,0.06)", borderRadius:3 }}/>
      </div>
      {/* App mockup */}
      <div style={{ display:"flex", height:480, background:"#08070C" }}>
        {/* Sidebar */}
        <div style={{ width:178, background:"#0E0C14",
          borderRight:"1px solid rgba(255,255,255,0.06)",
          padding:"18px 12px", display:"flex", flexDirection:"column", flexShrink:0 }}>
          <div style={{ paddingBottom:14, borderBottom:"1px solid rgba(255,255,255,0.06)", marginBottom:14 }}>
            <div style={{ fontSize:7, color:"#D88A3E", fontFamily:"monospace", letterSpacing:"0.3em", marginBottom:3 }}>UNBROKE</div>
            <div style={{ fontSize:13, color:"#FFB561", fontFamily:"serif", fontStyle:"italic" }}>wealth, alive</div>
          </div>
          {[
            { icon:"◐", label:"Pulse",       active:true  },
            { icon:"↑", label:"Add Income",  active:false },
            { icon:"↓", label:"Add Expense", active:false },
            { icon:"✦", label:"AI Analysis", locked:true  },
            { icon:"△", label:"Scenarios",   locked:true  },
            { icon:"▲", label:"FIRE Calc",   active:false },
            { icon:"▭", label:"Debt Tracker",active:false },
          ].map((item, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"7px 10px",
              marginBottom:2, borderRadius:8,
              background:item.active?"rgba(255,181,97,0.12)":"transparent",
              borderLeft:`2px solid ${item.active?"#FFB561":"transparent"}` }}>
              <span style={{ fontSize:11, color:item.active?"#FFB561":item.locked?"rgba(255,255,255,0.1)":"rgba(255,255,255,0.28)" }}>{item.icon}</span>
              <span style={{ fontSize:10, color:item.active?"#FFB561":item.locked?"rgba(255,255,255,0.1)":"rgba(255,255,255,0.28)" }}>{item.label}</span>
              {item.locked&&<span style={{ marginLeft:"auto", fontSize:7, color:"rgba(255,255,255,0.15)", fontFamily:"monospace" }}>T1</span>}
            </div>
          ))}
        </div>
        {/* Main content */}
        <div style={{ flex:1, padding:"22px 24px", overflow:"hidden", minWidth:0 }}>
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:8, color:"rgba(255,255,255,0.2)", fontFamily:"monospace", letterSpacing:"0.25em", marginBottom:5 }}>MONDAY · MAY 2026</div>
            <div style={{ display:"flex", alignItems:"baseline", gap:10 }}>
              <span style={{ fontFamily:"serif", fontSize:26, fontWeight:300, color:"#F5EFE6", lineHeight:1 }}>Good evening,</span>
              <span style={{ fontFamily:"serif", fontSize:26, fontWeight:300, color:"#FFB561", fontStyle:"italic", lineHeight:1 }}>Arjun.</span>
            </div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:6 }}>
              Your wealth pulse is steady. Income up <span style={{ color:"#6FE9B5" }}>12.4%</span> vs prior month.
            </div>
          </div>
          {/* Hero card */}
          <div style={{ background:"linear-gradient(135deg,rgba(255,255,255,0.03),rgba(255,255,255,0.01))",
            border:"1px solid rgba(255,255,255,0.06)", borderRadius:14, padding:"18px 22px", marginBottom:14 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:24, alignItems:"center" }}>
              <div>
                <div style={{ fontSize:7, color:"#D88A3E", fontFamily:"monospace", letterSpacing:"0.3em", marginBottom:8 }}>TOTAL WEALTH ACCUMULATED</div>
                <div style={{ fontFamily:"serif", fontSize:52, fontWeight:300, color:"#F5EFE6", lineHeight:0.9, letterSpacing:"-0.03em" }}>
                  ₹12.8<span style={{ color:"#FFB561" }}>L</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:10 }}>
                  <span style={{ fontSize:10, color:"#6FE9B5", background:"rgba(111,233,181,0.12)", padding:"2px 8px", borderRadius:20, fontFamily:"monospace" }}>↑ 31.2%</span>
                  <span style={{ fontSize:10, color:"rgba(255,255,255,0.35)" }}>vs 6 months ago</span>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginTop:14,
                  paddingTop:12, borderTop:"1px solid rgba(255,255,255,0.06)" }}>
                  {[["INCOME","₹85,000","#6FE9B5"],["OUTFLOW","₹61,200","#FF8E7C"],["NET","₹23,800","#FFB561"]].map(([l,v,c]) => (
                    <div key={l}>
                      <div style={{ fontSize:7, color:"rgba(255,255,255,0.2)", fontFamily:"monospace", letterSpacing:"0.2em", marginBottom:4 }}>{l}</div>
                      <div style={{ fontSize:15, color:"#F5EFE6", fontFamily:"serif", fontWeight:300 }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Score ring */}
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                <div style={{ fontSize:7, color:"rgba(255,255,255,0.2)", fontFamily:"monospace", letterSpacing:"0.2em" }}>BEHAVIORAL</div>
                <svg width="90" height="90" viewBox="0 0 90 90">
                  <circle cx="45" cy="45" r="36" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="7"/>
                  <circle cx="45" cy="45" r="36" fill="none" stroke="#FFB561" strokeWidth="7"
                    strokeLinecap="round" strokeDasharray="226" strokeDashoffset="56"
                    transform="rotate(-90 45 45)" style={{ filter:"drop-shadow(0 0 6px #FFB561)" }}/>
                  <text x="45" y="42" textAnchor="middle" fontSize="20" fill="#F5EFE6" fontFamily="serif" fontWeight="300">76</text>
                  <text x="45" y="54" textAnchor="middle" fontSize="6" fill="#FFB561" fontFamily="monospace" letterSpacing="1">STRONG</text>
                </svg>
              </div>
            </div>
          </div>
          {/* Mini chart */}
          <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)",
            borderRadius:10, padding:"12px 14px" }}>
            <div style={{ fontSize:7, color:"rgba(255,255,255,0.2)", fontFamily:"monospace", letterSpacing:"0.2em", marginBottom:8 }}>6-MONTH CASH FLOW</div>
            <div style={{ display:"flex", alignItems:"flex-end", gap:4, height:56 }}>
              {[[60,45],[65,48],[70,44],[78,50],[82,52],[90,56]].map(([inc,exp],i) => (
                <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", gap:2, height:"100%", justifyContent:"flex-end" }}>
                  <div style={{ height:`${exp*0.55}%`, background:i===5?"rgba(255,142,124,0.7)":"rgba(255,142,124,0.25)", borderRadius:"2px 2px 0 0" }}/>
                  <div style={{ height:`${inc*0.55}%`, background:i===5?"rgba(111,233,181,0.8)":"rgba(111,233,181,0.3)", borderRadius:"2px 2px 0 0" }}/>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div style={{ textAlign:"center", marginTop:18, fontSize:10, color:"rgba(255,255,255,0.2)", fontFamily:"monospace", letterSpacing:"0.14em" }}>
      ✦ DEMO DATA SHOWN · YOUR ACTUAL DATA POPULATES INSTANTLY
    </div>
  </div>
);



// ─── Landing Page ─────────────────────────────────────────────────────────────

// ─── Register Screen ──────────────────────────────────────────────────────────
const RegisterScreen = ({ onLogin, onBack }) => {
  const G = useG();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [goal, setGoal] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [emailSent, setEmailSent] = useState(false);
 
  const handleGoogle = async () => {
    setLoading(true); setErr("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin, queryParams: { access_type:"offline", prompt:"select_account" } },
    });
    if (error) { setErr(error.message); setLoading(false); }
  };
 
  const handleEmailSignUp = async () => {
    if (!name.trim()||!email.trim()||!password.trim()) { setErr("Please fill in all fields."); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setLoading(true); setErr("");
    const { data, error } = await supabase.auth.signUp({ email: email.trim(), password });
    if (error) { setErr(error.message); setLoading(false); return; }
    if (data.session) {
      const user = await findOrCreateUserByEmail(data.user.email, name.trim());
      if (user && goal.trim()) {
        await fetch(`${SUPABASE_URL}/rest/v1/users?client_id=eq.${user.clientId}`, {
          method:"PATCH", headers:{ ...sbHeaders(), "Prefer":"return=minimal" },
          body: JSON.stringify({ goal: goal.trim(), name: name.trim() }),
        });
        user.goal = goal.trim(); user.name = name.trim();
      }
      if (user) { onLogin(user); return; }
    }
    setEmailSent(true);
    setLoading(false);
  };
 
  const inp = {
    width:"100%", background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:8,
    padding:"12px 16px", color:G.text, fontSize:14, fontFamily:"Outfit,sans-serif",
    outline:"none", boxSizing:"border-box",
  };
 
  if (emailSent) return (
    <div style={{ minHeight:"100vh", background:G.bg0, display:"flex", alignItems:"center",
      justifyContent:"center", fontFamily:"Outfit,sans-serif", color:G.text }}>
      <div style={{ width:400, textAlign:"center", display:"flex", flexDirection:"column", alignItems:"center", gap:16 }}>
        <div style={{ fontSize:48 }}>📬</div>
        <div style={{ fontSize:22, fontWeight:300, color:G.gold, fontFamily:"Cormorant Garamond,serif" }}>Check your email</div>
        <div style={{ fontSize:13, color:G.muted, lineHeight:1.7 }}>
          We sent a confirmation link to <strong style={{ color:G.text }}>{email}</strong>.<br/>
          Click it to activate your account, then sign in.
        </div>
        <button onClick={onBack} style={{ padding:"12px 28px", background:G.goldDim,
          border:`0.5px solid ${G.gold}`, borderRadius:10, color:G.gold, fontSize:13,
          cursor:"pointer", fontFamily:"inherit", fontWeight:500 }}>
          Go to Sign In →
        </button>
      </div>
    </div>
  );
 
  return (
    <div style={{ minHeight:"100vh", background:G.bg0, display:"flex", alignItems:"center",
      justifyContent:"center", fontFamily:"Outfit,sans-serif", color:G.text,
      backgroundImage:`radial-gradient(ellipse at 50% 0%,${G.goldDim}55 0%,transparent 60%)` }}>
      <div style={{ width:440 }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ fontSize:9, letterSpacing:"0.28em", color:G.goldD, textTransform:"uppercase",
            fontFamily:"DM Mono,monospace", marginBottom:8 }}>Purely Automated</div>
          <div style={{ fontSize:28, fontWeight:300, color:G.gold, fontFamily:"Cormorant Garamond,serif", letterSpacing:"0.04em" }}>
            Create your account
          </div>
          <div style={{ fontSize:12, color:G.muted, marginTop:6 }}>Free forever. No credit card required.</div>
        </div>
 
        <div style={{ background:G.bg1, border:`0.5px solid ${G.border}`, borderRadius:16, padding:"28px 28px 24px",
          display:"flex", flexDirection:"column", gap:14 }}>
          {/* Google */}
          <button onClick={handleGoogle} disabled={loading}
            style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, padding:"13px",
              background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:10, color:G.text,
              fontSize:14, fontFamily:"inherit", fontWeight:500, cursor:"pointer", transition:"all 0.15s", opacity:loading?0.6:1 }}
            onMouseEnter={e=>{ e.currentTarget.style.borderColor=G.gold; e.currentTarget.style.background=G.bg3; }}
            onMouseLeave={e=>{ e.currentTarget.style.borderColor=G.border; e.currentTarget.style.background=G.bg2; }}>
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.259c-.805.54-1.835.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>
 
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ flex:1, height:"0.5px", background:G.border }}/>
            <span style={{ fontSize:11, color:G.dim, fontFamily:"DM Mono,monospace" }}>or</span>
            <div style={{ flex:1, height:"0.5px", background:G.border }}/>
          </div>
 
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" style={inp}/>
            <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email address" type="email" style={inp}/>
            <input value={password} onChange={e=>setPassword(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleEmailSignUp()}
              placeholder="Password (min 6 chars)" type="password" style={inp}/>
            <input value={goal} onChange={e=>setGoal(e.target.value)}
              placeholder="Financial goal (optional) — e.g. Retire by 45" style={inp}/>
          </div>
 
          {err && (
            <div style={{ background:G.redDim, border:`0.5px solid ${G.red}40`, borderRadius:8,
              padding:"10px 14px", fontSize:12, color:G.red, textAlign:"center" }}>{err}</div>
          )}
 
          <button onClick={handleEmailSignUp} disabled={(loading) || (!name.trim()) || (!email.trim()) || (!password.trim())}
            style={{ padding:"13px", background:G.goldDim, border:`0.5px solid ${G.gold}`,
              borderRadius:10, color:G.gold, fontSize:14, fontFamily:"inherit", fontWeight:500,
              cursor:loading||!name.trim()||!email.trim()||!password.trim()?"not-allowed":"pointer",
              opacity:loading||!name.trim()||!email.trim()||!password.trim()?0.5:1, transition:"all 0.2s" }}>
            {loading?"◌  Creating account…":"Create account →"}
          </button>
        </div>
 
        <div style={{ textAlign:"center", marginTop:16, fontSize:12, color:G.dim }}>
          Already have an account?{" "}
          <button onClick={onBack} style={{ background:"none", border:"none", color:G.muted,
            cursor:"pointer", fontSize:12, fontFamily:"inherit" }}>Sign in</button>
        </div>
      </div>
    </div>
  );
};

// ─── Login Screen ─────────────────────────────────────────────────────────────
const LoginScreen = ({onLogin, onRegister}) => {
  const G = useG();
  // tab: "oauth" | "legacy"
  const [tab, setTab] = useState("oauth");
  // email+password state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // legacy state
  const [cid, setCid] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ── Google OAuth ──
  const handleGoogle = async () => {
    setLoading(true); setErr("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
        queryParams: { access_type: "offline", prompt: "select_account" },
      },
    });
    if (error) { setErr(error.message); setLoading(false); }
    // On success the page redirects; App's onAuthStateChange picks it up.
  };

  // ── Email + Password ──
  const handleEmailSignIn = async () => {
    if (!email.trim() || !password.trim()) { setErr("Please enter your email and password."); return; }
    setLoading(true); setErr("");
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) { setErr(error.message); setLoading(false); return; }
    const user = await findOrCreateUserByEmail(data.user.email, data.user.user_metadata?.full_name);
    if (user) onLogin(user);
    else { setErr("Account lookup failed — please try again."); setLoading(false); }
  };

  const handleEmailSignUp = async () => {
    if (!email.trim() || !password.trim()) { setErr("Please enter your email and password."); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setLoading(true); setErr("");
    const { data, error } = await supabase.auth.signUp({ email: email.trim(), password });
    if (error) { setErr(error.message); setLoading(false); return; }
    // If email confirmation is off, session is live immediately
    if (data.session) {
      const user = await findOrCreateUserByEmail(data.user.email, data.user.user_metadata?.full_name);
      if (user) onLogin(user);
      else { setErr("Account creation failed — please try again."); setLoading(false); }
    } else {
      setErr(""); setLoading(false);
      alert("Check your email to confirm your account, then sign in.");
    }
  };

  // ── Legacy Client ID + PIN ──
  const handleLegacyLogin = async () => {
    if (!cid.trim() || pin.length !== 4) { setErr("Please enter your Client ID and 4-digit PIN."); return; }
    setLoading(true); setErr("");
    const users = (await sGet("pa_users")) || [];
    let user = users.find(u => u.clientId === cid.trim().toUpperCase() && u.pin === pin);
    if (!user) {
      try {
        const r = await fetch(
          `${SUPABASE_URL}/rest/v1/users?client_id=eq.${cid.trim().toUpperCase()}&pin=eq.${pin}&select=*`,
          { headers: sbHeaders() }
        );
        const data = await r.json();
        if (data && data[0]) {
          const u = data[0];
          user = { clientId: u.client_id, name: u.name, email: u.email, goal: u.goal, plan: u.plan, pin: u.pin, createdAt: u.created_at, trialStart: u.trial_start };
          const allUsers = (await sGet("pa_users")) || [];
          allUsers.push(user);
          await sSet("pa_users", allUsers);
        }
      } catch {}
    }
    if (user) { onLogin(user); }
    else { setErr("Invalid Client ID or PIN."); setPin(""); }
    setLoading(false);
  };
  const pk = d => { if (pin.length < 4) setPin(p => p + d); };

  const inp = { width:"100%", background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:8, padding:"12px 16px", color:G.text, fontSize:14, fontFamily:"Geist, sans-serif", outline:"none", boxSizing:"border-box" };
  const tabBtn = active => ({ flex:1, padding:"10px", background:active?G.bg3:"transparent", border:active?`0.5px solid ${G.gold}`:"none", borderRadius:8, color:active?G.gold:G.muted, fontSize:12, cursor:"pointer", fontFamily:"inherit", fontWeight:active?500:400, transition:"all 0.15s", letterSpacing:"0.04em" });

  return (
    <div style={{minHeight:"100vh",background:G.bg0,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"Geist, sans-serif",color:G.text,backgroundImage:`radial-gradient(ellipse at 50% 0%, ${G.goldDim}55 0%, transparent 60%)`}}>
      <div style={{width:420,display:"flex",flexDirection:"column",alignItems:"center"}}>
        {/* Header */}
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:9,letterSpacing:"0.28em",color:G.goldD,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>Purely Automated</div>
          <div style={{fontSize:32,fontWeight:300,color:G.gold,fontFamily:"Fraunces, serif",letterSpacing:"0.04em",lineHeight:1.2}}>Financial Intelligence</div>
          <div style={{fontSize:12,color:G.muted,marginTop:8}}>Private Client Portal</div>
        </div>

        <div style={{width:"100%",background:G.bg1,border:`0.5px solid ${G.border}`,borderRadius:16,padding:"28px 28px 24px",display:"flex",flexDirection:"column",gap:18}}>
          {/* Tab switcher */}
          <div style={{display:"flex",gap:2,background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:10,padding:3}}>
            <button style={tabBtn(tab==="oauth")} onClick={()=>{setTab("oauth");setErr("");}}>Sign In</button>
            <button style={tabBtn(tab==="legacy")} onClick={()=>{setTab("legacy");setErr("");}}>♯ Client ID</button>
          </div>

          {tab==="oauth" && (
            <>
              {/* Google */}
              <button onClick={handleGoogle} disabled={loading} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"13px",background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:10,color:G.text,fontSize:14,fontFamily:"inherit",fontWeight:500,cursor:"pointer",transition:"all 0.15s",opacity:loading?0.6:1}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=G.gold;e.currentTarget.style.background=G.bg3;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=G.border;e.currentTarget.style.background=G.bg2;}}>
                <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.259c-.805.54-1.835.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/></svg>
                Continue with Google
              </button>

              {/* Divider */}
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{flex:1,height:"0.5px",background:G.border}}/>
                <span style={{fontSize:11,color:G.dim,fontFamily:"'JetBrains Mono', monospace"}}>or</span>
                <div style={{flex:1,height:"0.5px",background:G.border}}/>
              </div>

              {/* Email + Password */}
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <input value={email} onChange={e=>{setEmail(e.target.value);setErr("");}} placeholder="Email address" type="email" style={inp}/>
                <input value={password} onChange={e=>{setPassword(e.target.value);setErr("");}} onKeyDown={e=>e.key==="Enter"&&handleEmailSignIn()} placeholder="Password" type="password" style={inp}/>
              </div>
              {err&&<div style={{background:G.redDim,border:`0.5px solid ${G.red}40`,borderRadius:8,padding:"10px 14px",fontSize:12,color:G.red,textAlign:"center"}}>{err}</div>}
              <div style={{display:"flex",gap:8}}>
                <button onClick={handleEmailSignIn} disabled={loading} style={{flex:2,padding:"13px",background:G.goldDim,border:`0.5px solid ${G.gold}`,borderRadius:10,color:G.gold,fontSize:14,fontFamily:"inherit",fontWeight:500,cursor:loading?"not-allowed":"pointer",opacity:loading?0.5:1,transition:"all 0.2s"}}>
                  {loading?"◌  Signing in…":"Sign In →"}
                </button>
                <button onClick={handleEmailSignUp} disabled={loading} style={{flex:1,padding:"13px",background:"transparent",border:`0.5px solid ${G.border}`,borderRadius:10,color:G.muted,fontSize:13,fontFamily:"inherit",cursor:loading?"not-allowed":"pointer",opacity:loading?0.5:1,transition:"all 0.2s"}}>
                  Sign Up
                </button>
              </div>
            </>
          )}

          {tab==="legacy" && (
            <>
              <div style={{fontSize:12,color:G.dim,textAlign:"center",lineHeight:1.6}}>Existing users — sign in with your original Client ID &amp; PIN</div>
              <div>
                <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>Client ID</div>
                <input value={cid} onChange={e=>{setCid(e.target.value.toUpperCase());setErr("");}} onKeyDown={e=>e.key==="Enter"&&handleLegacyLogin()} placeholder="e.g. PA-AB1CD" style={{...inp,fontFamily:"'JetBrains Mono', monospace",letterSpacing:"0.08em",fontSize:15}}/>
              </div>
              <div>
                <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:12}}>4-Digit PIN</div>
                <div style={{display:"flex",gap:12,justifyContent:"center",marginBottom:14}}>
                  {[0,1,2,3].map(i=><div key={i} style={{width:44,height:44,borderRadius:10,background:pin.length>i?G.goldDim:G.bg3,border:`0.5px solid ${pin.length>i?G.gold:G.border}`,display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.15s"}}>
                    {pin.length>i&&<div style={{width:10,height:10,borderRadius:"50%",background:G.gold}}/>}
                  </div>)}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                  {[1,2,3,4,5,6,7,8,9].map(d=><button key={d} onClick={()=>pk(String(d))} style={{padding:"14px",background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:10,color:G.text,fontSize:16,cursor:"pointer",fontFamily:"'JetBrains Mono', monospace",fontWeight:500}}>{d}</button>)}
                  <div/>
                  <button onClick={()=>pk("0")} style={{padding:"14px",background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:10,color:G.text,fontSize:16,cursor:"pointer",fontFamily:"'JetBrains Mono', monospace",fontWeight:500}}>0</button>
                  <button onClick={()=>setPin(p=>p.slice(0,-1))} style={{padding:"14px",background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:10,color:G.muted,fontSize:14,cursor:"pointer",fontFamily:"'JetBrains Mono', monospace"}}>⌫</button>
                </div>
              </div>
              {err&&<div style={{background:G.redDim,border:`0.5px solid ${G.red}40`,borderRadius:8,padding:"10px 14px",fontSize:12,color:G.red,textAlign:"center"}}>{err}</div>}
              <button onClick={handleLegacyLogin} disabled={loading||!cid.trim()||pin.length!==4} style={{padding:"13px",background:loading||!cid.trim()||pin.length!==4?G.bg4:G.goldDim,border:`0.5px solid ${G.gold}`,borderRadius:10,color:G.gold,fontSize:14,fontFamily:"inherit",fontWeight:500,letterSpacing:"0.05em",cursor:loading||!cid.trim()||pin.length!==4?"not-allowed":"pointer",opacity:loading||!cid.trim()||pin.length!==4?0.5:1,transition:"all 0.2s"}}>
                {loading?"◌  Verifying…":"Access Dashboard →"}
              </button>
            </>
          )}
        </div>

        <div style={{fontSize:11,color:G.dim,marginTop:20,textAlign:"center"}}>New client? <button onClick={onRegister} style={{background:"none",border:"none",color:G.muted,cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>Create account →</button></div>
      </div>
    </div>
  );
};

// ─── Add Transaction View ─────────────────────────────────────────────────────
const AddTransaction = ({transactions, onAdd, onDelete}) => {
  const { fmtN, sym } = useFmt();
  const G = useG();
  const [type, setType] = useState("income");
  const [cat, setCat] = useState("");
  const [amount, setAmount] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);
  const monthOpts = getMonthOpts();
  const cats = type==="income"?INCOME_CATS:EXPENSE_CATS;

  const submit = async () => {
    if (!cat||!amount||isNaN(parseFloat(amount))||parseFloat(amount)<=0) return;
      await onAdd({id:genTxId(),type,category:cat,amount:parseFloat(amount),month,note:note.trim(),created_at:new Date().toISOString(),ts:Date.now()});
    setAmount("");setNote("");setCat("");setSaved(true);setTimeout(()=>setSaved(false),2000);
  };

  const recent = [...transactions].sort((a,b)=>b.createdAt?.localeCompare(a.createdAt||"")).slice(0,30);
  const catMap = [...INCOME_CATS,...EXPENSE_CATS].reduce((m,c)=>{m[c.name]=c;return m;},{});

  return (
    <div style={{display:"flex",gap:20,alignItems:"flex-start"}}>
      {/* Left: Form */}
      <div style={{flex:1,display:"flex",flexDirection:"column",gap:16}}>
        {/* Type toggle */}
        <div style={{display:"flex",gap:2,background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:10,padding:3}}>
          {["income","expense"].map(t=>(
            <button key={t} onClick={()=>{setType(t);setCat("");}} style={{flex:1,padding:"10px",background:type===t?G.bg3:"transparent",border:type===t?`0.5px solid ${type==="income"?G.green:G.red}`:"none",borderRadius:8,color:type===t?(type==="income"?G.green:G.red):G.muted,fontSize:13,cursor:"pointer",fontFamily:"inherit",fontWeight:500,transition:"all 0.15s",letterSpacing:"0.05em"}}>
              {t==="income"?"↑ Income":"↓ Expense"}
            </button>
          ))}
        </div>

        {/* Category grid */}
        <div>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:10}}>Category</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:7}}>
            {cats.map(c=>(
              <button key={c.name} onClick={()=>setCat(c.name)} style={{padding:"10px 6px",background:cat===c.name?`${c.color}22`:G.bg2,border:`0.5px solid ${cat===c.name?c.color:G.border}`,borderRadius:9,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4,transition:"all 0.12s"}}>
                <span style={{fontSize:16}}>{c.icon}</span>
                <span style={{fontSize:10,color:cat===c.name?c.color:G.muted,textAlign:"center",lineHeight:1.3}}>{c.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Amount + Month + Note */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div>
            <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>Amount</div>
            <input value={amount} onChange={e=>setAmount(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()} placeholder="0.00" type="number" min="0" style={{width:"100%",background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:8,padding:"12px 16px",color:G.text,fontSize:20,fontFamily:"'JetBrains Mono', monospace",outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div>
            <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>Month</div>
            <select value={month} onChange={e=>setMonth(e.target.value)} style={{width:"100%",background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:8,padding:"12px 16px",color:G.text,fontSize:14,fontFamily:"'JetBrains Mono', monospace",outline:"none",cursor:"pointer",boxSizing:"border-box"}}>
              {monthOpts.map(m=><option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>Note <span style={{color:G.dim,fontStyle:"normal"}}>(optional)</span></div>
          <input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Monthly salary, Netflix, Groceries..." style={{width:"100%",background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:8,padding:"12px 16px",color:G.text,fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
        </div>

        <button onClick={submit} disabled={!cat||!amount||parseFloat(amount)<=0} style={{padding:"14px",background:!cat||!amount||parseFloat(amount)<=0?G.bg4:type==="income"?`${G.green}22`:`${G.red}22`,border:`0.5px solid ${!cat||!amount||parseFloat(amount)<=0?G.border:type==="income"?G.green:G.red}`,borderRadius:10,color:!cat||!amount||parseFloat(amount)<=0?G.dim:type==="income"?G.green:G.red,fontSize:14,cursor:!cat||!amount||parseFloat(amount)<=0?"not-allowed":"pointer",fontFamily:"inherit",fontWeight:500,transition:"all 0.15s",opacity:!cat||!amount||parseFloat(amount)<=0?0.5:1}}>
          {saved?"✓ Saved!":`Add ${type==="income"?"Income":"Expense"} →`}
        </button>
      </div>

      {/* Right: History */}
      <div style={{width:320,background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"18px 16px",maxHeight:"75vh",overflowY:"auto"}}>
        <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:14}}>Recent Entries</div>
        {recent.length===0
          ?<div style={{textAlign:"center",padding:"40px 0",color:G.dim,fontSize:12}}>No entries yet.<br/>Add your first transaction.</div>
          :recent.map(t=>{const c=catMap[t.category]||{color:G.muted,icon:"📦"};return(
            <div key={t.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`0.5px solid ${G.border}`}}>
              <span style={{fontSize:18,flexShrink:0}}>{c.icon}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,color:G.text,fontWeight:500}}>{t.category}</div>
                <div style={{fontSize:11,color:G.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.note||fmtMk(t.month)}</div>
                <div style={{fontSize:10,color:G.dim,fontFamily:"'JetBrains Mono', monospace"}}>{fmtMk(t.month)}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:13,color:t.type==="income"?G.green:G.red,fontFamily:"'JetBrains Mono', monospace",fontWeight:500}}>{t.type==="income"?"+":"-"}{fmtN(t.amount)}</div>
                <button onClick={()=>onDelete(t.id)} style={{background:"none",border:"none",color:G.dim,cursor:"pointer",fontSize:11,padding:"2px 0",fontFamily:"inherit"}} onMouseEnter={e=>e.target.style.color=G.red} onMouseLeave={e=>e.target.style.color=G.dim}>✕</button>
              </div>
            </div>
          );})}
      </div>
    </div>
  );
};

const RadialGauge = ({ value, max = 100, size = 150, color }) => {
  const G = useG();
  const r = (size / 2) - 12;
  const c = 2 * Math.PI * r;
  const pct = Math.min(Math.max(value / max, 0), 1);
  const offset = c - (pct * c);
  const cx = size / 2, cy = size / 2;
  const gradId = `rg-grad-${color?.replace(/[^a-z0-9]/gi, "")}`;

  return (
    <div style={{ position:"relative", width:size, height:size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={color || G.gold} stopOpacity="0.5"/>
            <stop offset="100%" stopColor={color || G.gold} stopOpacity="1"/>
          </linearGradient>
        </defs>
        <circle cx={cx} cy={cy} r={r} stroke={G.bg4} strokeWidth={7} fill="none"/>
        <circle cx={cx} cy={cy} r={r} stroke={`url(#${gradId})`} strokeWidth={7} fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition:"stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)" }}/>
      </svg>
      <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center" }}>
        <div style={{ fontSize:38, fontWeight:300, color:color || G.gold,
          fontFamily:"Fraunces, serif", lineHeight:1, letterSpacing:"-0.02em" }}>{Math.round(value)}</div>
        <div style={{ fontSize:9, color:G.muted, fontFamily:"'JetBrains Mono', monospace",
          letterSpacing:"0.14em", marginTop:4 }}>OF {max}</div>
      </div>
    </div>
  );
};

const SnapshotStat = ({ icon, label, value, change, color }) => {
  const G = useG();
  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:6 }}>
        <span style={{ fontSize:14, color, fontFamily:"'JetBrains Mono', monospace", fontWeight:500, width:14, textAlign:"center" }}>{icon}</span>
        <span style={{ fontSize:10, color:G.muted, letterSpacing:"0.12em", textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace" }}>{label}</span>
      </div>
      <div style={{ fontSize:24, fontWeight:300, color:G.text, fontFamily:"Fraunces, serif", letterSpacing:"-0.01em", lineHeight:1.1 }}>{value}</div>
      {change !== undefined && change !== null && change !== 0 && (
        <div style={{ fontSize:10, color: change > 0 ? G.green : G.red, marginTop:4, fontFamily:"'JetBrains Mono', monospace", letterSpacing:"0.04em" }}>
          {change > 0 ? "↑" : "↓"} {Math.abs(change).toFixed(1)}% vs prior month
        </div>
      )}
    </div>
  );
};

const HeroCard = ({ monthly, scores }) => {
  const G = useG();
  const { fmtN } = useFmt();

  const latest = monthly.length > 0 ? monthly[monthly.length - 1] : {};
  const prev = monthly.length > 1 ? monthly[monthly.length - 2] : {};
  const overall = scores.length > 0
    ? Math.round(scores.reduce((s,sc) => s+sc.score, 0) / scores.length)
    : 0;

  const incomeChange = prev.income > 0 ? ((latest.income - prev.income) / prev.income * 100) : null;
  const netFlow = (latest.income || 0) - (latest.expenses || 0);

  const tier = overall >= 80 ? { label:"ELITE",       color:G.green }
             : overall >= 65 ? { label:"STRONG",      color:G.gold }
             : overall >= 50 ? { label:"DEVELOPING",  color:G.blue }
             :                  { label:"NEEDS FOCUS", color:G.red };

  const hasData = monthly.length > 0;

  return (
    <div style={{
      background: `linear-gradient(135deg, ${G.bg2} 0%, ${G.bg1} 100%)`,
      border: `0.5px solid ${G.borderGold}`,
      borderRadius: 16,
      padding: "28px 36px",
      display: "grid",
      gridTemplateColumns: "auto 1fr",
      gap: 36,
      alignItems: "center",
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{ position:"absolute", top:-80, right:-60, width:340, height:340,
        background:`radial-gradient(circle, ${G.goldDim}66 0%, transparent 70%)`,
        pointerEvents:"none" }}/>

      <div style={{ position:"relative", zIndex:1, display:"flex", flexDirection:"column",
        alignItems:"center", gap:12 }}>
        <div style={{ fontSize:9, letterSpacing:"0.2em", color:G.goldD,
          textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace" }}>Behavioral Intelligence</div>
        <RadialGauge value={overall} color={tier.color}/>
        <div style={{ padding:"4px 14px", background:`${tier.color}18`,
          border:`0.5px solid ${tier.color}50`, borderRadius:14, fontSize:10, color:tier.color,
          fontFamily:"'JetBrains Mono', monospace", letterSpacing:"0.14em" }}>{tier.label}</div>
      </div>

      <div style={{ position:"relative", zIndex:1, display:"flex", flexDirection:"column", gap:18 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
          <div style={{ fontSize:9, letterSpacing:"0.2em", color:G.muted,
            textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace" }}>Latest Month Snapshot</div>
          <div style={{ fontSize:11, color:G.dim, fontFamily:"'JetBrains Mono', monospace" }}>
            {hasData ? latest.month : "Awaiting data"}
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:20 }}>
          <SnapshotStat icon="↑" label="Income" 
            value={latest.income ? fmtN(latest.income) : "—"}
            change={incomeChange} color={G.green}/>
          <SnapshotStat icon="↓" label="Expenses" 
            value={latest.expenses ? fmtN(latest.expenses) : "—"}
            color={G.red}/>
          <SnapshotStat icon="=" label="Net Flow" 
            value={hasData ? fmtN(netFlow) : "—"}
            color={netFlow >= 0 ? G.green : G.red}/>
        </div>

        {!hasData && (
          <div style={{ fontSize:11, color:G.dim, lineHeight:1.6, marginTop:4 }}>
            Add your first income and expenses to populate the snapshot and unlock your intelligence score.
          </div>
        )}
      </div>
    </div>
  );
};


// ─── Helper: filter txs to "through selected month" ──────────────────────────
const txsThroughMonth = (txs, monthKey) =>
  !monthKey ? txs : txs.filter(t => t.month <= monthKey);
 
const txsForMonth = (txs, monthKey) =>
  !monthKey ? [] : txs.filter(t => t.month === monthKey);
 
const uniqMonthKeys = (txs) =>
  [...new Set(txs.map(t => t.month))].sort();
 
 
// ─── TimeMachineSlider ───────────────────────────────────────────────────────
const TimeMachineSlider = ({
  monthKeys, selected, onSelect,
  compareEnabled, onCompareToggle,
  compareWith, onCompareChange,
}) => {
  const T = useG();
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(null); // 'main' | 'compare' | null
  const n = monthKeys.length;
 
  // Hooks MUST run on every render — keep them above any early return.
  const handlePointer = useCallback((clientX, which) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const idx = Math.round(ratio * (n - 1));
    const key = monthKeys[idx];
    if (which === 'main') onSelect(key);
    else if (which === 'compare') onCompareChange(key);
  }, [n, monthKeys, onSelect, onCompareChange]);
 
  useEffect(() => {
    if (!dragging) return;
    const move = e => {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      handlePointer(x, dragging);
    };
    const up = () => setDragging(null);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
    };
  }, [dragging, handlePointer]);
 
  if (n === 0) return null;
 
  const selIdx = Math.max(0, monthKeys.indexOf(selected));
  const cmpIdx = compareWith ? Math.max(0, monthKeys.indexOf(compareWith)) : -1;
  const pctOf = i => n === 1 ? 50 : (i / (n - 1)) * 100;
 
  return (
    <div style={{
      background: T.bg2, border: `0.5px solid ${T.border}`, borderRadius: 12,
      padding: "18px 22px 14px", marginBottom: 18,
    }}>
      {/* Header row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 18 }}>
        <div>
          <div style={{
            fontSize: 10, letterSpacing: "0.18em", color: T.goldD,
            textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace",
          }}>Time Machine</div>
          <div style={{
            fontSize: 22, fontWeight: 300, color: T.gold, marginTop: 4,
            fontFamily: "Fraunces, serif", letterSpacing: "0.01em", lineHeight: 1,
          }}>
            {fmtMk(selected)}
            {compareEnabled && compareWith && (
              <span style={{ fontSize: 13, color: T.muted, marginLeft: 10, fontFamily: "'JetBrains Mono', monospace" }}>
                vs {fmtMk(compareWith)}
              </span>
            )}
          </div>
        </div>
 
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => onCompareToggle(!compareEnabled)} style={{
            padding: "6px 12px",
            background: compareEnabled ? `${T.gold}18` : "transparent",
            border: `0.5px solid ${compareEnabled ? T.gold : T.border}`,
            borderRadius: 6,
            color: compareEnabled ? T.gold : T.muted,
            fontSize: 10, letterSpacing: "0.12em", cursor: "pointer",
            fontFamily: "'JetBrains Mono', monospace", fontWeight: 500,
            transition: "all 0.15s",
          }}>
            {compareEnabled ? "● COMPARE" : "○ COMPARE"}
          </button>
          <button onClick={() => onSelect(monthKeys[n - 1])} style={{
            padding: "6px 12px",
            background: "transparent",
            border: `0.5px solid ${T.border}`,
            borderRadius: 6, color: T.muted,
            fontSize: 10, letterSpacing: "0.12em", cursor: "pointer",
            fontFamily: "'JetBrains Mono', monospace",
          }}>NOW</button>
        </div>
      </div>
 
      {/* Track */}
      <div
        ref={trackRef}
        style={{ position: "relative", height: 38, cursor: "pointer", userSelect: "none" }}
        onMouseDown={e => { setDragging('main'); handlePointer(e.clientX, 'main'); }}
        onTouchStart={e => { setDragging('main'); handlePointer(e.touches[0].clientX, 'main'); }}
      >
        {/* Base line */}
        <div style={{
          position: "absolute", left: 0, right: 0, top: "50%",
          height: 1, background: T.border, transform: "translateY(-50%)",
        }}/>
 
        {/* Filled segment up to selected */}
        <div style={{
          position: "absolute", left: 0, top: "50%",
          height: 1, background: T.gold,
          width: `${pctOf(selIdx)}%`,
          transform: "translateY(-50%)", transition: "width 0.15s",
        }}/>
 
        {/* Dots */}
        {monthKeys.map((k, i) => {
          const isSel = i === selIdx;
          const isCmp = i === cmpIdx && compareEnabled;
          return (
            <div key={k} style={{
              position: "absolute", left: `${pctOf(i)}%`, top: "50%",
              transform: "translate(-50%, -50%)",
              width: isSel ? 12 : isCmp ? 10 : 6,
              height: isSel ? 12 : isCmp ? 10 : 6,
              borderRadius: "50%",
              background: isSel ? T.gold : isCmp ? T.blue : T.bg4,
              border: `1px solid ${isSel ? T.gold : isCmp ? T.blue : T.dim}`,
              transition: "all 0.15s",
              boxShadow: isSel ? `0 0 12px ${T.gold}66` : "none",
              pointerEvents: "none",
            }}/>
          );
        })}
 
        {/* Compare drag handle */}
        {compareEnabled && cmpIdx >= 0 && (
          <div
            style={{
              position: "absolute", left: `${pctOf(cmpIdx)}%`, top: "50%",
              transform: "translate(-50%, -50%)",
              width: 22, height: 22, borderRadius: "50%",
              cursor: "grab", zIndex: 2,
            }}
            onMouseDown={e => { e.stopPropagation(); setDragging('compare'); }}
            onTouchStart={e => { e.stopPropagation(); setDragging('compare'); }}
          />
        )}
 
        {/* End labels */}
        <div style={{
          position: "absolute", left: 0, bottom: -2, fontSize: 9,
          color: T.dim, fontFamily: "'JetBrains Mono', monospace",
        }}>{fmtMk(monthKeys[0])}</div>
        <div style={{
          position: "absolute", right: 0, bottom: -2, fontSize: 9,
          color: T.dim, fontFamily: "'JetBrains Mono', monospace",
        }}>{fmtMk(monthKeys[n - 1])}</div>
      </div>
 
      {/* Hint */}
      {n > 1 && (
        <div style={{
          marginTop: 14, fontSize: 10, color: T.dim,
          fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em",
        }}>
          {compareEnabled
            ? "Drag the gold dot to set the primary month, blue dot to set the comparison."
            : `Drag to rewind. Dashboard shows history through ${fmtMk(selected)}.`}
        </div>
      )}
    </div>
  );
};
 
 
// ─── SankeyCashFlow ──────────────────────────────────────────────────────────
//   Two-tier flow: income sources (left) → outflows (right).
//   Outflows = each expense category present + Savings + Investments
//              + "Unaccounted" balancing node if income > outflows.
//   Ribbon thickness = proportional flow allocation.
// ───────────────────────────────────────────────────────────────────────────
const SankeyCashFlow = ({ transactions, height = 420 }) => {
  const T = useG();
  const { fmtN } = useFmt();
  const [hover, setHover] = useState(null);
 
  // ── Aggregate ──
  const incomeAgg = {}, outflowAgg = {};
  const incColor = INCOME_CATS.reduce((m,c)=>{m[c.name]=c.color;return m;},{});
  const expColor = EXPENSE_CATS.reduce((m,c)=>{m[c.name]=c.color;return m;},{});
 
  transactions.forEach(t => {
    if (t.type === "income") {
      incomeAgg[t.category] = (incomeAgg[t.category] || 0) + t.amount;
    } else {
      outflowAgg[t.category] = (outflowAgg[t.category] || 0) + t.amount;
    }
  });
 
  const leftItems = Object.entries(incomeAgg)
    .map(([name, value]) => ({ name, value, color: incColor[name] || T.muted }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);
 
  let rightItems = Object.entries(outflowAgg)
    .map(([name, value]) => ({ name, value, color: expColor[name] || T.muted }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);
 
  const leftTotal = leftItems.reduce((s, d) => s + d.value, 0);
  const rightTotal = rightItems.reduce((s, d) => s + d.value, 0);
 
  // Balancing node — keeps the diagram honest
  if (leftTotal > rightTotal) {
    rightItems.push({
      name: "Unaccounted / Cash",
      value: leftTotal - rightTotal,
      color: T.dim,
    });
  } else if (rightTotal > leftTotal) {
    leftItems.unshift({
      name: "From Reserves",
      value: rightTotal - leftTotal,
      color: T.red,
    });
  }
 
  // Empty state
  if (leftItems.length === 0 && rightItems.length === 0) {
    return (
      <div style={{
        background: T.bg2, border: `0.5px solid ${T.borderGold}`, borderRadius: 12,
        padding: "20px 22px", height,
        display: "flex", flexDirection: "column",
      }}>
        <div style={{ fontSize: 10, letterSpacing: "0.14em", color: T.goldD,
          textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: 14,
        }}>Cash Flow · Sankey</div>
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          color: T.dim, fontSize: 11,
        }}>Add income and expenses to see the flow.</div>
      </div>
    );
  }
 
  // ── Layout ──
  const W = 720;
  const H = height - 60; // reserve for header
  const pT = 12, pB = 16;
  const nodeW = 11;
  const labelGap = 12;
  const leftLabelW = 140, rightLabelW = 160;
  const xL = leftLabelW, xR = W - rightLabelW - nodeW;
  const usable = H - pT - pB;
 
  const sideTotal = Math.max(leftTotal, rightTotal); // equal after balancing
  const gap = 5;
  // Use a single drawArea (sized to the side with more nodes) so ribbons and
  // node heights stay in sync on both ends. The side with fewer nodes gets
  // larger gaps to fill the remaining vertical space.
  const maxCount = Math.max(leftItems.length, rightItems.length, 2);
  const drawArea = usable - (maxCount - 1) * gap;
  const computeNodes = (items, x) => {
    const totalNodeH = items.reduce((s, it) => s + (it.value / sideTotal) * drawArea, 0);
    const sideGap = items.length > 1 ? (usable - totalNodeH) / (items.length - 1) : 0;
    let cursor = pT;
    return items.map(it => {
      const h = (it.value / sideTotal) * drawArea;
      const node = { ...it, x, y: cursor, h };
      cursor += h + sideGap;
      return node;
    });
  };
  const leftNodes = computeNodes(leftItems, xL);
  const rightNodes = computeNodes(rightItems, xR);
 
  // ── Ribbons (proportional allocation) ──
  // Σ over j of flow(i,*) == L_i.value  → ribbon stack matches node height
  // Σ over i of flow(*,j) == R_j.value  → same on the right
  const ribbons = [];
  const leftCursors = leftNodes.map(n => n.y);
  const rightCursors = rightNodes.map(n => n.y);
 
  leftNodes.forEach((L, i) => {
    rightNodes.forEach((R, j) => {
      const flow = (L.value / sideTotal) * R.value;
      const rh = (flow / sideTotal) * drawArea;
      if (rh < 0.4) return; // skip negligible
      const y1 = leftCursors[i];
      const y2 = rightCursors[j];
      ribbons.push({
        i, j, L, R, flow, rh,
        x1: L.x + nodeW, y1, x2: R.x, y2,
        color: L.color,
      });
      leftCursors[i] += rh;
      rightCursors[j] += rh;
    });
  });
 
  const ribbonPath = (r) => {
    const cpX = (r.x1 + r.x2) / 2;
    return `M${r.x1},${r.y1}
            C${cpX},${r.y1} ${cpX},${r.y2} ${r.x2},${r.y2}
            L${r.x2},${r.y2 + r.rh}
            C${cpX},${r.y2 + r.rh} ${cpX},${r.y1 + r.rh} ${r.x1},${r.y1 + r.rh} Z`;
  };
 
  return (
    <div style={{
      background: T.bg2, border: `0.5px solid ${T.borderGold}`, borderRadius: 12,
      padding: "20px 22px 16px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: "0.14em", color: T.goldD,
            textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace",
          }}>Cash Flow · Sankey</div>
          <div style={{ fontSize: 10, color: T.dim, marginTop: 3,
            fontFamily: "'JetBrains Mono', monospace",
          }}>Where every rupee went — sized by amount</div>
        </div>
        <div style={{ display: "flex", gap: 14, fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>
          <span style={{ color: T.green }}>In · {fmtN(leftTotal)}</span>
          <span style={{ color: T.red }}>Out · {fmtN(rightTotal)}</span>
        </div>
      </div>
 
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H }}>
        {/* Ribbons */}
        {ribbons.map((r, idx) => {
          const isHov = hover && hover.i === r.i && hover.j === r.j;
          const anyHov = hover !== null;
          return (
            <path key={idx}
              d={ribbonPath(r)}
              fill={r.color}
              opacity={anyHov ? (isHov ? 0.7 : 0.08) : 0.32}
              style={{ transition: "opacity 0.12s", cursor: "pointer" }}
              onMouseEnter={() => setHover({ i: r.i, j: r.j, flow: r.flow, L: r.L, R: r.R })}
              onMouseLeave={() => setHover(null)}
            />
          );
        })}
 
        {/* Left nodes + labels */}
        {leftNodes.map((n, i) => (
          <g key={`L${i}`}>
            <rect x={n.x} y={n.y} width={nodeW} height={n.h} fill={n.color}
              stroke={T.bg2} strokeWidth={0.5}/>
            <text x={n.x - labelGap} y={n.y + n.h / 2 + 4}
              textAnchor="end" fontSize={11} fill={T.text}
              fontFamily="Geist, sans-serif">
              {n.name}
            </text>
            <text x={n.x - labelGap} y={n.y + n.h / 2 + 18}
              textAnchor="end" fontSize={10} fill={T.muted}
              fontFamily="'JetBrains Mono', monospace">
              {fmtN(n.value)}
            </text>
          </g>
        ))}
 
        {/* Right nodes + labels */}
        {rightNodes.map((n, j) => (
          <g key={`R${j}`}>
            <rect x={n.x} y={n.y} width={nodeW} height={n.h} fill={n.color}
              stroke={T.bg2} strokeWidth={0.5}/>
            <text x={n.x + nodeW + labelGap} y={n.y + n.h / 2 + 4}
              textAnchor="start" fontSize={11} fill={T.text}
              fontFamily="Geist, sans-serif">
              {n.name}
            </text>
            <text x={n.x + nodeW + labelGap} y={n.y + n.h / 2 + 18}
              textAnchor="start" fontSize={10} fill={T.muted}
              fontFamily="'JetBrains Mono', monospace">
              {fmtN(n.value)}
            </text>
          </g>
        ))}
      </svg>
 
      {/* Hover detail */}
      {hover && (
        <div style={{
          marginTop: 8, padding: "8px 12px",
          background: T.bg3, border: `0.5px solid ${T.border}`, borderRadius: 8,
          fontSize: 11, color: T.text, fontFamily: "'JetBrains Mono', monospace",
          display: "flex", justifyContent: "space-between",
        }}>
          <span>
            <span style={{ color: hover.L.color }}>{hover.L.name}</span>
            <span style={{ color: T.muted, margin: "0 8px" }}>→</span>
            <span style={{ color: T.text }}>{hover.R.name}</span>
          </span>
          <span style={{ color: T.gold }}>{fmtN(hover.flow)}</span>
        </div>
      )}
    </div>
  );
};
 
 
// ─── CompareView (mini-dashboard × 2) ────────────────────────────────────────
const CompareView = ({ transactions, monthA, monthB }) => {
  const T = useG();
  const { fmtN } = useFmt();
 
  const buildSide = (monthKey) => {
    const txs = txsForMonth(transactions, monthKey);
    const income = txs.filter(t => t.type === "income").reduce((s,t)=>s+t.amount,0);
    const expenses = txs.filter(t => t.type === "expense" && t.category !== "Savings" && t.category !== "Investments").reduce((s,t)=>s+t.amount,0);
    const savings = txs.filter(t => t.category === "Savings").reduce((s,t)=>s+t.amount,0);
    const investments = txs.filter(t => t.category === "Investments").reduce((s,t)=>s+t.amount,0);
    const pie = computeExpensePie(txs);
    return { monthKey, income, expenses, savings, investments, net: income - expenses - savings - investments, pie, txCount: txs.length };
  };
 
  const A = buildSide(monthA);
  const B = buildSide(monthB);
 
  const Side = ({ data, accent, label }) => (
    <div style={{
      flex: 1, background: T.bg2, border: `0.5px solid ${accent}40`,
      borderRadius: 12, padding: "20px 22px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.14em", color: accent,
          textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace",
        }}>{label}</div>
        <div style={{ fontSize: 18, color: T.text, fontWeight: 300,
          fontFamily: "Fraunces, serif",
        }}>{fmtMk(data.monthKey)}</div>
      </div>
 
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <Stat label="Income" value={fmtN(data.income)} color={T.green}/>
        <Stat label="Expenses" value={fmtN(data.expenses)} color={T.red}/>
        <Stat label="Savings" value={fmtN(data.savings)} color={T.gold}/>
        <Stat label="Net Flow" value={fmtN(data.net)} color={data.net >= 0 ? T.green : T.red}/>
      </div>
 
      {data.pie.length > 0 ? (
        <SVGPieChart data={data.pie} height={130}/>
      ) : (
        <div style={{ height: 130, display: "flex", alignItems: "center", justifyContent: "center",
          color: T.dim, fontSize: 11,
        }}>No expenses this month</div>
      )}
 
      <div style={{ marginTop: 12, fontSize: 10, color: T.dim,
        fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.08em",
      }}>{data.txCount} ENTRIES</div>
    </div>
  );
 
  // Delta strip
  const delta = (a, b) => {
    if (b === 0 && a === 0) return { pct: 0, dir: "flat" };
    if (b === 0) return { pct: 100, dir: "up" };
    const pct = ((a - b) / Math.abs(b)) * 100;
    return { pct: Math.abs(pct).toFixed(1), dir: a > b ? "up" : a < b ? "down" : "flat" };
  };
 
  const deltas = [
    { key: "Income",   d: delta(B.income,   A.income),   goodDir: "up" },
    { key: "Expenses", d: delta(B.expenses, A.expenses), goodDir: "down" },
    { key: "Savings",  d: delta(B.savings,  A.savings),  goodDir: "up" },
    { key: "Net Flow", d: delta(B.net,      A.net),      goodDir: "up" },
  ];
 
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 14 }}>
        <Side data={A} accent={T.gold} label="Primary"/>
        <Side data={B} accent={T.blue} label="Comparison"/>
      </div>
 
      {/* Delta strip */}
      <div style={{
        background: T.bg2, border: `0.5px solid ${T.border}`, borderRadius: 12,
        padding: "14px 22px",
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16,
      }}>
        {deltas.map(({ key, d, goodDir }) => {
          const good = d.dir === goodDir;
          const color = d.dir === "flat" ? T.muted : good ? T.green : T.red;
          return (
            <div key={key}>
              <div style={{ fontSize: 10, letterSpacing: "0.14em", color: T.muted,
                fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase",
              }}>{key} Δ</div>
              <div style={{ fontSize: 18, fontWeight: 300, color,
                fontFamily: "Fraunces, serif", marginTop: 4,
              }}>
                {d.dir === "up" ? "↑" : d.dir === "down" ? "↓" : "="} {d.pct}%
              </div>
              <div style={{ fontSize: 9, color: T.dim, marginTop: 3,
                fontFamily: "'JetBrains Mono', monospace",
              }}>vs primary</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
 
const Stat = ({ label, value, color }) => {
  const T = useG();
  return (
    <div>
      <div style={{ fontSize: 9, letterSpacing: "0.14em", color: T.muted,
        fontFamily: "'JetBrains Mono', monospace", textTransform: "uppercase", marginBottom: 4,
      }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 500, color,
        fontFamily: "'JetBrains Mono', monospace", letterSpacing: "-0.01em",
      }}>{value}</div>
    </div>
  );
};
 
 
// ─── DashboardTimeAware (wrapper) ────────────────────────────────────────────
const DashboardTimeAware = ({ monthly, expenses, scores, transactions }) => {
  const T = useG();
  const allMonthKeys = uniqMonthKeys(transactions);
 
  // Default: latest month
  const latestKey = allMonthKeys[allMonthKeys.length - 1] || thisMonth();
  const priorKey = allMonthKeys[allMonthKeys.length - 2] || latestKey;
 
  const [selectedMonth, setSelectedMonth] = useState(latestKey);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareMonth, setCompareMonth] = useState(priorKey);
 
  // Keep selectedMonth in sync when data first lands
  useEffect(() => {
    if (allMonthKeys.length > 0 && !allMonthKeys.includes(selectedMonth)) {
      setSelectedMonth(allMonthKeys[allMonthKeys.length - 1]);
    }
    if (compareEnabled && allMonthKeys.length > 0 && !allMonthKeys.includes(compareMonth)) {
      setCompareMonth(allMonthKeys[Math.max(0, allMonthKeys.length - 2)]);
    }
  }, [allMonthKeys.join("|")]); // eslint-disable-line
 
  // Filtered/recomputed data for the "through selected" view
  const txsThrough = txsThroughMonth(transactions, selectedMonth);
  const monthlyT = computeMonthly(txsThrough);
  const expensesT = computeExpensePie(
    transactions.filter(t => t.month === selectedMonth) // pies show selected month only
  );
  const scoresT = computeScores(monthlyT);
 
  // Show Sankey for the selected month only (clearest signal)
  const sankeyTxs = transactions.filter(t => t.month === selectedMonth);
 
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Slider always on top */}
      <TimeMachineSlider
        monthKeys={allMonthKeys}
        selected={selectedMonth}
        onSelect={setSelectedMonth}
        compareEnabled={compareEnabled}
        onCompareToggle={setCompareEnabled}
        compareWith={compareMonth}
        onCompareChange={setCompareMonth}
      />
 
      {compareEnabled && allMonthKeys.length >= 2 ? (
        <CompareView
          transactions={transactions}
          monthA={selectedMonth}
          monthB={compareMonth}
        />
      ) : (
        <>
          {/* Existing Dashboard, fed time-shifted data */}
          <Dashboard
            monthly={monthlyT}
            expenses={expensesT}
            scores={scoresT}
            transactions={txsThrough}
          />
 
          {/* Sankey below the existing pies */}
          <div style={{ marginTop: 18 }}>
            <SankeyCashFlow transactions={sankeyTxs}/>
          </div>
        </>
      )}
    </div>
  );
};



// ─── Dashboard View ───────────────────────────────────────────────────────────
const Dashboard = ({ monthly, expenses, scores, transactions }) => {
  const { fmtN } = useFmt();
  const G = useG();

  const strategic = computeStrategicBreakdown(transactions || []);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>

      {/* HERO CARD */}
      <HeroCard monthly={monthly} scores={scores}/>

      {/* CASH FLOW CHART - full width */}
      <div style={{ background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:12, padding:"20px 22px 12px" }}>
        <div style={{ fontSize:10, letterSpacing:"0.14em", color:G.muted, textTransform:"uppercase",
          fontFamily:"'JetBrains Mono', monospace", marginBottom:14 }}>
          {monthly.length}-Month Cash Flow Intelligence
        </div>
        <div style={{ display:"flex", gap:18, marginBottom:10 }}>
          {[[G.blue,"Income"],[G.red,"Expenses"],[G.gold,"Savings"]].map(([c,l]) => (
            <div key={l} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:c }}/>
              <span style={{ fontSize:11, color:G.muted }}>{l}</span>
            </div>
          ))}
        </div>
        {monthly.length > 0 ? (
          <SVGAreaChart data={monthly} height={210} series={[
            { key:"income",   label:"Income",   color:G.blue, fill:true },
            { key:"expenses", label:"Expenses", color:G.red,  fill:false, dashed:true },
            { key:"savings",  label:"Savings",  color:G.gold, fill:true },
          ]}/>
        ) : (
          <div style={{ height:210, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:8 }}>
            <span style={{ fontSize:22, color:G.dim }}>◎</span>
            <span style={{ fontSize:11, color:G.dim }}>Enter income & expenses to see your chart</span>
          </div>
        )}
      </div>

      {/* TWO PIES — Strategic + Category */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>

        {/* Strategic Breakdown */}
        <div style={{ background:G.bg2, border:`0.5px solid ${G.borderGold}`, borderRadius:12, padding:"20px 22px 14px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:14 }}>
            <div style={{ fontSize:10, letterSpacing:"0.14em", color:G.goldD, textTransform:"uppercase",
              fontFamily:"'JetBrains Mono', monospace" }}>Strategic Breakdown</div>
            <div style={{ fontSize:9, color:G.dim, fontFamily:"'JetBrains Mono', monospace" }}>D2D framework</div>
          </div>
          {strategic.length > 0 ? (
            <>
              <SVGPieChart data={strategic} height={140}/>
              <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:8 }}>
                {strategic.map(e => (
                  <div key={e.name} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ width:8, height:8, borderRadius:2, background:e.color }}/>
                      <span style={{ fontSize:11, color:G.muted }}>{e.name}</span>
                    </div>
                    <span style={{ fontSize:11, color:G.text, fontFamily:"'JetBrains Mono', monospace" }}>{fmtN(e.value)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ height:140, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <span style={{ fontSize:11, color:G.dim }}>Add expenses to see breakdown</span>
            </div>
          )}
        </div>

        {/* Category Distribution (existing) */}
        <div style={{ background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:12, padding:"20px 22px 14px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:14 }}>
            <div style={{ fontSize:10, letterSpacing:"0.14em", color:G.muted, textTransform:"uppercase",
              fontFamily:"'JetBrains Mono', monospace" }}>Category Distribution</div>
            <div style={{ fontSize:9, color:G.dim, fontFamily:"'JetBrains Mono', monospace" }}>tactical view</div>
          </div>
          {expenses.length > 0 ? (
            <>
              <SVGPieChart data={expenses} height={140}/>
              <div style={{ display:"flex", flexDirection:"column", gap:6, marginTop:8 }}>
                {expenses.slice(0, 6).map(e => (
                  <div key={e.name} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ width:8, height:8, borderRadius:2, background:e.color }}/>
                      <span style={{ fontSize:11, color:G.muted }}>{e.name}</span>
                    </div>
                    <span style={{ fontSize:11, color:G.text, fontFamily:"'JetBrains Mono', monospace" }}>{fmtN(e.value)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ height:140, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <span style={{ fontSize:11, color:G.dim }}>Add expenses to see breakdown</span>
            </div>
          )}
        </div>
      </div>

      {/* SCORE FACTOR BREAKDOWN */}
      <div style={{ background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:12, padding:"20px 24px" }}>
        <div style={{ fontSize:10, letterSpacing:"0.14em", color:G.muted, textTransform:"uppercase",
          fontFamily:"'JetBrains Mono', monospace", marginBottom:16 }}>Score Factor Breakdown</div>
        {scores.length > 0 ? (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:"14px 32px" }}>
            {scores.map(s => <ScoreBar key={s.label} {...s}/>)}
          </div>
        ) : (
          <div style={{ fontSize:12, color:G.dim, textAlign:"center", padding:"20px 0" }}>
            Scores appear once you enter financial data for at least one month
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Analysis View ─────────────────────────────────────────────────────────────
const Analysis = ({client, monthly, scores, plan}) => {
  const G = useG();
  const { sym } = useFmt();
  const [insights, setInsights] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  if (plan<1) return <UpgradeGate tier={plan} requiredTier={1} feature="AI Behavioral Analysis"/>;
  const run = async () => {
    setAnalyzing(true);setInsights("");
    try {
      const r = await callAI(`You are an elite private wealth behavioral intelligence analyst. Analysis must be surgical, data-anchored, forward-looking. No generic advice. Use ## for section headers, • for bullets. Max 4 sections. Senior private banking partner tone.`, buildAnalysisPrompt(client, monthly, scores, sym));
      setInsights(r);
    } catch(e){setInsights("❌ "+e.message);}
    setAnalyzing(false);
  };
  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{background:G.bg2,border:`0.5px solid ${G.borderGold}`,borderRadius:12,padding:"22px 26px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:18,fontWeight:300,color:G.text,fontFamily:"Fraunces, serif"}}>Behavioral Intelligence Analysis</div>
            <div style={{fontSize:12,color:G.muted,marginTop:4}}>AI-powered pattern recognition across {monthly.length} months of financial behavior</div>
          </div>
          <button onClick={run} disabled={analyzing||monthly.length===0} style={{padding:"10px 22px",background:analyzing||monthly.length===0?G.bg4:G.goldDim,border:`0.5px solid ${G.gold}`,borderRadius:8,color:G.gold,cursor:analyzing||monthly.length===0?"not-allowed":"pointer",fontSize:13,fontFamily:"inherit",fontWeight:500,opacity:analyzing||monthly.length===0?0.5:1}}>
            {analyzing?"◌  Processing...":"⚡  Run AI Analysis"}
          </button>
        </div>
      </div>
      {!insights&&!analyzing&&<div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"52px 28px",textAlign:"center"}}>
        <div style={{fontSize:28,marginBottom:14,color:G.goldD}}>◈</div>
        <div style={{fontSize:14,color:G.muted,marginBottom:6}}>{monthly.length===0?"Enter income & expense data first, then run analysis":"Your behavioral intelligence report is ready to generate"}</div>
      </div>}
      {analyzing&&<div style={{background:G.bg2,border:`0.5px solid ${G.borderGold}`,borderRadius:12,padding:"52px 28px",textAlign:"center"}}>
        <div style={{fontSize:12,color:G.gold,letterSpacing:"0.18em",animation:"pulse 1.5s ease-in-out infinite"}}>PROCESSING BEHAVIORAL PATTERNS</div>
        <div style={{fontSize:12,color:G.muted,marginTop:10}}>Analyzing {monthly.length} months of data...</div>
      </div>}
      {insights&&<div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"24px 28px"}}>
        <div style={{fontSize:10,letterSpacing:"0.14em",color:G.goldD,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:16,paddingBottom:12,borderBottom:`0.5px solid ${G.borderGold}`}}>Intelligence Report · Client · Powered by DeepSeek</div>
        {renderMD(insights, G)}
      </div>}
          </div>
        );
      };
const SVGMultiScenChart = ({ scenarios, height=260 }) => {
  const [tip, setTip] = useState(null);
  const { fmt } = useFmt();
  const G = useG();
  const W=660,H=height,P={top:12,right:12,bottom:28,left:56};
  const iW=W-P.left-P.right,iH=H-P.top-P.bottom;

  const allVals = Object.values(scenarios).flatMap(s=>s.data.map(d=>d.value));
  const maxV = Math.max(...allVals,1)*1.1;
  const years = scenarios.conservative.data.map(d=>d.year);
  const xS = i=>P.left+(i/Math.max(years.length-1,1))*iW;
  const yS = v=>P.top+iH-((v)/maxV)*iH;

  const mkLine = data => data.map((d,i)=>`${i===0?"M":"L"}${xS(i).toFixed(1)},${yS(d.value).toFixed(1)}`).join(" ");
  const mkArea = data => {
    const l=mkLine(data),last=data.length-1;
    return `${l} L${xS(last).toFixed(1)},${(P.top+iH).toFixed(1)} L${P.left.toFixed(1)},${(P.top+iH).toFixed(1)} Z`;
  };

  return (
    <div style={{position:"relative",width:"100%",height:H}}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"100%"}}>
        <defs>
          {Object.entries(scenarios).map(([k,s])=>(
            <linearGradient key={k} id={`mg_${k}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={s.color} stopOpacity={0.12}/>
              <stop offset="95%" stopColor={s.color} stopOpacity={0}/>
            </linearGradient>
          ))}
        </defs>

        {/* Grid lines */}
        {Array.from({length:6},(_,i)=>{
          const v=(maxV*i)/5,y=yS(v);
          return(<g key={i}>
            <line x1={P.left} x2={W-P.right} y1={y} y2={y} stroke={G.border} strokeWidth={0.5}/>
            <text x={P.left-6} y={y+4} textAnchor="end" fontSize={10} fill={G.muted}>{fmt(v)}</text>
          </g>);
        })}

        {/* X axis labels */}
        {years.map((yr,i)=><text key={i} x={xS(i)} y={H-6} textAnchor="middle" fontSize={10} fill={G.muted}>{yr}</text>)}

        {/* Area fills */}
        {Object.entries(scenarios).map(([k,s])=>(
          <path key={`area_${k}`} d={mkArea(s.data)} fill={`url(#mg_${k})`}/>
        ))}

        {/* Lines */}
        {Object.entries(scenarios).map(([k,s])=>(
          <path key={`line_${k}`} d={mkLine(s.data)} fill="none" stroke={s.color} strokeWidth={2}/>
        ))}

        {/* Hover dots */}
        {Object.entries(scenarios).map(([k,s])=>
          s.data.map((d,i)=>(
            <circle key={`${k}_${i}`} cx={xS(i)} cy={yS(d.value)} r={4}
              fill={s.color} stroke={G.bg2} strokeWidth={1.5}
              onMouseEnter={()=>setTip({i,year:d.year,data:scenarios})}
              onMouseLeave={()=>setTip(null)}
              style={{cursor:"pointer"}}/>
          ))
        )}

        {/* Vertical crosshair */}
        {tip&&<line x1={xS(tip.i)} x2={xS(tip.i)} y1={P.top} y2={P.top+iH} stroke={G.muted} strokeWidth={0.5} strokeDasharray="3 3"/>}
      </svg>

      {/* Tooltip */}
      {tip&&(
        <div style={{position:"absolute",left:`${(xS(tip.i)/W)*100}%`,top:16,
          transform:tip.i>years.length*0.6?"translateX(-110%)":"translateX(10px)",
          background:G.bg3,border:`0.5px solid ${G.border}`,borderRadius:10,
          padding:"10px 14px",fontSize:11,pointerEvents:"none",zIndex:10,minWidth:160}}>
          <div style={{color:G.gold,marginBottom:8,fontFamily:"'JetBrains Mono', monospace",fontWeight:500}}>{tip.year}</div>
          {Object.entries(tip.data).map(([k,s])=>(
            <div key={k} style={{display:"flex",justifyContent:"space-between",gap:16,marginBottom:4}}>
              <span style={{color:s.color}}>{s.label}</span>
              <span style={{color:G.text,fontFamily:"'JetBrains Mono', monospace"}}>{fmt(s.data[tip.i].value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
// ─── Scenarios View ────────────────────────────────────────────────────────────
const Scenarios = ({client, monthly, plan}) => {
  const { fmt, sym } = useFmt();
  const G = useG();
  const [multiplier, setMultiplier] = useState(1);
  if (plan<1) return <UpgradeGate tier={plan} requiredTier={1} feature="Scenario Modeling"/>;

  const SCEN = buildScenarios(monthly, multiplier, sym);
  const baseNW = monthly.reduce((t,m)=>t+(m.savings||0)+(m.investments||0),0)+50000;

  // Current monthly contrib for display
  const lastInv = monthly.length>0?(monthly[monthly.length-1].investments||0):0;
  const lastSav = monthly.length>0?(monthly[monthly.length-1].savings||0):0;
  const baseContrib = lastInv + lastSav;
  const adjContrib = Math.round(baseContrib * multiplier);

  const pctLabel = multiplier===1?"Current rate"
    :multiplier>1?`+${Math.round((multiplier-1)*100)}% more`
    :`-${Math.round((1-multiplier)*100)}% less`;

  return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>

      {/* Scenario summary cards */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
        {Object.entries(SCEN).map(([k,v])=>(
          <div key={k} style={{background:G.bg2,border:`0.5px solid ${v.color}40`,borderRadius:12,padding:"18px 20px"}}>
            <div style={{fontSize:12,fontWeight:500,color:v.color,marginBottom:3}}>{v.label}</div>
            <div style={{fontSize:11,color:G.muted,marginBottom:10}}>{v.rate}</div>
            <div style={{fontSize:24,fontWeight:500,color:G.text,fontFamily:"'JetBrains Mono', monospace"}}>{v.end}</div>
            <div style={{fontSize:11,color:G.dim}}>by {new Date().getFullYear()+6} · <span style={{color:v.color}}>{v.growth} growth</span></div>
          </div>
        ))}
      </div>

      {/* Combined chart */}
      <div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"20px 20px 12px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace"}}>
            All Scenarios — {new Date().getFullYear()}–{new Date().getFullYear()+6}
          </div>
          <div style={{display:"flex",gap:16}}>
            {Object.entries(SCEN).map(([k,v])=>(
              <div key={k} style={{display:"flex",alignItems:"center",gap:6}}>
                <div style={{width:24,height:2,background:v.color,borderRadius:1}}/>
                <span style={{fontSize:11,color:G.muted}}>{v.label}</span>
              </div>
            ))}
          </div>
        </div>
        <SVGMultiScenChart scenarios={SCEN} height={260}/>
      </div>

      {/* Savings Rate Slider */}
      <div style={{background:G.bg2,border:`0.5px solid ${G.borderGold}`,borderRadius:12,padding:"20px 24px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.gold,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace"}}>
            Savings Rate Simulator
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:11,color:G.muted}}>Monthly contribution:</span>
            <span style={{fontSize:14,color:G.gold,fontFamily:"'JetBrains Mono', monospace",fontWeight:500}}>
              {sym}{adjContrib.toLocaleString()}
            </span>
            <span style={{padding:"3px 10px",background:multiplier>=1?G.greenDim:G.redDim,
              border:`0.5px solid ${multiplier>=1?G.green:G.red}40`,borderRadius:20,
              fontSize:10,color:multiplier>=1?G.green:G.red,fontFamily:"'JetBrains Mono', monospace"}}>
              {pctLabel}
            </span>
          </div>
        </div>
        <div style={{fontSize:11,color:G.dim,marginBottom:16}}>
          Drag to see how saving more or less each month changes your 7-year outcome
        </div>
        <input
          type="range" min={0.5} max={2} step={0.05} value={multiplier}
          onChange={e=>setMultiplier(parseFloat(e.target.value))}
          style={{width:"100%",accentColor:G.gold,cursor:"pointer",height:4}}
        />
        <div style={{display:"flex",justifyContent:"space-between",marginTop:6,fontSize:10,color:G.dim,fontFamily:"'JetBrains Mono', monospace"}}>
          <span>−50%</span>
          <span>Current</span>
          <span>+100%</span>
        </div>

        {/* Impact summary */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginTop:16}}>
          {Object.entries(SCEN).map(([k,v])=>{
            const base = buildScenarios(monthly,1,sym)[k].data[6].value;
            const adj = v.data[6].value;
            const diff = adj-base;
            const isPos = diff>=0;
            return(
              <div key={k} style={{background:G.bg3,borderRadius:10,padding:"12px 14px"}}>
                <div style={{fontSize:11,color:v.color,marginBottom:4}}>{v.label}</div>
                <div style={{fontSize:16,color:G.text,fontFamily:"'JetBrains Mono', monospace",fontWeight:500}}>{fmt(adj)}</div>
                {multiplier!==1&&<div style={{fontSize:11,color:isPos?G.green:G.red,marginTop:3}}>
                  {isPos?"+":""}{fmt(diff)} vs baseline
                </div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* KPI row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12}}>
        {[
          {label:"Current Net Worth", value:fmt(baseNW)},
          {label:`${new Date().getFullYear()+2} Milestone`, value:fmt(SCEN.moderate.data[2]?.value||0)},
          {label:`${new Date().getFullYear()+4} Projection`, value:fmt(SCEN.moderate.data[4]?.value||0)},
          {label:"7-Year Target", value:SCEN.moderate.end},
        ].map(m=><KPI key={m.label} label={m.label} value={m.value}/>)}
      </div>

    </div>
  );
};

// ─── Assistant View ────────────────────────────────────────────────────────────
const Assistant = ({client, monthly, scores, plan}) => {
  const G = useG();
  const { sym } = useFmt();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  useEffect(()=>{
    if (plan<2) return;
    setMessages([{role:"assistant",content:`Good ${tod()}, ${client.name}. I have full context of your financial profile with ${monthly.length} months of data.\n\n${scores.length>0?`Your Behavioral Intelligence Score is ${(scores.reduce((s,sc)=>s+sc.score,0)/scores.length).toFixed(1)}/100. `:""} I'm ready to discuss your financial patterns, projections, and optimization opportunities.\n\nWhat would you like to explore?`}]);
  },[plan]);
  useEffect(()=>{if(ref.current)ref.current.scrollTop=ref.current.scrollHeight;},[messages,loading]);
  if (plan<2) return <UpgradeGate tier={plan} requiredTier={2} feature="AI Wealth Assistant"/>;
  const send = async () => {
    if(!input.trim()||loading) return;
    const msg=input.trim();setInput("");
    const newMsgs=[...messages,{role:"user",content:msg}];
    setMessages(newMsgs);setLoading(true);
    try{const r=await callAI(buildChatCtx(client,monthly,scores,sym),msg,newMsgs.slice(0,-1).map(m=>({role:m.role,content:m.content})));setMessages(p=>[...p,{role:"assistant",content:r}]);}
    catch(e){setMessages(p=>[...p,{role:"assistant",content:"❌ "+e.message}]);}
    setLoading(false);
  };
  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 160px)"}}>
      <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:16,paddingBottom:16}} ref={ref}>
        {messages.map((m,i)=>(
          <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start",flexDirection:m.role==="user"?"row-reverse":"row",animation:"fadeIn 0.3s ease"}}>
            <div style={{width:34,height:34,borderRadius:"50%",flexShrink:0,background:m.role==="assistant"?G.goldDim:G.bg4,border:`0.5px solid ${m.role==="assistant"?G.gold:G.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:m.role==="assistant"?G.gold:G.muted,fontFamily:"'JetBrains Mono', monospace"}}>{m.role==="assistant"?"AI":"YOU"}</div>
            <div style={{maxWidth:"78%",padding:"13px 17px",background:m.role==="user"?G.bg4:G.bg2,border:`0.5px solid ${m.role==="user"?G.border:G.borderGold}`,borderRadius:m.role==="user"?"12px 4px 12px 12px":"4px 12px 12px 12px",fontSize:13,color:G.text,lineHeight:1.65,whiteSpace:"pre-wrap"}}>{m.content}</div>
          </div>
        ))}
        {loading&&<div style={{display:"flex",gap:12,alignItems:"center"}}><div style={{width:34,height:34,borderRadius:"50%",background:G.goldDim,border:`0.5px solid ${G.gold}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:G.gold,fontFamily:"'JetBrains Mono', monospace"}}>AI</div><div style={{fontSize:12,color:G.muted,animation:"pulse 1.2s ease-in-out infinite"}}>Analyzing your portfolio...</div></div>}
      </div>
      <div>
        <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
          {["Analyze my spending behavior","What's my savings trend?","How do I optimize investments?","Biggest financial risks now"].map(p=><button key={p} onClick={()=>setInput(p)} style={{padding:"5px 12px",background:G.bg3,border:`0.5px solid ${G.border}`,borderRadius:20,fontSize:11,color:G.muted,cursor:"pointer",fontFamily:"inherit"}}>{p}</button>)}
        </div>
        <div style={{display:"flex",gap:10,padding:"14px 18px",background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,alignItems:"center"}}>
          <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&send()} placeholder="Ask about your financial patterns, projections, strategy..." style={{flex:1,background:"transparent",border:"none",outline:"none",color:G.text,fontSize:13,fontFamily:"inherit"}}/>
          <button onClick={send} disabled={loading||!input.trim()} style={{padding:"8px 20px",background:G.goldDim,border:`0.5px solid ${G.gold}`,borderRadius:7,color:G.gold,cursor:loading||!input.trim()?"not-allowed":"pointer",fontSize:13,fontFamily:"inherit",opacity:loading||!input.trim()?0.45:1}}>Send ↵</button>
        </div>
      </div>
    </div>
  );
};

// ─── Money Personality ────────────────────────────────────────────────────────
const ARCHETYPES = {
  guardian: {
    name: "The Guardian",
    emoji: "🛡️",
    tagline: "Security above all else",
    color: "blue",
    desc: "You prioritise financial safety and stability. You keep reserves, avoid unnecessary risk, and sleep soundly knowing your bases are covered. Your superpower is discipline — your shadow side is missed growth opportunities from being overly cautious.",
    strengths: ["Strong emergency fund instincts","Low impulse spending","Consistent saving habits"],
    blindspots: ["Under-invests — cash sitting idle losing to inflation","May stay in safe but underpaying job too long","Struggles to spend on experiences or self"],
    advice: "Your financial foundation is solid. The next level is deploying your security buffer into low-risk investments. Even moving 20% of idle savings into index funds would meaningfully compound over 10 years without sacrificing safety.",
    celebrities: "Warren Buffett · Suze Orman",
  },
  achiever: {
    name: "The Achiever",
    emoji: "🚀",
    tagline: "Money is how I keep score",
    color: "gold",
    desc: "You are ambitious, income-focused, and driven. You spend on quality and status — not out of shallowness but because you associate money with success and self-worth. You earn well and spend well. The risk: your expenses scale with income, so wealth accumulation lags behind earnings.",
    strengths: ["High earning potential","Invests in self and career","Motivated by financial goals"],
    blindspots: ["Lifestyle inflation erodes wealth gains","Emotional spending tied to stress or success","Portfolio often too concentrated in career income"],
    advice: "Your biggest lever is automating a fixed percentage to investments before lifestyle spending has a chance to absorb your raises. Every income increase — automate 50% of it before you ever see it.",
    celebrities: "Gary Vee · Oprah Winfrey",
  },
  explorer: {
    name: "The Explorer",
    emoji: "🌍",
    tagline: "Experiences over everything",
    color: "green",
    desc: "You value freedom, experiences, and living fully now over optimising for a distant future. You spend on travel, food, culture, and people. You resist rigid budgets as they feel like cages. The risk: you can reach 40 with great memories but a thin financial cushion.",
    strengths: ["Rich life and strong relationships","Low materialism — spends on meaning","Adaptable with money"],
    blindspots: ["Under-saved for emergencies","Inconsistent investment habits","Avoids thinking about retirement"],
    advice: "You don't need to become a spreadsheet person. Just set one automatic rule: every month, move a fixed amount to investments before you can spend it. Even $300/month at 8% is $540,000 over 30 years. Fund the future, then live freely.",
    celebrities: "Tim Ferriss · Marie Kondo",
  },
  architect: {
    name: "The Architect",
    emoji: "📐",
    tagline: "Every dollar has a job",
    color: "purple",
    desc: "You think systemically about money. You like plans, structures, and optimisation. You probably track things carefully and enjoy the puzzle of financial efficiency. You are the most likely of any type to build real long-term wealth — your risk is analysis paralysis and missing life while optimising it.",
    strengths: ["Excellent long-term planning","Low financial anxiety — always has a plan","Strong investment discipline"],
    blindspots: ["Can over-optimise at the cost of enjoyment","May be too rigid when life disrupts the plan","Sometimes misses high-return opportunities from excessive caution"],
    advice: "Your system is your strength. Make sure your plan has a 'guilt-free spending' line built in — a monthly allowance with zero tracking. Permission to enjoy the wealth you're building is part of the plan.",
    celebrities: "Ramit Sethi · Charlie Munger",
  },
};

function derivePersonality(transactions, monthly) {
  if (transactions.length < 3) return null;
  const expTxs = transactions.filter(t=>t.type==="expense");
  const totals = {};
  expTxs.forEach(t=>{ totals[t.category]=(totals[t.category]||0)+t.amount; });
  const totalExp = Object.values(totals).reduce((s,v)=>s+v,0)||1;
  const pct = k => (totals[k]||0)/totalExp;
  const n = monthly.length||1;
  const avgInc = monthly.reduce((s,m)=>s+m.income,0)/n;
  const avgSav = monthly.reduce((s,m)=>s+m.savings,0)/n;
  const avgInv = monthly.reduce((s,m)=>s+m.investments,0)/n;
  const savRate = avgInc>0?(avgSav+avgInv)/avgInc:0;
  const expRate = avgInc>0?(monthly.reduce((s,m)=>s+m.expenses,0)/n)/avgInc:0;

  // Score each archetype
  const scores = {
    guardian: 0, achiever: 0, explorer: 0, architect: 0,
  };
  // Guardian: high savings, low entertainment/shopping, high insurance/housing
  scores.guardian += savRate > 0.20 ? 30 : savRate > 0.10 ? 15 : 0;
  scores.guardian += pct("Insurance") > 0.05 ? 15 : 0;
  scores.guardian += pct("Shopping") < 0.08 ? 10 : 0;
  scores.guardian += pct("Entertainment") < 0.06 ? 10 : 0;
  scores.guardian += pct("Savings") > 0.12 ? 15 : 0;

  // Achiever: high income variation, high shopping/subscriptions, high investments
  scores.achiever += pct("Shopping") > 0.12 ? 20 : pct("Shopping") > 0.07 ? 10 : 0;
  scores.achiever += pct("Investments") > 0.10 ? 20 : pct("Investments") > 0.05 ? 10 : 0;
  scores.achiever += pct("Subscriptions") > 0.06 ? 15 : 0;
  scores.achiever += expRate > 0.65 ? 20 : 0;
  scores.achiever += avgInc > 5000 ? 15 : 0;

  // Explorer: high travel/entertainment/dining, low savings, low insurance
  scores.explorer += pct("Travel") > 0.08 ? 25 : pct("Travel") > 0.04 ? 12 : 0;
  scores.explorer += pct("Entertainment") > 0.10 ? 20 : pct("Entertainment") > 0.05 ? 10 : 0;
  scores.explorer += pct("Food & Dining") > 0.18 ? 20 : pct("Food & Dining") > 0.10 ? 10 : 0;
  scores.explorer += savRate < 0.10 ? 15 : 0;
  scores.explorer += pct("Education") > 0.05 ? 10 : 0;

  // Architect: balanced across all, high investments, lower entertainment
  scores.architect += pct("Investments") > 0.08 ? 20 : 0;
  scores.architect += savRate > 0.15 ? 15 : 0;
  scores.architect += pct("Entertainment") < 0.08 && pct("Shopping") < 0.10 ? 20 : 0;
  scores.architect += transactions.length > 10 ? 15 : 0; // tracks carefully
  scores.architect += expRate < 0.55 ? 20 : 0;

  const best = Object.entries(scores).sort((a,b)=>b[1]-a[1])[0][0];
  const total = Object.values(scores).reduce((s,v)=>s+v,0)||1;
  const confidence = Math.round((scores[best]/total)*100);
  return { type: best, confidence, scores, archetype: ARCHETYPES[best] };
}


const MoneyPersonality = ({ client, transactions, monthly }) => {
  const G = useG();
  const { fmtN } = useFmt();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiInsight, setAiInsight] = useState("");
  const [ran, setRan] = useState(false);

  useEffect(()=>{
    if (!ran && transactions.length >= 3) {
      setRan(true);
      const r = derivePersonality(transactions, monthly);
      setResult(r);
    }
  }, [transactions, monthly, ran]);

  const getAiInsight = async () => {
    if (!result) return;
    setLoading(true);
    const expTotals = {};
    transactions.filter(t=>t.type==="expense").forEach(t=>{ expTotals[t.category]=(expTotals[t.category]||0)+t.amount; });
    const topCats = Object.entries(expTotals).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([k,v])=>`${k}: ${fmtN(v)}`).join(", ");
    try {
      const insight = await callAI(
        `You are a financial psychologist and wealth advisor. Analyse this client's money personality and give a sharp, personalised 3-paragraph insight. Be specific, empathetic, and actionable. No generic advice. Reference their actual spending patterns.`,
        `Client: ${client.name} | Money Personality: ${result.archetype.name} (${result.confidence}% match)
Top spending categories: ${topCats}
${monthly.length} months of data | Archetype traits: ${result.archetype.tagline}
Write: 1) What their spending pattern reveals about their relationship with money. 2) Their single biggest financial blind spot right now. 3) One specific, high-leverage action they can take this month.`
      );
      setAiInsight(insight);
    } catch(e) { setAiInsight("Unable to load insight. Please try again."); }
    setLoading(false);
  };

  const accentColors = {
    blue: {main: G.blue, dim: G.blueDim},
    gold: {main: G.gold, dim: G.goldDim},
    green: {main: G.green, dim: G.greenDim},
    purple: {main: G.purple, dim: G.purpleDim},
  };

  if (transactions.length < 3) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60vh",gap:16,textAlign:"center"}}>
      <div style={{fontSize:40}}>◉</div>
      <div style={{fontSize:20,fontWeight:300,color:G.gold,fontFamily:"Fraunces, serif"}}>Money Personality</div>
      <div style={{fontSize:13,color:G.muted,maxWidth:380}}>Add at least 3 expense transactions to unlock your money personality profile. The more data you add, the more accurate the analysis.</div>
    </div>
  );

  if (!result) return <div style={{color:G.muted,fontSize:13}}>Analysing your patterns…</div>;

  const ac = accentColors[result.archetype.color] || accentColors.gold;
  const allTypes = Object.entries(ARCHETYPES);

  return (
    <div style={{animation:"fadeIn 0.3s ease"}}>
      {/* Hero card */}
      <div style={{background:G.bg2,border:`1.5px solid ${ac.main}40`,borderRadius:14,padding:"28px 32px",marginBottom:20,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,right:0,width:200,height:200,background:`radial-gradient(circle at top right, ${ac.main}15 0%, transparent 70%)`,pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"flex-start",gap:20,flexWrap:"wrap"}}>
          <div style={{fontSize:52,lineHeight:1}}>{result.archetype.emoji}</div>
          <div style={{flex:1,minWidth:200}}>
            <div style={{fontSize:10,letterSpacing:"0.18em",color:ac.main,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:6}}>Your Money Personality</div>
            <div style={{fontSize:28,fontWeight:300,color:G.text,fontFamily:"Fraunces, serif",marginBottom:4}}>{result.archetype.name}</div>
            <div style={{fontSize:14,color:G.muted,fontStyle:"italic",marginBottom:14}}>{result.archetype.tagline}</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <div style={{padding:"4px 14px",background:ac.dim,border:`0.5px solid ${ac.main}40`,borderRadius:20,fontSize:11,color:ac.main,fontFamily:"'JetBrains Mono', monospace"}}>{result.confidence}% match</div>
              <div style={{padding:"4px 14px",background:G.bg3,border:`0.5px solid ${G.border}`,borderRadius:20,fontSize:11,color:G.muted}}>{transactions.length} transactions analysed</div>
            </div>
          </div>
        </div>
        <div style={{marginTop:20,fontSize:13,color:G.muted,lineHeight:1.7,maxWidth:680}}>{result.archetype.desc}</div>
      </div>

      {/* Two column: strengths + blindspots */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
        <div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"18px 20px"}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.green,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:14}}>Strengths</div>
          {result.archetype.strengths.map((s,i)=>(
            <div key={i} style={{display:"flex",gap:10,marginBottom:10,alignItems:"flex-start"}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:G.green,marginTop:5,flexShrink:0}}/>
              <div style={{fontSize:13,color:G.text,lineHeight:1.5}}>{s}</div>
            </div>
          ))}
        </div>
        <div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"18px 20px"}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.red,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:14}}>Blind spots</div>
          {result.archetype.blindspots.map((s,i)=>(
            <div key={i} style={{display:"flex",gap:10,marginBottom:10,alignItems:"flex-start"}}>
              <div style={{width:6,height:6,borderRadius:"50%",background:G.red,marginTop:5,flexShrink:0}}/>
              <div style={{fontSize:13,color:G.text,lineHeight:1.5}}>{s}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Advice card */}
      <div style={{background:ac.dim,border:`0.5px solid ${ac.main}30`,borderRadius:12,padding:"18px 22px",marginBottom:14}}>
        <div style={{fontSize:10,letterSpacing:"0.14em",color:ac.main,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:10}}>High-leverage advice for your type</div>
        <div style={{fontSize:13,color:G.text,lineHeight:1.7}}>{result.archetype.advice}</div>
        <div style={{marginTop:10,fontSize:11,color:G.muted}}>Inspired by: {result.archetype.celebrities}</div>
      </div>

      {/* Archetype score breakdown */}
      <div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"18px 22px",marginBottom:14}}>
        <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:16}}>Your personality mix</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12}}>
          {allTypes.map(([key,arch])=>{
            const sc = result.scores[key]||0;
            const max = Math.max(...Object.values(result.scores))||1;
            const pct = Math.round((sc/max)*100);
            const aColors = accentColors[arch.color]||accentColors.gold;
            const isMain = key===result.type;
            return (
              <div key={key} style={{background:isMain?aColors.dim:G.bg3,border:`0.5px solid ${isMain?aColors.main:G.border}`,borderRadius:10,padding:"12px 14px"}}>
                <div style={{fontSize:20,marginBottom:6}}>{arch.emoji}</div>
                <div style={{fontSize:12,fontWeight:500,color:isMain?aColors.main:G.text,marginBottom:2}}>{arch.name}</div>
                <div style={{fontSize:10,color:G.muted,marginBottom:8}}>{arch.tagline}</div>
                <div style={{background:G.bg4,borderRadius:2,height:3}}>
                  <div style={{width:`${pct}%`,height:"100%",background:aColors.main,borderRadius:2,transition:"width 1s ease"}}/>
                </div>
                <div style={{fontSize:10,color:G.dim,marginTop:4,fontFamily:"'JetBrains Mono', monospace"}}>{pct}%</div>
                
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Deep Insight */}
      <div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"20px 24px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:aiInsight?16:0}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.goldD,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace"}}>AI Psychological Deep-Dive</div>
          {!aiInsight&&<button onClick={getAiInsight} disabled={loading} style={{padding:"8px 18px",background:G.goldDim,border:`0.5px solid ${G.gold}`,borderRadius:8,color:G.gold,fontSize:12,cursor:loading?"wait":"pointer",fontFamily:"inherit"}}>{loading?"Analysing…":"Generate Insight →"}</button>}
        </div>
        {aiInsight&&<div style={{fontSize:13,color:G.muted,lineHeight:1.75}}>{renderMD(aiInsight,G)}</div>}
      </div>
      {result && (
        <div style={{ marginTop: 14 }}>
          <PersonalityShareCard
            archetype={{ ...result.archetype, type: result.type }}
            confidence={result.confidence}
            scores={result.scores}
            clientName={client?.name}/>
        </div>
      )}
    </div>
  );
};

// ─── Regret Tracker ───────────────────────────────────────────────────────────
const REGRET_THRESHOLD = 100; // ask for regret on purchases above this
const REGRET_WINDOW_DAYS = 7;

const REGRET_OPTIONS = [
  { value: 0, label: "Zero regret", emoji: "😊", color: "#4EAD8A" },
  { value: 1, label: "Slight regret", emoji: "🤔", color: "#C9A84C" },
  { value: 2, label: "Moderate regret", emoji: "😕", color: "#E8894A" },
  { value: 3, label: "Strong regret", emoji: "😞", color: "#C05A5A" },
];

const RegretTracker = ({ transactions, onUpdate }) => {
  const G = useG();
  const { fmtN } = useFmt();

  const now = Date.now();
  const windowMs = REGRET_WINDOW_DAYS * 24 * 60 * 60 * 1000;

  // Pending: expense > threshold, within 7 days, no regret logged yet
  const pending = transactions.filter(t =>
    t.type === "expense" &&
    t.amount >= REGRET_THRESHOLD &&
    t.regret === undefined &&
    t.ts && (now - t.ts) >= 0 && (now - t.ts) <= windowMs * 4 // show up to 28 days for demo
  );

  // Rated: has regret value
  const rated = transactions.filter(t => t.type === "expense" && t.regret !== undefined)
    .sort((a,b) => (b.ts||0) - (a.ts||0));

  // Category regret analysis
  const catRegret = {};
  const catCount = {};
  rated.forEach(t => {
    if (!catRegret[t.category]) { catRegret[t.category]=0; catCount[t.category]=0; }
    catRegret[t.category] += t.regret;
    catCount[t.category]++;
  });
  const catStats = Object.entries(catRegret).map(([cat,total])=>({
    cat,
    avg: total/catCount[cat],
    count: catCount[cat],
    total,
  })).sort((a,b)=>b.avg-a.avg);

  // Overall regret score (0-100, lower is better)
  const totalRated = rated.length;
  const avgRegret = totalRated > 0 ? rated.reduce((s,t)=>s+t.regret,0)/totalRated : null;
  const regretScore = avgRegret !== null ? Math.round((1 - avgRegret/3)*100) : null;

  const regretColor = regretScore === null ? G.muted : regretScore >= 75 ? G.green : regretScore >= 50 ? G.gold : G.red;

  const highestRegretCat = catStats[0];
  const lowestRegretCat = catStats.filter(c=>c.avg<1)[0];

  return (
    <div style={{animation:"fadeIn 0.3s ease"}}>

      {/* Score header */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:14,marginBottom:20}}>
        <div style={{background:G.bg2,border:`0.5px solid ${G.borderGold}`,borderRadius:12,padding:"18px 22px"}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>Purchase Satisfaction Score</div>
          <div style={{fontSize:36,fontWeight:500,color:regretColor,fontFamily:"'JetBrains Mono', monospace"}}>{regretScore !== null ? regretScore : "—"}</div>
          <div style={{fontSize:11,color:G.muted,marginTop:4}}>{regretScore===null?"Rate purchases to unlock":regretScore>=75?"You spend with intention 👏":regretScore>=50?"Some patterns to address":"High regret spending detected"}</div>
        </div>
        <div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"18px 22px"}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>Awaiting Your Rating</div>
          <div style={{fontSize:36,fontWeight:500,color:pending.length>0?G.orange:G.muted,fontFamily:"'JetBrains Mono', monospace"}}>{pending.length}</div>
          <div style={{fontSize:11,color:G.muted,marginTop:4}}>purchases over {fmtN(REGRET_THRESHOLD)}</div>
        </div>
        <div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"18px 22px"}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>Rated Purchases</div>
          <div style={{fontSize:36,fontWeight:500,color:G.text,fontFamily:"'JetBrains Mono', monospace"}}>{totalRated}</div>
          <div style={{fontSize:11,color:G.muted,marginTop:4}}>avg regret: {avgRegret !== null ? avgRegret.toFixed(1) : "—"}/3.0</div>
        </div>
      </div>

      {/* Pending ratings */}
      {pending.length > 0 && (
        <div style={{background:G.bg2,border:`1.5px solid ${G.orange}40`,borderRadius:12,padding:"20px 24px",marginBottom:18}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.orange,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:4}}>Rate These Purchases</div>
          <div style={{fontSize:12,color:G.muted,marginBottom:18}}>How do you feel about these purchases now that some time has passed?</div>
          {pending.map(tx=>(
            <div key={tx.id} style={{background:G.bg3,borderRadius:10,padding:"14px 18px",marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontSize:13,fontWeight:500,color:G.text}}>{tx.note||tx.category}</div>
                  <div style={{fontSize:11,color:G.muted,marginTop:2,fontFamily:"'JetBrains Mono', monospace"}}>{tx.category} · {fmtN(tx.amount)} · {tx.ts ? new Date(tx.ts).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : tx.month}</div>
                </div>
                <div style={{fontSize:11,color:G.dim,fontFamily:"'JetBrains Mono', monospace"}}>
                  {tx.ts ? `${Math.floor((now-tx.ts)/(1000*60*60*24))}d ago` : ""}
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {REGRET_OPTIONS.map(opt=>(
                  <button key={opt.value} onClick={()=>onUpdate(tx.id,{regret:opt.value})}
                    style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"transparent",border:`0.5px solid ${G.border}`,borderRadius:20,color:G.muted,fontSize:12,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor=opt.color;e.currentTarget.style.color=opt.color;e.currentTarget.style.background=opt.color+"18";}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor=G.border;e.currentTarget.style.color=G.muted;e.currentTarget.style.background="transparent";}}>
                    <span style={{fontSize:14}}>{opt.emoji}</span>
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Category regret heatmap */}
      {catStats.length > 0 && (
        <div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"20px 24px",marginBottom:14}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:6}}>Regret by Category</div>
          <div style={{fontSize:12,color:G.dim,marginBottom:18}}>Lower avg = spending you feel good about. Higher avg = patterns worth examining.</div>
          {catStats.map(({cat,avg,count})=>{
            const pct = (avg/3)*100;
            const barColor = avg<1?G.green:avg<2?G.gold:avg<2.5?G.orange:G.red;
            const emoji = avg<1?"😊":avg<2?"🤔":avg<2.5?"😕":"😞";
            return (
              <div key={cat} style={{marginBottom:14}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:13}}>{emoji}</span>
                    <span style={{fontSize:13,color:G.text}}>{cat}</span>
                    <span style={{fontSize:10,color:G.dim,fontFamily:"'JetBrains Mono', monospace"}}>{count} rated</span>
                  </div>
                  <div style={{fontSize:12,color:barColor,fontFamily:"'JetBrains Mono', monospace"}}>{avg.toFixed(1)}/3.0</div>
                </div>
                <div style={{background:G.bg4,borderRadius:3,height:5}}>
                  <div style={{width:`${pct}%`,height:"100%",background:barColor,borderRadius:3,transition:"width 0.8s ease"}}/>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Insight card */}
      {catStats.length > 0 && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:14}}>
          {highestRegretCat && highestRegretCat.avg > 1 && (
            <div style={{background:G.redDim,border:`0.5px solid ${G.redD}40`,borderRadius:12,padding:"16px 20px"}}>
              <div style={{fontSize:10,letterSpacing:"0.14em",color:G.red,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>Highest regret category</div>
              <div style={{fontSize:18,fontWeight:500,color:G.text,marginBottom:4}}>{highestRegretCat.cat}</div>
              <div style={{fontSize:12,color:G.muted}}>You average {highestRegretCat.avg.toFixed(1)}/3 regret here. Before the next {highestRegretCat.cat.toLowerCase()} purchase, wait 48 hours.</div>
            </div>
          )}
          {lowestRegretCat && (
            <div style={{background:G.greenDim,border:`0.5px solid ${G.greenD}40`,borderRadius:12,padding:"16px 20px"}}>
              <div style={{fontSize:10,letterSpacing:"0.14em",color:G.green,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:8}}>Best value category</div>
              <div style={{fontSize:18,fontWeight:500,color:G.text,marginBottom:4}}>{lowestRegretCat.cat}</div>
              <div style={{fontSize:12,color:G.muted}}>Near-zero regret here. This spending aligns with your values — keep it in your budget guilt-free.</div>
            </div>
          )}
        </div>
      )}

      {/* Rated history */}
      {rated.length > 0 && (
        <div style={{background:G.bg2,border:`0.5px solid ${G.border}`,borderRadius:12,padding:"20px 24px"}}>
          <div style={{fontSize:10,letterSpacing:"0.14em",color:G.muted,textTransform:"uppercase",fontFamily:"'JetBrains Mono', monospace",marginBottom:16}}>Rated History</div>
          {rated.slice(0,12).map(tx=>{
            const opt = REGRET_OPTIONS[tx.regret]||REGRET_OPTIONS[0];
            return (
              <div key={tx.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:`0.5px solid ${G.border}`}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:16}}>{opt.emoji}</span>
                  <div>
                    <div style={{fontSize:13,color:G.text}}>{tx.note||tx.category}</div>
                    <div style={{fontSize:10,color:G.muted,fontFamily:"'JetBrains Mono', monospace"}}>{tx.category} · {tx.ts?new Date(tx.ts).toLocaleDateString("en-US",{month:"short",day:"numeric"}):tx.month}</div>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{fontSize:12,color:G.muted,fontFamily:"'JetBrains Mono', monospace"}}>{fmtN(tx.amount)}</div>
                  <div style={{padding:"2px 10px",background:opt.color+"22",border:`0.5px solid ${opt.color}50`,borderRadius:20,fontSize:10,color:opt.color}}>{opt.label}</div>
                  <button onClick={()=>onUpdate(tx.id,{regret:undefined})} style={{background:"none",border:"none",color:G.dim,cursor:"pointer",fontSize:12,padding:"2px 6px"}} title="Clear rating">✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {rated.length===0 && pending.length===0 && (
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"40vh",gap:14,textAlign:"center"}}>
          <div style={{fontSize:40}}>◑</div>
          <div style={{fontSize:18,fontWeight:300,color:G.gold,fontFamily:"Fraunces, serif"}}>No purchases to rate yet</div>
          <div style={{fontSize:13,color:G.muted,maxWidth:380}}>Add expense transactions over {fmtN(REGRET_THRESHOLD)} and come back in a day or two. You'll be asked to rate how you feel about each one — revealing which spending truly brings you value.</div>
        </div>
      )}
    </div>
  );
};

// ─── WhatsApp Logger ──────────────────────────────────────────────────────────
const WA_PARSE_SYSTEM = `You are an expense parser for a personal finance app. The user sends natural language messages describing income or expenses.

Extract and return ONLY a valid JSON object — no markdown, no explanation — with these exact fields:
{
  "type": "income" | "expense",
  "amount": number,
  "category": string,
  "note": string,
  "month": "YYYY-MM"
}

Rules:
- "type": income only if user says salary, received, earned, got paid, freelance income, dividend, rental income. Otherwise expense.
- "amount": numeric only, no currency symbols. If ambiguous, use 0.
- "category": must be exactly one of these expense categories: Housing, Food & Dining, Transport, Healthcare, Shopping, Entertainment, Utilities, Insurance, Subscriptions, Travel, Education, Investments, Savings, Other
  Or for income: Salary, Business, Rental, Dividends, Freelance, Capital Gains, Other Income
- "note": a clean short description of what the expense/income was for
- "month": infer from message. "this morning/today/yesterday" = current month. "last month" = previous month. If no clue, use current month. Format: YYYY-MM

Examples:
"₹340 dinner at Bandra" → {"type":"expense","amount":340,"category":"Food & Dining","note":"dinner at Bandra","month":"2026-05"}
"paid Netflix 649" → {"type":"expense","amount":649,"category":"Subscriptions","note":"Netflix subscription","month":"2026-05"}
"got salary 85000" → {"type":"income","amount":85000,"category":"Salary","note":"Monthly salary","month":"2026-05"}
"uber 180 to airport" → {"type":"expense","amount":180,"category":"Transport","note":"Uber to airport","month":"2026-05"}
"grocery run 2400" → {"type":"expense","amount":2400,"category":"Food & Dining","note":"Groceries","month":"2026-05"}`;

const CAT_ICONS = [...INCOME_CATS, ...EXPENSE_CATS].reduce((m,c)=>{m[c.name]=c;return m;},{});


// ─── WhatsApp Phone Link Component ───────────────────────────────────────────
const WhatsAppPhoneLink = ({ clientId, theme: T }) => {
  const [phone, setPhone] = useState(() => (localStorage.getItem("pa_wa_phone") || "").replace(/^whatsapp:/i, ""));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!phone.trim() || !clientId) return;
    setSaving(true);
    const clean = phone.trim().replace(/\s+/g, "").replace(/^whatsapp:/i, "");
    const ok = await sbSavePhoneLink(clean, clientId);
    if (ok) {
      localStorage.setItem("pa_wa_phone", clean);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      <input
        value={phone}
        onChange={e => { setPhone(e.target.value); setSaved(false); }}
        placeholder="+91 9876543210"
        style={{width:"100%",background:T.bg2,border:`0.5px solid ${T.border}`,borderRadius:8,padding:"10px 12px",color:T.text,fontSize:13,fontFamily:"'JetBrains Mono', monospace",outline:"none",boxSizing:"border-box"}}
      />
      <button
        onClick={handleSave}
        disabled={saving || !phone.trim()}
        style={{padding:"9px",background:saved?T.greenDim:T.goldDim,border:`0.5px solid ${saved?T.green:T.gold}`,borderRadius:8,color:saved?T.green:T.gold,fontSize:12,cursor:"pointer",fontFamily:"inherit",transition:"all 0.2s",opacity:!phone.trim()?0.5:1}}
      >
        {saving ? "Saving…" : saved ? "✓ Linked!" : "Link Phone"}
      </button>
      {saved && <div style={{fontSize:10,color:T.green,fontFamily:"'JetBrains Mono', monospace"}}>WhatsApp transactions will now sync to your account.</div>}
    </div>
  );
};
// ─── Debt Repayment Tracker ──────────────────────────────────────────────────
const DEBT_CATS = [
  { name:"Home Loan", icon:"🏠", color:"#5A8EC0" },
  { name:"Car Loan",  icon:"🚗", color:"#E8894A" },
  { name:"Personal Loan", icon:"💳", color:"#9B7ECF" },
  { name:"Credit Card", icon:"💳", color:"#C05A5A" },
  { name:"Education Loan", icon:"📚", color:"#4EAD8A" },
  { name:"Business Loan", icon:"🏢", color:"#C9A84C" },
  { name:"Other", icon:"📦", color:"#72727C" },
];

const DebtTracker = ({ clientId, transactions }) => {
  const G = useG();
  const { fmtN, sym } = useFmt();
  const [showForm, setShowForm] = useState(false);
  const [debts, setDebts] = useState([]);
  const [editDebt, setEditDebt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name:"", category:"Personal Loan", total_amount:"", remaining_balance:"", emi_amount:"", interest_rate:"", due_day:"1", start_date:"" });

  useEffect(() => {
    if (clientId) sbGetDebts(clientId).then(setDebts);
  }, [clientId]);

  const resetForm = () => setForm({ name:"", category:"Personal Loan", total_amount:"", remaining_balance:"", emi_amount:"", interest_rate:"", due_day:"1", start_date:"" });

  const handleSave = async () => {
    if (!form.name.trim() || !form.total_amount || !form.emi_amount) return;
    setSaving(true);
    const payload = {
      id: crypto.randomUUID(),
      client_id: clientId,
      name: form.name.trim(),
      category: form.category,
      total_amount: parseFloat(form.total_amount),
      remaining_balance: parseFloat(form.remaining_balance || form.total_amount),
      emi_amount: parseFloat(form.emi_amount),
      interest_rate: parseFloat(form.interest_rate || 0),
      due_day: parseInt(form.due_day || 1),
      start_date: form.start_date || new Date().toISOString().slice(0,10),
      created_at: new Date().toISOString(),
    };
    if (editDebt) {
      await sbUpdateDebt(editDebt.id, { ...payload, id: editDebt.id });
      setDebts(prev => prev.map(d => d.id === editDebt.id ? { ...d, ...payload, id: editDebt.id } : d));
    } else {
      const saved = await sbInsertDebt(payload);
      setDebts(prev => [...prev, saved || payload]);
    }
    resetForm(); setShowForm(false); setEditDebt(null); setSaving(false);
  };

  const handleDelete = async (id) => {
    await sbDeleteDebt(id);
    setDebts(prev => prev.filter(d => d.id !== id));
  };

  const handleEdit = (debt) => {
    setEditDebt(debt);
    setForm({ name:debt.name, category:debt.category, total_amount:String(debt.total_amount), remaining_balance:String(debt.remaining_balance), emi_amount:String(debt.emi_amount), interest_rate:String(debt.interest_rate||0), due_day:String(debt.due_day||1), start_date:debt.start_date||"" });
    setShowForm(true);
  };

  // Auto-update remaining balance from matched expense transactions
  const getMatchedPayments = (debt) => {
    return transactions.filter(t =>
      t.type === "expense" &&
      (t.note?.toLowerCase().includes(debt.name.toLowerCase()) ||
       t.category === "Housing" && debt.category === "Home Loan" ||
       t.category === "Transport" && debt.category === "Car Loan")
    ).reduce((s,t) => s + t.amount, 0);
  };

  const getPayoffMonths = (debt) => {
    if (!debt.emi_amount || !debt.remaining_balance) return null;
    const rate = (debt.interest_rate || 0) / 100 / 12;
    if (rate === 0) return Math.ceil(debt.remaining_balance / debt.emi_amount);
    return Math.ceil(Math.log(debt.emi_amount / (debt.emi_amount - rate * debt.remaining_balance)) / Math.log(1 + rate));
  };

  const getPayoffDate = (debt) => {
    const months = getPayoffMonths(debt);
    if (!months || months < 0 || months > 600) return "—";
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return d.toLocaleDateString("en-US", { month:"short", year:"numeric" });
  };

  const totalDebt = debts.reduce((s,d) => s + (d.remaining_balance||0), 0);
  const totalEMI = debts.reduce((s,d) => s + (d.emi_amount||0), 0);
  const totalOriginal = debts.reduce((s,d) => s + (d.total_amount||0), 0);
  const overallPct = totalOriginal > 0 ? Math.round(((totalOriginal - totalDebt) / totalOriginal) * 100) : 0;

  const inp = { width:"100%", background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:8, padding:"10px 12px", color:G.text, fontSize:13, fontFamily:"Geist, sans-serif", outline:"none", boxSizing:"border-box" };
  const lbl = { fontSize:10, letterSpacing:"0.12em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:6, display:"block" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {/* Summary Row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        {[
          { label:"Total Debt", value:fmtN(totalDebt), color:G.red, sub:"remaining" },
          { label:"Monthly EMI", value:fmtN(totalEMI), color:G.orange, sub:"total outflow" },
          { label:"Paid Off", value:`${overallPct}%`, color:G.green, sub:"of original" },
          { label:"Active Loans", value:String(debts.length), color:G.blue, sub:"accounts" },
        ].map(s => (
          <div key={s.label} style={{ background:G.bg1, border:`0.5px solid ${G.border}`, borderRadius:12, padding:"16px 18px" }}>
            <div style={{ fontSize:10, letterSpacing:"0.12em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:8 }}>{s.label}</div>
            <div style={{ fontSize:22, fontWeight:300, color:s.color, fontFamily:"Fraunces, serif" }}>{s.value}</div>
            <div style={{ fontSize:10, color:G.dim, marginTop:3 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Debt Cards */}
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {debts.length === 0 && !showForm && (
          <div style={{ background:G.bg1, border:`0.5px solid ${G.border}`, borderRadius:12, padding:"48px", textAlign:"center" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
            <div style={{ fontSize:16, fontWeight:300, color:G.gold, fontFamily:"Fraunces, serif", marginBottom:8 }}>No debts tracked yet</div>
            <div style={{ fontSize:12, color:G.muted, marginBottom:20 }}>Add your loans, EMIs and credit card balances to track payoff progress</div>
            <button onClick={() => setShowForm(true)} style={{ padding:"10px 24px", background:G.goldDim, border:`0.5px solid ${G.gold}`, borderRadius:10, color:G.gold, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>+ Add First Debt</button>
          </div>
        )}
        {debts.map(debt => {
          const pct = debt.total_amount > 0 ? Math.round(((debt.total_amount - debt.remaining_balance) / debt.total_amount) * 100) : 0;
          const months = getPayoffMonths(debt);
          const cat = DEBT_CATS.find(c => c.name === debt.category) || DEBT_CATS[6];
          const matched = getMatchedPayments(debt);
          return (
            <div key={debt.id} style={{ background:G.bg1, border:`0.5px solid ${G.border}`, borderRadius:12, padding:"20px 22px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
                <div style={{ display:"flex", gap:12, alignItems:"center" }}>
                  <div style={{ fontSize:24 }}>{cat.icon}</div>
                  <div>
                    <div style={{ fontSize:15, fontWeight:500, color:G.text }}>{debt.name}</div>
                    <div style={{ fontSize:11, color:G.muted, fontFamily:"'JetBrains Mono', monospace" }}>{debt.category} · {debt.interest_rate}% p.a.</div>
                  </div>
                </div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <button onClick={() => handleEdit(debt)} style={{ padding:"5px 12px", background:"transparent", border:`0.5px solid ${G.border}`, borderRadius:7, color:G.muted, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>Edit</button>
                  <button onClick={() => handleDelete(debt.id)} style={{ padding:"5px 12px", background:"transparent", border:`0.5px solid ${G.redD}`, borderRadius:7, color:G.red, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>✕</button>
                </div>
              </div>

              {/* Progress bar */}
              <div style={{ marginBottom:14 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ fontSize:11, color:G.muted }}>Remaining: <span style={{ color:G.red, fontFamily:"'JetBrains Mono', monospace" }}>{fmtN(debt.remaining_balance)}</span></span>
                  <span style={{ fontSize:11, color:G.muted }}>Original: <span style={{ color:G.text, fontFamily:"'JetBrains Mono', monospace" }}>{fmtN(debt.total_amount)}</span></span>
                </div>
                <div style={{ background:G.bg4, borderRadius:3, height:6 }}>
                  <div style={{ width:`${pct}%`, height:"100%", background:`linear-gradient(90deg,${G.greenD},${G.green})`, borderRadius:3, transition:"width 0.5s ease" }}/>
                </div>
                <div style={{ fontSize:10, color:G.green, marginTop:4, fontFamily:"'JetBrains Mono', monospace" }}>{pct}% paid off</div>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
                {[
                  { label:"EMI", value:fmtN(debt.emi_amount), color:G.orange },
                  { label:"Due Day", value:`${debt.due_day}th`, color:G.blue },
                  { label:"Payoff In", value: months && months > 0 ? `${months}mo` : "—", color:G.purple },
                  { label:"Free By", value:getPayoffDate(debt), color:G.gold },
                ].map(s => (
                  <div key={s.label} style={{ background:G.bg2, borderRadius:8, padding:"10px 12px" }}>
                    <div style={{ fontSize:9, letterSpacing:"0.1em", color:G.dim, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:4 }}>{s.label}</div>
                    <div style={{ fontSize:13, color:s.color, fontFamily:"'JetBrains Mono', monospace", fontWeight:500 }}>{s.value}</div>
                  </div>
                ))}
              </div>
              {matched > 0 && (
                <div style={{ marginTop:10, padding:"8px 12px", background:G.greenDim, border:`0.5px solid ${G.green}40`, borderRadius:8, fontSize:11, color:G.green }}>
                  ✓ {fmtN(matched)} matched from your expense transactions
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add button */}
      {!showForm && debts.length > 0 && (
        <button onClick={() => { resetForm(); setEditDebt(null); setShowForm(true); }} style={{ padding:"12px", background:"transparent", border:`0.5px solid ${G.border}`, borderRadius:10, color:G.muted, fontSize:13, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor=G.gold; e.currentTarget.style.color=G.gold; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor=G.border; e.currentTarget.style.color=G.muted; }}>
          + Add Debt
        </button>
      )}

      {/* Form */}
      {showForm && (
        <div style={{ background:G.bg1, border:`0.5px solid ${G.borderGold}`, borderRadius:14, padding:"24px" }}>
          <div style={{ fontSize:13, fontWeight:500, color:G.gold, marginBottom:20 }}>{editDebt ? "Edit Debt" : "Add New Debt"}</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div style={{ gridColumn:"1/-1" }}>
              <label style={lbl}>Debt Name</label>
              <input value={form.name} onChange={e => setForm(p=>({...p, name:e.target.value}))} placeholder="e.g. HDFC Home Loan" style={inp}/>
            </div>
            <div>
              <label style={lbl}>Category</label>
              <select value={form.category} onChange={e => setForm(p=>({...p, category:e.target.value}))} style={{...inp, cursor:"pointer"}}>
                {DEBT_CATS.map(c => <option key={c.name} value={c.name}>{c.icon} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Interest Rate (% p.a.)</label>
              <input value={form.interest_rate} onChange={e => setForm(p=>({...p, interest_rate:e.target.value}))} placeholder="e.g. 8.5" type="number" style={inp}/>
            </div>
            <div>
              <label style={lbl}>Total / Original Amount ({sym})</label>
              <input value={form.total_amount} onChange={e => setForm(p=>({...p, total_amount:e.target.value}))} placeholder="e.g. 2500000" type="number" style={inp}/>
            </div>
            <div>
              <label style={lbl}>Remaining Balance ({sym})</label>
              <input value={form.remaining_balance} onChange={e => setForm(p=>({...p, remaining_balance:e.target.value}))} placeholder="Current outstanding" type="number" style={inp}/>
            </div>
            <div>
              <label style={lbl}>Monthly EMI ({sym})</label>
              <input value={form.emi_amount} onChange={e => setForm(p=>({...p, emi_amount:e.target.value}))} placeholder="e.g. 22000" type="number" style={inp}/>
            </div>
            <div>
              <label style={lbl}>EMI Due Day</label>
              <input value={form.due_day} onChange={e => setForm(p=>({...p, due_day:e.target.value}))} placeholder="1–31" type="number" min="1" max="31" style={inp}/>
            </div>
            <div>
              <label style={lbl}>Loan Start Date</label>
              <input value={form.start_date} onChange={e => setForm(p=>({...p, start_date:e.target.value}))} type="date" style={inp}/>
            </div>
          </div>
          <div style={{ display:"flex", gap:10, marginTop:18 }}>
            <button onClick={handleSave} disabled={saving || !form.name.trim() || !form.total_amount || !form.emi_amount} style={{ flex:2, padding:"12px", background:G.goldDim, border:`0.5px solid ${G.gold}`, borderRadius:10, color:G.gold, fontSize:13, cursor:"pointer", fontFamily:"inherit", fontWeight:500, opacity:saving||!form.name.trim()||!form.total_amount||!form.emi_amount?0.5:1 }}>
              {saving ? "Saving…" : editDebt ? "Save Changes" : "Add Debt"}
            </button>
            <button onClick={() => { setShowForm(false); setEditDebt(null); resetForm(); }} style={{ flex:1, padding:"12px", background:"transparent", border:`0.5px solid ${G.border}`, borderRadius:10, color:G.muted, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const FIRECalculator = ({ client, monthly, transactions }) => {
  const G = useG();
  const { fmtN, fmt, sym } = useFmt();

  // ── Persisted settings ──
  const [returnRate, setReturnRate]         = useState(8);
  const [withdrawalRate, setWithdrawalRate] = useState(4);
  const [inflationRate, setInflationRate]   = useState(6); // India-friendly default
  const [targetExpenses, setTargetExpenses] = useState("");
  const [currentNetWorth, setCurrentNetWorth] = useState("");
  const [monthlyInvestment, setMonthlyInvestment] = useState("");
  const [currentAge, setCurrentAge]   = useState("");
  const [targetAge, setTargetAge]     = useState("");
  const [sideIncome, setSideIncome]   = useState("");
  const [baristaIncome, setBaristaIncome] = useState("");
  const [showReal, setShowReal]       = useState(true);
  const [fireSaved, setFireSaved]     = useState(false);

  // Load persisted settings
  useEffect(() => {
    sbGetFireSettings(client.clientId).then(s => {
      if (!s) return;
      if (s.return_rate)        setReturnRate(s.return_rate);
      if (s.withdrawal_rate)    setWithdrawalRate(s.withdrawal_rate);
      if (s.inflation_rate)     setInflationRate(s.inflation_rate);
      if (s.target_expenses)    setTargetExpenses(String(s.target_expenses));
      if (s.current_net_worth)  setCurrentNetWorth(String(s.current_net_worth));
      if (s.monthly_investment) setMonthlyInvestment(String(s.monthly_investment));
      if (s.current_age)        setCurrentAge(String(s.current_age));
      if (s.target_age)         setTargetAge(String(s.target_age));
      if (s.side_income)        setSideIncome(String(s.side_income));
      if (s.barista_income)     setBaristaIncome(String(s.barista_income));
      if (typeof s.show_real === "boolean") setShowReal(s.show_real);
    });
  }, [client.clientId]);

  const handleSaveFIRE = async () => {
    const ok = await sbSaveFireSettings({
      client_id: client.clientId,
      return_rate:       returnRate,
      withdrawal_rate:   withdrawalRate,
      inflation_rate:    inflationRate,
      target_expenses:   parseFloat(targetExpenses) || null,
      current_net_worth: parseFloat(currentNetWorth) || null,
      monthly_investment: parseFloat(monthlyInvestment) || null,
      current_age:       parseInt(currentAge) || null,
      target_age:        parseInt(targetAge) || null,
      side_income:       parseFloat(sideIncome) || null,
      barista_income:    parseFloat(baristaIncome) || null,
      show_real:         showReal,
      updated_at: new Date().toISOString(),
    });
    if (ok) { setFireSaved(true); setTimeout(() => setFireSaved(false), 2500); }
  };

  // ── Real-data computations ──
  const n = monthly.length;
  const avgIncome  = n > 0 ? monthly.reduce((s,m) => s+m.income,  0) / n : 0;
  const avgExpense = n > 0 ? monthly.reduce((s,m) => s+m.expenses,0) / n : 0;
  const avgSavings = n > 0 ? monthly.reduce((s,m) => s+m.savings, 0) / n : 0;
  const avgInvest  = n > 0 ? monthly.reduce((s,m) => s+m.investments,0) / n : 0;
  const savingsRate = avgIncome > 0 ? ((avgSavings + avgInvest) / avgIncome * 100).toFixed(1) : 0;

  // ── Effective inputs ──
  const annualExpenses = parseFloat(targetExpenses) || avgExpense * 12;
  const monthlyContrib = parseFloat(monthlyInvestment) || (avgSavings + avgInvest);
  const nw      = parseFloat(currentNetWorth) || 0;
  const side    = parseFloat(sideIncome) || 0;
  const barista = parseFloat(baristaIncome) || 0;
  const age     = parseInt(currentAge) || 0;
  const tAge    = parseInt(targetAge) || 0;

  // ── Rates (real vs nominal) ──
  const nominalR = returnRate / 100;
  const infl     = inflationRate / 100;
  const realR    = (1 + nominalR) / (1 + infl) - 1;
  const rr       = showReal ? realR : nominalR;
  const wr       = withdrawalRate / 100;

  // ── FIRE corpus targets ──
  // Net expenses = expenses minus passive income that continues in retirement
  const netAnnualExpenses = Math.max(0, annualExpenses - side);
  const fiNumber   = netAnnualExpenses / wr;
  const leanFire   = (netAnnualExpenses * 0.75) / wr;
  const fatFire    = (netAnnualExpenses * 1.5) / wr;
  // Barista FIRE: barista income covers part of expenses, portfolio covers the rest
  const baristaFire = Math.max(0, (annualExpenses - side - barista) / wr);

  // Coast FIRE: corpus today that compounds to FI Number by target retirement age,
  // assuming no further contributions.
  const yearsUntilTargetAge = (tAge > age && age > 0) ? tAge - age : 0;
  const coastFire = yearsUntilTargetAge > 0
    ? fiNumber / Math.pow(1 + rr, yearsUntilTargetAge)
    : fiNumber;

  // ── Years-to-target via monthly compounding ──
  const yearsToTarget = (target) => {
    if (target <= nw) return 0;
    if (monthlyContrib <= 0) return null;
    const r = rr / 12;
    let acc = nw, months = 0;
    while (acc < target && months < 720) {
      acc = acc * (1 + r) + monthlyContrib;
      months++;
    }
    return months >= 720 ? null : months / 12;
  };

  const yrs        = yearsToTarget(fiNumber);
  const leanYrs    = yearsToTarget(leanFire);
  const fatYrs     = yearsToTarget(fatFire);
  const coastYrs   = yearsToTarget(coastFire);
  const baristaYrs = yearsToTarget(baristaFire);

  const fireYear = (y) => y !== null ? new Date().getFullYear() + Math.ceil(y) : "—";
  const fmtYrs   = (y) => y === null ? "—" : y < 0.08 ? "Now" : y < 1 ? "< 1 yr" : `${y.toFixed(1)} yrs`;

  // ── Progress ──
  const progressPct = fiNumber > 0 ? Math.min(100, Math.round((nw / fiNumber) * 100)) : 0;
  const progressColor = progressPct >= 75 ? G.green : progressPct >= 40 ? G.gold : G.blue;

  // ── Projection data (annual snapshots) ──
  const projectionYears = Math.max(30, yearsUntilTargetAge || 30);
  const projectionData = [];
  {
    const r = rr / 12;
    let acc = nw;
    projectionData.push({ year: 0, value: acc });
    for (let m = 1; m <= projectionYears * 12; m++) {
      acc = acc * (1 + r) + monthlyContrib;
      if (m % 12 === 0) projectionData.push({ year: m / 12, value: acc });
    }
  }

  // ── Monte Carlo: probability of hitting FI by target age ──
  // 1,000 trials, returns drawn from N(meanReturn, 15%) — typical equity volatility.
  const monteCarlo = useMemo(() => {
    if (yearsUntilTargetAge === 0 || monthlyContrib <= 0) return null;
    const TRIALS = 1000;
    const STDDEV = 0.15;
    const mean = rr;
    let success = 0;
    const finals = [];
    for (let t = 0; t < TRIALS; t++) {
      let v = nw;
      for (let y = 0; y < yearsUntilTargetAge; y++) {
        // Box-Muller transform → normal random
        let u = 0, w = 0;
        while (u === 0) u = Math.random();
        while (w === 0) w = Math.random();
        const z = Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * w);
        const ret = mean + z * STDDEV;
        v = v * (1 + ret) + monthlyContrib * 12;
      }
      finals.push(v);
      if (v >= fiNumber) success++;
    }
    finals.sort((a, b) => a - b);
    return {
      successRate: (success / TRIALS) * 100,
      median: finals[Math.floor(TRIALS / 2)],
      p10:    finals[Math.floor(TRIALS * 0.1)],
      p90:    finals[Math.floor(TRIALS * 0.9)],
    };
  }, [rr, monthlyContrib, nw, yearsUntilTargetAge, fiNumber]);

  // ── Styles ──
  const inp = { background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:8, padding:"10px 12px", color:G.text, fontSize:13, fontFamily:"'JetBrains Mono', monospace", outline:"none", boxSizing:"border-box", width:"100%" };
  const lbl = { fontSize:10, letterSpacing:"0.12em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:6, display:"block" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {/* ── Header strip: rate summary + real/nominal toggle ── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
        <div style={{ fontSize:11, color:G.muted, fontFamily:"'JetBrains Mono', monospace", letterSpacing:"0.06em" }}>
          {showReal ? `Shown in today's ${sym}` : `Shown in future ${sym}`} · {returnRate}% nominal · {inflationRate}% inflation · {(realR*100).toFixed(1)}% real
        </div>
        <div style={{ display:"flex", gap:4, background:G.bg2, padding:3, borderRadius:8, border:`0.5px solid ${G.border}` }}>
          <button onClick={() => setShowReal(true)}  style={{ padding:"6px 14px", background:showReal?G.goldDim:"transparent",  border:"none", borderRadius:6, color:showReal?G.gold:G.muted,  fontSize:11, cursor:"pointer", fontFamily:"'JetBrains Mono', monospace", letterSpacing:"0.08em" }}>REAL</button>
          <button onClick={() => setShowReal(false)} style={{ padding:"6px 14px", background:!showReal?G.goldDim:"transparent", border:"none", borderRadius:6, color:!showReal?G.gold:G.muted, fontSize:11, cursor:"pointer", fontFamily:"'JetBrains Mono', monospace", letterSpacing:"0.08em" }}>NOMINAL</button>
        </div>
      </div>

      {/* ── Top: FI Number + 3 standard targets ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        <div style={{ background:G.bg1, border:`0.5px solid ${G.gold}40`, borderRadius:14, padding:"24px" }}>
          <div style={{ fontSize:10, letterSpacing:"0.18em", color:G.goldD, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:10 }}>Your FI Number</div>
          <div style={{ fontSize:38, fontWeight:300, color:G.gold, fontFamily:"Fraunces, serif", lineHeight:1, marginBottom:6 }}>{fmt(fiNumber)}</div>
          <div style={{ fontSize:11, color:G.muted, marginBottom:16 }}>
            {side > 0 ? `Net of ${fmtN(side)}/yr passive income · ` : ""}{withdrawalRate}% withdrawal rate
          </div>
          {nw > 0 && (
            <>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                <span style={{ fontSize:11, color:G.muted }}>Progress</span>
                <span style={{ fontSize:11, color:progressColor, fontFamily:"'JetBrains Mono', monospace" }}>{progressPct}%</span>
              </div>
              <div style={{ background:G.bg4, borderRadius:3, height:8, marginBottom:6 }}>
                <div style={{ width:`${progressPct}%`, height:"100%", background:`linear-gradient(90deg,${progressColor}88,${progressColor})`, borderRadius:3, transition:"width 0.5s ease" }}/>
              </div>
              <div style={{ fontSize:10, color:G.dim, fontFamily:"'JetBrains Mono', monospace" }}>
                {fmtN(nw)} of {fmt(fiNumber)} accumulated
              </div>
            </>
          )}
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[
            { label:"Lean FIRE",    desc:"75% of current expenses", corpus:leanFire,  yrs:leanYrs, color:G.blue  },
            { label:"Regular FIRE", desc:"Current lifestyle",       corpus:fiNumber,  yrs,         color:G.gold  },
            { label:"Fat FIRE",     desc:"1.5× current lifestyle",  corpus:fatFire,   yrs:fatYrs,  color:G.green },
          ].map(t => (
            <div key={t.label} style={{ background:G.bg1, border:`0.5px solid ${t.color}40`, borderRadius:10, padding:"14px 16px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:12, fontWeight:500, color:t.color }}>{t.label}</div>
                <div style={{ fontSize:10, color:G.dim }}>{t.desc}</div>
                <div style={{ fontSize:13, color:G.text, fontFamily:"'JetBrains Mono', monospace", marginTop:4 }}>{fmt(t.corpus)}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:18, fontWeight:300, color:t.color, fontFamily:"Fraunces, serif" }}>{fmtYrs(t.yrs)}</div>
                <div style={{ fontSize:10, color:G.dim, fontFamily:"'JetBrains Mono', monospace" }}>{fireYear(t.yrs)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Coast FIRE + Barista FIRE ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        {/* Coast FIRE */}
        <div style={{ background:G.bg1, border:`0.5px solid ${G.purple}40`, borderRadius:14, padding:"20px 22px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
            <div style={{ fontSize:10, letterSpacing:"0.18em", color:G.purple, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace" }}>Coast FIRE</div>
            {age > 0 && tAge > age && (
              <div style={{ fontSize:10, color:G.dim, fontFamily:"'JetBrains Mono', monospace" }}>by age {tAge}</div>
            )}
          </div>
          <div style={{ fontSize:30, fontWeight:300, color:G.purple, fontFamily:"Fraunces, serif", lineHeight:1, marginBottom:6 }}>{fmt(coastFire)}</div>
          <div style={{ fontSize:11, color:G.muted, marginBottom:10, lineHeight:1.5 }}>
            {age > 0 && tAge > age
              ? `Reach this and you can stop contributing — compounding alone gets you to FI by ${tAge}.`
              : `Enter Current Age and Target Retirement Age below to compute.`}
          </div>
          {age > 0 && tAge > age && nw >= coastFire && (
            <div style={{ padding:"8px 12px", background:G.greenDim, border:`0.5px solid ${G.green}`, borderRadius:8, color:G.green, fontSize:11, fontFamily:"'JetBrains Mono', monospace" }}>
              ✓ You're already Coasting. Saving is now optional.
            </div>
          )}
          {age > 0 && tAge > age && nw < coastFire && (
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:G.muted, fontFamily:"'JetBrains Mono', monospace" }}>
              <span>Reach in</span>
              <span style={{ color:G.purple }}>{fmtYrs(coastYrs)} · {fireYear(coastYrs)}</span>
            </div>
          )}
        </div>

        {/* Barista FIRE */}
        <div style={{ background:G.bg1, border:`0.5px solid ${G.orange}40`, borderRadius:14, padding:"20px 22px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
            <div style={{ fontSize:10, letterSpacing:"0.18em", color:G.orange, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace" }}>Barista FIRE</div>
            <div style={{ fontSize:10, color:G.dim, fontFamily:"'JetBrains Mono', monospace" }}>part-time + portfolio</div>
          </div>
          <div style={{ fontSize:30, fontWeight:300, color:G.orange, fontFamily:"Fraunces, serif", lineHeight:1, marginBottom:6 }}>{fmt(baristaFire)}</div>
          <div style={{ fontSize:11, color:G.muted, marginBottom:10, lineHeight:1.5 }}>
            {barista > 0
              ? `Corpus needed if part-time work brings in ${fmtN(barista)}/yr.`
              : `Enter expected part-time income below to compute.`}
          </div>
          {barista > 0 && barista >= (annualExpenses - side) ? (
            <div style={{ padding:"8px 12px", background:G.greenDim, border:`0.5px solid ${G.green}`, borderRadius:8, color:G.green, fontSize:11, fontFamily:"'JetBrains Mono', monospace" }}>
              Barista income already covers expenses. No corpus required.
            </div>
          ) : (
            barista > 0 && (
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:G.muted, fontFamily:"'JetBrains Mono', monospace" }}>
                <span>Reach in</span>
                <span style={{ color:G.orange }}>{fmtYrs(baristaYrs)} · {fireYear(baristaYrs)}</span>
              </div>
            )
          )}
        </div>
      </div>

      {/* ── Net Worth Projection chart ── */}
      <ProjectionChart
        data={projectionData}
        fiNumber={fiNumber}
        coastFire={coastFire}
        currentNw={nw}
        G={G}
        fmt={fmt}
        showReal={showReal}
      />

      {/* ── Monte Carlo card ── */}
      {monteCarlo && (
        <div style={{ background:G.bg1, border:`0.5px solid ${G.border}`, borderRadius:14, padding:"20px 22px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:14, flexWrap:"wrap", gap:8 }}>
            <div>
              <div style={{ fontSize:10, letterSpacing:"0.14em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace" }}>Probability of Hitting FI</div>
              <div style={{ fontSize:10, color:G.dim, marginTop:2 }}>1,000 Monte Carlo trials · 15% market volatility · target age {tAge}</div>
            </div>
            <div style={{ fontSize:42, fontWeight:300, color: monteCarlo.successRate >= 80 ? G.green : monteCarlo.successRate >= 50 ? G.gold : G.red, fontFamily:"Fraunces, serif", lineHeight:1 }}>
              {monteCarlo.successRate.toFixed(0)}%
            </div>
          </div>
          <div style={{ background:G.bg4, borderRadius:3, height:8, marginBottom:14, overflow:"hidden" }}>
            <div style={{ width:`${monteCarlo.successRate}%`, height:"100%", background:`linear-gradient(90deg, ${G.red}, ${G.gold}, ${G.green})`, borderRadius:3 }}/>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
            {[
              { label:"Bad outcome (10th %ile)",  value: fmt(monteCarlo.p10),    color: G.red   },
              { label:"Median outcome",            value: fmt(monteCarlo.median), color: G.gold  },
              { label:"Best outcome (90th %ile)", value: fmt(monteCarlo.p90),    color: G.green },
            ].map(s => (
              <div key={s.label} style={{ background:G.bg2, borderRadius:8, padding:"10px 12px" }}>
                <div style={{ fontSize:9, color:G.dim, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", letterSpacing:"0.1em", marginBottom:4 }}>{s.label}</div>
                <div style={{ fontSize:14, color:s.color, fontFamily:"'JetBrains Mono', monospace" }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── From Your Actual Data ── */}
      <div style={{ background:G.bg1, border:`0.5px solid ${G.border}`, borderRadius:14, padding:"20px 22px" }}>
        <div style={{ fontSize:10, letterSpacing:"0.14em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:16 }}>From Your Actual Data ({n} months)</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}>
          {[
            { label:"Avg Income",     value:fmtN(avgIncome),  color:G.green  },
            { label:"Avg Expenses",   value:fmtN(avgExpense), color:G.red    },
            { label:"Avg Savings",    value:fmtN(avgSavings), color:G.blue   },
            { label:"Avg Investment", value:fmtN(avgInvest),  color:G.gold   },
            { label:"Savings Rate",   value:`${savingsRate}%`, color:G.purple },
          ].map(s => (
            <div key={s.label} style={{ background:G.bg2, borderRadius:8, padding:"12px" }}>
              <div style={{ fontSize:9, color:G.dim, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", letterSpacing:"0.1em", marginBottom:6 }}>{s.label}</div>
              <div style={{ fontSize:14, color:s.color, fontFamily:"'JetBrains Mono', monospace" }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Inputs ── */}
      <div style={{ background:G.bg1, border:`0.5px solid ${G.border}`, borderRadius:14, padding:"20px 22px" }}>
        <div style={{ fontSize:10, letterSpacing:"0.14em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:16 }}>Adjust Assumptions</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14 }}>
          <div>
            <label style={lbl}>Current Net Worth ({sym})</label>
            <input value={currentNetWorth} onChange={e => setCurrentNetWorth(e.target.value)} placeholder={`e.g. ${fmtN(500000)}`} type="number" style={inp}/>
            <div style={{ fontSize:10, color:G.dim, marginTop:4 }}>Total savings + investments today</div>
          </div>
          <div>
            <label style={lbl}>Monthly Investment ({sym})</label>
            <input value={monthlyInvestment} onChange={e => setMonthlyInvestment(e.target.value)} placeholder={fmtN(avgSavings + avgInvest)} type="number" style={inp}/>
            <div style={{ fontSize:10, color:G.dim, marginTop:4 }}>Auto-filled from your avg savings+investments</div>
          </div>
          <div>
            <label style={lbl}>Target Annual Expenses ({sym})</label>
            <input value={targetExpenses} onChange={e => setTargetExpenses(e.target.value)} placeholder={fmtN(avgExpense * 12)} type="number" style={inp}/>
            <div style={{ fontSize:10, color:G.dim, marginTop:4 }}>Auto-filled from your avg monthly × 12</div>
          </div>
          <div>
            <label style={lbl}>Passive Income in Retirement ({sym}/yr)</label>
            <input value={sideIncome} onChange={e => setSideIncome(e.target.value)} placeholder="0" type="number" style={inp}/>
            <div style={{ fontSize:10, color:G.dim, marginTop:4 }}>Rental, dividends, royalties — reduces corpus needed</div>
          </div>
          <div>
            <label style={lbl}>Current Age</label>
            <input value={currentAge} onChange={e => setCurrentAge(e.target.value)} placeholder="e.g. 32" type="number" style={inp}/>
            <div style={{ fontSize:10, color:G.dim, marginTop:4 }}>Required for Coast FIRE</div>
          </div>
          <div>
            <label style={lbl}>Target Retirement Age</label>
            <input value={targetAge} onChange={e => setTargetAge(e.target.value)} placeholder="e.g. 50" type="number" style={inp}/>
            <div style={{ fontSize:10, color:G.dim, marginTop:4 }}>When you'd like financial independence</div>
          </div>
          <div>
            <label style={lbl}>Barista Income ({sym}/yr)</label>
            <input value={baristaIncome} onChange={e => setBaristaIncome(e.target.value)} placeholder="e.g. 600000" type="number" style={inp}/>
            <div style={{ fontSize:10, color:G.dim, marginTop:4 }}>Expected part-time income in semi-retirement</div>
          </div>
          <div>
            <label style={lbl}>Inflation Rate (%)</label>
            <div style={{ display:"flex", gap:6 }}>
              {[3,4,5,6,7].map(r => (
                <button key={r} onClick={() => setInflationRate(r)} style={{ flex:1, padding:"10px 0", background:inflationRate===r?G.goldDim:"transparent", border:`0.5px solid ${inflationRate===r?G.gold:G.border}`, borderRadius:8, color:inflationRate===r?G.gold:G.muted, fontSize:12, cursor:"pointer", fontFamily:"'JetBrains Mono', monospace" }}>{r}%</button>
              ))}
            </div>
          </div>
          <div>
            <label style={lbl}>Expected Return Rate (%)</label>
            <div style={{ display:"flex", gap:6 }}>
              {[6,8,10,12].map(r => (
                <button key={r} onClick={() => setReturnRate(r)} style={{ flex:1, padding:"10px 0", background:returnRate===r?G.goldDim:"transparent", border:`0.5px solid ${returnRate===r?G.gold:G.border}`, borderRadius:8, color:returnRate===r?G.gold:G.muted, fontSize:12, cursor:"pointer", fontFamily:"'JetBrains Mono', monospace" }}>{r}%</button>
              ))}
            </div>
          </div>
          <div>
            <label style={lbl}>Withdrawal Rate (%)</label>
            <div style={{ display:"flex", gap:6 }}>
              {[3,3.5,4,5].map(r => (
                <button key={r} onClick={() => setWithdrawalRate(r)} style={{ flex:1, padding:"10px 0", background:withdrawalRate===r?G.goldDim:"transparent", border:`0.5px solid ${withdrawalRate===r?G.gold:G.border}`, borderRadius:8, color:withdrawalRate===r?G.gold:G.muted, fontSize:12, cursor:"pointer", fontFamily:"'JetBrains Mono', monospace" }}>{r}%</button>
              ))}
            </div>
          </div>
        </div>

        {/* Formula breakdown */}
        <div style={{ marginTop:16, padding:"12px 14px", background:G.greenDim, border:`0.5px solid ${G.green}40`, borderRadius:10, fontSize:11, color:G.green, lineHeight:1.7 }}>
          <strong>FI Number =</strong> (Annual Expenses − Passive Income) ÷ Withdrawal Rate<br/>
          = ({fmtN(annualExpenses)} − {fmtN(side)}) ÷ {withdrawalRate}% = <strong>{fmt(fiNumber)}</strong><br/>
          <span style={{ color:G.muted }}>
            <strong>Real return:</strong> {(realR*100).toFixed(2)}% (after {inflationRate}% inflation)
            {yearsUntilTargetAge > 0 && <> · <strong>Coast multiplier:</strong> {Math.pow(1 + rr, yearsUntilTargetAge).toFixed(2)}×</>}
          </span>
        </div>

        <div style={{ marginTop:16 }}>
          <button onClick={handleSaveFIRE} style={{ padding:"11px 24px", background:fireSaved?G.greenDim:G.goldDim, border:`0.5px solid ${fireSaved?G.green:G.gold}`, borderRadius:10, color:fireSaved?G.green:G.gold, fontSize:13, cursor:"pointer", fontFamily:"inherit", fontWeight:500, transition:"all 0.2s" }}>
            {fireSaved ? "✓ Saved!" : "Save Settings →"}
          </button>
          <span style={{ marginLeft:12, fontSize:11, color:G.dim }}>Saved to your account — restored on next login.</span>
        </div>
      </div>
    </div>
  );
};

// ─── Net Worth Projection Chart (SVG) ─────────────────────────────────────────
const ProjectionChart = ({ data, fiNumber, coastFire, currentNw, G, fmt, showReal }) => {
  if (!data || data.length === 0) return null;
  const W = 800, H = 280, P = { t: 30, r: 30, b: 36, l: 70 };

  const xMax = data[data.length - 1].year;
  const yMax = Math.max(...data.map(d => d.value), fiNumber) * 1.1;

  const xS = (y) => P.l + (y / xMax) * (W - P.l - P.r);
  const yS = (v) => H - P.b - (v / yMax) * (H - P.t - P.b);

  const pathD = data.map((d, i) => `${i === 0 ? "M" : "L"} ${xS(d.year).toFixed(2)} ${yS(d.value).toFixed(2)}`).join(" ");
  const areaD = `${pathD} L ${xS(xMax).toFixed(2)} ${H - P.b} L ${xS(0).toFixed(2)} ${H - P.b} Z`;

  // Find year where portfolio first crosses FI
  const fiCrossIdx = data.findIndex(d => d.value >= fiNumber);
  const xTicks = [0, Math.floor(xMax/4), Math.floor(xMax/2), Math.floor(3*xMax/4), Math.floor(xMax)];
  const yTicks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div style={{ background:G.bg1, border:`0.5px solid ${G.border}`, borderRadius:14, padding:"20px 22px" }}>
      <div style={{ fontSize:10, letterSpacing:"0.14em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:6 }}>Net Worth Projection</div>
      <div style={{ fontSize:11, color:G.dim, marginBottom:12 }}>
        Compounded growth, {showReal ? "inflation-adjusted (today's money)" : "nominal (future money)"}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto", display:"block" }}>
        <defs>
          <linearGradient id="fireAreaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={G.gold} stopOpacity="0.28"/>
            <stop offset="100%" stopColor={G.gold} stopOpacity="0"/>
          </linearGradient>
        </defs>

        {/* y-axis gridlines */}
        {yTicks.map(p => (
          <g key={p}>
            <line x1={P.l} x2={W - P.r} y1={yS(yMax * p)} y2={yS(yMax * p)} stroke={G.border} strokeWidth="0.5"/>
            <text x={P.l - 8} y={yS(yMax * p) + 3} fontSize="9" fill={G.dim} textAnchor="end" fontFamily="'JetBrains Mono', monospace">{fmt(yMax * p)}</text>
          </g>
        ))}

        {/* x-axis labels */}
        {xTicks.map(yr => (
          <text key={yr} x={xS(yr)} y={H - P.b + 18} fontSize="9" fill={G.dim} textAnchor="middle" fontFamily="'JetBrains Mono', monospace">Yr {yr}</text>
        ))}

        {/* FI horizontal line */}
        <line x1={P.l} x2={W - P.r} y1={yS(fiNumber)} y2={yS(fiNumber)} stroke={G.gold} strokeWidth="1" strokeDasharray="3,3"/>
        <text x={W - P.r - 4} y={yS(fiNumber) - 4} fontSize="10" fill={G.gold} textAnchor="end" fontFamily="'JetBrains Mono', monospace">FI · {fmt(fiNumber)}</text>

        {/* Coast FIRE horizontal line */}
        {coastFire > 0 && coastFire < yMax && coastFire < fiNumber * 0.95 && (
          <>
            <line x1={P.l} x2={W - P.r} y1={yS(coastFire)} y2={yS(coastFire)} stroke={G.purple} strokeWidth="1" strokeDasharray="2,4" opacity="0.7"/>
            <text x={P.l + 4} y={yS(coastFire) - 4} fontSize="10" fill={G.purple} textAnchor="start" fontFamily="'JetBrains Mono', monospace">Coast · {fmt(coastFire)}</text>
          </>
        )}

        {/* Area + line */}
        <path d={areaD} fill="url(#fireAreaGradient)"/>
        <path d={pathD} stroke={G.gold} strokeWidth="1.5" fill="none"/>

        {/* FI crossing marker */}
        {fiCrossIdx > 0 && (
          <>
            <line
              x1={xS(data[fiCrossIdx].year)} x2={xS(data[fiCrossIdx].year)}
              y1={yS(data[fiCrossIdx].value)} y2={H - P.b}
              stroke={G.green} strokeWidth="0.5" strokeDasharray="2,2" opacity="0.5"
            />
            <circle cx={xS(data[fiCrossIdx].year)} cy={yS(data[fiCrossIdx].value)} r="5" fill={G.green} stroke={G.bg1} strokeWidth="2"/>
            <text x={xS(data[fiCrossIdx].year)} y={yS(data[fiCrossIdx].value) - 12} fontSize="10" fill={G.green} textAnchor="middle" fontFamily="'JetBrains Mono', monospace">FI at Yr {data[fiCrossIdx].year}</text>
          </>
        )}

        {/* Today marker */}
        <circle cx={xS(0)} cy={yS(currentNw)} r="4" fill={G.text} stroke={G.bg1} strokeWidth="2"/>
        <text x={xS(0) + 12} y={yS(currentNw) + 4} fontSize="9" fill={G.muted} textAnchor="start" fontFamily="'JetBrains Mono', monospace">Today</text>
      </svg>
    </div>
  );
};
// ─── How To Use ───────────────────────────────────────────────────────────────
const HowToUse = () => {
  const G = useG();
  return (
    <div style={{height:"calc(100vh - 160px)", borderRadius:12, overflow:"hidden", border:`0.5px solid ${G.border}`}}>
      <iframe
        src="/how-to-use.html"
        style={{width:"100%", height:"100%", border:"none", background:G.bg0}}
        title="How to Use"
      />
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────
// BANK STATEMENT IMPORTER  — paste this block into your App.jsx
// Uses PDF.js (free CDN) for text extraction, then your existing OR_KEY / GPT-4o
// ─────────────────────────────────────────────────────────────────────────────
//
// 4-line integration (add these to your existing code):
//
//  1. VIEWS object:    bankimport: "Bank Statement Import"
//  2. NAV array:       { id:"bankimport", label:"Bank Import", icon:"📄" }
//  3. View render:     {view==="bankimport" && <BankImport onAdd={handleAddTx} transactions={transactions}/>}
//
// Everything else (OR_KEY, callAI, G, useG, useFmt, genTxId, fmtMk,
// INCOME_CATS, EXPENSE_CATS) already exists in your file.
// ─────────────────────────────────────────────────────────────────────────────

// ── PDF.js lazy loader (no npm, no cost) ─────────────────────────────────────
let _pdfjs = null;
async function getPdfJs() {
  if (_pdfjs) return _pdfjs;
  if (window.pdfjsLib) { _pdfjs = window.pdfjsLib; return _pdfjs; }
  await new Promise((res, rej) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
  window.pdfjsLib.GlobalWorkerOptions.workerSrc =
    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
  _pdfjs = window.pdfjsLib;
  return _pdfjs;
}

async function extractPdfText(file, password = "") {
  const lib = await getPdfJs();
  const buf = await file.arrayBuffer();
  try {
    const doc = await lib.getDocument({ data: buf, password }).promise;
    const pages = [];
    for (let i = 1; i <= doc.numPages; i++) {
      const page    = await doc.getPage(i);
      const content = await page.getTextContent();
      pages.push(content.items.map(it => it.str).join(" "));
    }
    return pages.join("\n");
  } catch (e) {
    // PDF.js throws a specific error code for wrong/missing password
    if (e.name === "PasswordException") {
      throw new Error("PASSWORD_REQUIRED");
    }
    throw e;
  }
}

// ── AI parser — reuses your OR_KEY, higher token budget than callAI default ──
async function parseStatementText(rawText, sym) {
  const d = new Date();
  const currentMonth = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;

const SYSTEM = `You are a bank statement transaction extractor for global banks.
Return ONLY a raw JSON array — no markdown, no explanation.
Each element: {"type":"income"|"expense","amount":<positive number>,"category":"<see list>","note":"<3-6 words>","month":"<YYYY-MM>"}

EXPENSE categories: Housing, Food & Dining, Transport, Healthcare, Shopping, Entertainment, Utilities, Insurance, Subscriptions, Travel, Education, Investments, Savings, Other
INCOME categories: Salary, Business, Rental, Dividends, Freelance, Capital Gains, Other Income

CATEGORISATION RULES (global):

Food & Dining:
  Zomato, Swiggy, Dunzo, McDonald's, Starbucks, Subway, KFC, Domino's,
  Uber Eats, DoorDash, Deliveroo, Just Eat, Grubhub, OpenTable,
  any restaurant / cafe / diner / bistro / pub / bar name

Transport:
  Uber, Ola, Lyft, Bolt, Grab, Didi, Citymapper,
  TfL, BART, MTA, Oyster, National Rail, Trainline, Eurostar,
  BP, Shell, ExxonMobil, Chevron, Texaco, Gulf, HPCL, IOCL, BPCL,
  any petrol / gas / fuel / parking / toll

Shopping:
  Amazon, Flipkart, Myntra, Ajio, ASOS, Zara, H&M, Uniqlo,
  Walmart, Target, Costco, Best Buy, Argos, John Lewis, Marks & Spencer,
  eBay, Etsy, Shopify merchants, any retail store

Subscriptions:
  Netflix, Spotify, Apple, Google, Microsoft, Adobe, Dropbox,
  YouTube Premium, Disney+, HBO, Hulu, Prime, iCloud,
  any monthly recurring tech/media charge

Utilities:
  British Gas, EDF, Octopus, E.ON, Con Edison, PG&E, ComEd,
  Verizon, AT&T, T-Mobile, Vodafone, BT, Sky, Comcast, Xfinity,
  any electricity / gas / water / internet / phone bill

Housing:
  Rent, Mortgage, lease, any EMI labelled as home loan,
  council tax, HOA, property tax

Healthcare:
  NHS, CVS, Walgreens, Boots, any pharmacy, hospital, clinic,
  dental, optical, health insurance premium

Travel:
  Airlines (BA, Delta, United, Emirates, IndiGo, Air India, Ryanair, easyJet),
  Booking.com, Airbnb, Hotels.com, Expedia, TripAdvisor,
  any hotel / hostel / resort charge

Entertainment:
  cinema, theatre, concert, Ticketmaster, StubHub, bowling,
  gym, fitness, SoulCycle, PureGym, any leisure activity

Education:
  Udemy, Coursera, Skillshare, any university / tuition / school fee,
  bookstore, stationery

Investments:
  Zerodha, Groww, Robinhood, eToro, Fidelity, Vanguard, Charles Schwab,
  any brokerage / mutual fund / SIP / ETF purchase

Insurance:
  LIC, Aviva, AXA, Zurich, Allianz, Progressive, Geico, any insurance premium

Salary / income:
  SALARY, PAYROLL, BACS, DIRECT DEP, PAY, employer name + salary,
  dividend credit, rental income, freelance payment

ATM / cash withdrawals → Other expense
Ambiguous transfers (UPI, IMPS, NEFT, SEPA, ACH, Faster Payments) with no clear merchant → Other

Additional rules:
- Credits / deposits → income. Debits / withdrawals → expense.
- Skip opening balance, closing balance, and table header lines.
- If date is unclear use ${currentMonth}.
- Amount as a plain positive number — no currency symbols or commas.
- Infer month from the transaction date in the statement (YYYY-MM).`;

  const truncated = rawText.slice(0, 12000); // stay within context

 const raw = await callAI(SYSTEM, `Bank statement text:\n\n${truncated}`, []);
  const clean = raw.replace(/```json|```/g, "").trim();
  return JSON.parse(clean);
}

// ── Component ─────────────────────────────────────────────────────────────────
const BankImport = ({ onAdd, transactions }) => {
  const G = useG();
  const { fmtN, sym } = useFmt();

  const [dragOver,  setDragOver]  = useState(false);
  const [file,      setFile]      = useState(null);
  const [step,      setStep]      = useState("upload"); // upload | parsing | review | success
  const [parsed,    setParsed]    = useState([]);
  const [error,     setError]     = useState("");
  const [progress,  setProgress]  = useState("");
  const [importing, setImporting] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const [editIdx,   setEditIdx]   = useState(null);

  const fileRef = useRef(null);

  // ── file handling ──────────────────────────────────────────────────────────
  const handleFile = (f) => {
    if (!f || f.type !== "application/pdf") { setError("Please upload a PDF file."); return; }
    setFile(f); setError(""); runParse(f);
  };

const runParse = async (f, password = "") => {
  setStep("parsing"); setProgress("Reading PDF…");
  try {
    const text = await extractPdfText(f, password);
    if (!text.trim()) throw new Error("No readable text found. Try a downloaded e-statement, not a scanned image.");
    setProgress("Sending to AI (GPT-4o)…");
    const txs = await parseStatementText(text, sym);
    if (!Array.isArray(txs) || txs.length === 0)
      throw new Error("No transactions detected. Try a different statement.");
    setParsed(txs.map((t, i) => ({ ...t, _id: i, selected: true, amount: Math.abs(parseFloat(t.amount) || 0) })));
    setStep("review");
  } catch (e) {
    if (e.message === "PASSWORD_REQUIRED") {
      setStep("password"); // show password prompt
    } else {
      setError(e.message || "Parse failed."); setStep("upload");
    }
  }
  setProgress("");
};

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  // ── row helpers ────────────────────────────────────────────────────────────
  const updateRow = (idx, patch) => setParsed(p => p.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const toggleAll = (val) => setParsed(p => p.map(r => ({ ...r, selected: val })));
  const removeRow = (idx) => setParsed(p => p.filter((_, i) => i !== idx));
  const [password, setPassword] = useState("");
  // ── import ─────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    const sel = parsed.filter(r => r.selected && r.amount > 0);
    if (!sel.length) return;
    setImporting(true);
    for (const t of sel) {
      await onAdd({ id: genTxId(), type: t.type, category: t.category, amount: t.amount, month: t.month, note: t.note, created_at: new Date().toISOString(), ts: Date.now() });
    }
    setDoneCount(sel.length); setImporting(false); setStep("success");
  };

  const reset = () => { setFile(null); setParsed([]); setError(""); setStep("upload"); setEditIdx(null); setDoneCount(0); };

  // ── derived ────────────────────────────────────────────────────────────────
  const sel       = parsed.filter(r => r.selected);
  const totalInc  = sel.filter(r => r.type === "income").reduce((s, r) => s + r.amount, 0);
  const totalExp  = sel.filter(r => r.type === "expense").reduce((s, r) => s + r.amount, 0);

  const inp = { background: G.bg2, border: `0.5px solid ${G.border}`, borderRadius: 6, padding: "5px 8px", color: G.text, fontSize: 12, fontFamily: "'JetBrains Mono', monospace", outline: "none" };
  const CAT_META = [...INCOME_CATS, ...EXPENSE_CATS].reduce((m, c) => { m[c.name] = c; return m; }, {});

  // ════════════════════════════════════════════════════════════════════════════
  // UPLOAD
  // ════════════════════════════════════════════════════════════════════════════
  if (step === "upload") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileRef.current?.click()}
        style={{ border: `1.5px dashed ${dragOver ? G.gold : G.border}`, borderRadius: 16, padding: "64px 40px", textAlign: "center", cursor: "pointer", background: dragOver ? G.goldDim : G.bg1, transition: "all 0.18s" }}
      >
        <input ref={fileRef} type="file" accept="application/pdf" style={{ display: "none" }} onChange={e => handleFile(e.target.files?.[0])} />
        <div style={{ fontSize: 40, marginBottom: 16 }}>📄</div>
        <div style={{ fontSize: 22, fontWeight: 300, color: G.text, fontFamily: "Fraunces, serif", marginBottom: 8 }}>Drop your bank statement PDF here</div>
        <div style={{ fontSize: 12, color: G.muted, marginBottom: 24, lineHeight: 1.7 }}>
          Works with Chase, Bank of America, Wells Fargo,Barclays, HSBC, HDFC, ICICI, SBI, Axis, Kotak, and most banks.<br />
          Must be a text-based e-statement (not a scanned image).
        </div>
        <div style={{ display: "inline-block", padding: "10px 28px", background: G.goldDim, border: `0.5px solid ${G.gold}`, borderRadius: 8, color: G.gold, fontSize: 13, fontWeight: 500 }}>Choose PDF →</div>
      </div>

      {error && (
        <div style={{ background: G.redDim, border: `0.5px solid ${G.red}40`, borderRadius: 10, padding: "14px 18px", fontSize: 13, color: G.red }}>⚠ {error}</div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
        {[
          { icon: "🆓", title: "No extra cost",  desc: "Uses your existing OpenRouter key (GPT-4o). PDF text extraction is 100% free via PDF.js." },
          { icon: "⚡", title: "Fast parse",      desc: "Most statements parse in 10–20 seconds. Multi-page PDFs handled automatically." },
          { icon: "✏️", title: "Review & edit",   desc: "Every transaction is shown before import. Fix categories, deselect wrong rows, or delete any entry." },
        ].map(c => (
          <div key={c.title} style={{ background: G.bg2, border: `0.5px solid ${G.border}`, borderRadius: 12, padding: "18px 20px" }}>
            <div style={{ fontSize: 22, marginBottom: 10 }}>{c.icon}</div>
            <div style={{ fontSize: 13, fontWeight: 500, color: G.text, marginBottom: 6 }}>{c.title}</div>
            <div style={{ fontSize: 11, color: G.muted, lineHeight: 1.6 }}>{c.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // PARSING
  // ════════════════════════════════════════════════════════════════════════════
  if (step === "parsing") return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 20, textAlign: "center" }}>
      <div style={{ fontSize: 44, animation: "pulse 1.5s ease-in-out infinite" }}>📄</div>
      <div style={{ fontSize: 20, fontWeight: 300, color: G.gold, fontFamily: "Fraunces, serif" }}>Analysing statement</div>
      <div style={{ fontSize: 11, color: G.muted, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.12em", animation: "pulse 1.2s ease-in-out infinite" }}>{progress}</div>
      <div style={{ fontSize: 11, color: G.dim }}>{file?.name}</div>
    </div>
  );
if (step === "password") return (
  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"60vh", gap:20, textAlign:"center" }}>
    <div style={{ fontSize:40 }}>🔒</div>
    <div style={{ fontSize:22, fontWeight:300, color:G.gold, fontFamily:"Fraunces, serif" }}>
      This PDF is password protected
    </div>
    <div style={{ fontSize:13, color:G.muted }}>
      Most banks use your date of birth (DDMMYYYY) or PAN number as the password.
    </div>
    <input
      type="password"
      value={password}
      onChange={e => setPassword(e.target.value)}
      onKeyDown={e => e.key === "Enter" && runParse(file, password)}
      placeholder="Enter PDF password"
      autoFocus
      style={{ width:280, background:G.bg2, border:`0.5px solid ${G.gold}`, borderRadius:8, padding:"12px 16px", color:G.text, fontSize:14, fontFamily:"'JetBrains Mono', monospace", outline:"none", textAlign:"center", letterSpacing:"0.12em" }}
    />
    <div style={{ display:"flex", gap:10 }}>
      <button onClick={() => runParse(file, password)} disabled={!password.trim()}
        style={{ padding:"11px 28px", background:G.goldDim, border:`0.5px solid ${G.gold}`, borderRadius:9, color:G.gold, fontSize:13, fontWeight:500, cursor:!password.trim()?"not-allowed":"pointer", fontFamily:"inherit", opacity:!password.trim()?0.5:1 }}>
        Unlock & Parse →
      </button>
      <button onClick={reset}
        style={{ padding:"11px 18px", background:"transparent", border:`0.5px solid ${G.border}`, borderRadius:9, color:G.muted, fontSize:13, cursor:"pointer", fontFamily:"inherit" }}>
        Cancel
      </button>
    </div>
    <div style={{ fontSize:11, color:G.dim, maxWidth:340, lineHeight:1.7 }}>
      The password is used only to unlock the PDF locally in your browser. It is never sent anywhere.
    </div>
  </div>
);
  // ════════════════════════════════════════════════════════════════════════════
  // REVIEW
  // ════════════════════════════════════════════════════════════════════════════
  if (step === "review") return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {[
          { label: "Found",    value: String(parsed.length),  color: G.text  },
          { label: "Selected", value: String(sel.length),     color: G.gold  },
          { label: "Income",   value: fmtN(totalInc),         color: G.green },
          { label: "Expenses", value: fmtN(totalExp),         color: G.red   },
        ].map(s => (
          <div key={s.label} style={{ background: G.bg2, border: `0.5px solid ${G.border}`, borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ fontSize: 9, letterSpacing: "0.14em", color: G.muted, textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 20, fontWeight: 500, color: s.color, fontFamily: "'JetBrains Mono', monospace" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 8 }}>
          {[["Select all", () => toggleAll(true)], ["Deselect all", () => toggleAll(false)], ["← New file", reset]].map(([label, fn]) => (
            <button key={label} onClick={fn}
              style={{ padding: "6px 14px", background: "transparent", border: `0.5px solid ${G.border}`, borderRadius: 7, color: G.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
              {label}
            </button>
          ))}
        </div>
        <button onClick={handleImport} disabled={importing || sel.length === 0}
          style={{ padding: "10px 26px", background: sel.length === 0 ? G.bg4 : G.goldDim, border: `0.5px solid ${G.gold}`, borderRadius: 9, color: G.gold, fontSize: 13, fontWeight: 500, cursor: sel.length === 0 ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: sel.length === 0 ? 0.5 : 1 }}>
          {importing ? "◌  Importing…" : `Import ${sel.length} transaction${sel.length !== 1 ? "s" : ""} →`}
        </button>
      </div>

      {/* Table */}
      <div style={{ background: G.bg1, border: `0.5px solid ${G.border}`, borderRadius: 12, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ display: "grid", gridTemplateColumns: "36px 28px 80px 1fr 180px 96px 76px 40px", padding: "10px 16px", background: G.bg2, borderBottom: `0.5px solid ${G.border}`, fontSize: 9, letterSpacing: "0.12em", color: G.muted, textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace", gap: 0 }}>
          <div>✓</div><div></div><div>Month</div><div>Description</div><div>Category</div><div style={{ textAlign: "right" }}>Amount</div><div style={{ textAlign: "center" }}>Type</div><div></div>
        </div>

        {/* Rows */}
        <div style={{ maxHeight: "54vh", overflowY: "auto" }}>
          {parsed.map((row, idx) => {
            const meta    = CAT_META[row.category] || { icon: "📦", color: G.muted };
            const isEdit  = editIdx === idx;
            const rowClr  = row.type === "income" ? G.green : G.red;
            return (
              <div key={row._id} style={{ display: "grid", gridTemplateColumns: "36px 28px 80px 1fr 180px 96px 76px 40px", padding: "9px 16px", borderBottom: `0.5px solid ${G.border}`, background: row.selected ? "transparent" : `${G.dim}10`, opacity: row.selected ? 1 : 0.4, alignItems: "center", gap: 0, transition: "all 0.12s" }}>

                {/* Checkbox */}
                <div onClick={() => updateRow(idx, { selected: !row.selected })}
                  style={{ width: 16, height: 16, borderRadius: 4, border: `1.5px solid ${row.selected ? G.gold : G.dim}`, background: row.selected ? G.goldDim : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: G.gold }}>
                  {row.selected && "✓"}
                </div>

                {/* Icon */}
                <div style={{ fontSize: 14, textAlign: "center" }}>{meta.icon}</div>

                {/* Month */}
                {isEdit
                  ? <input value={row.month} onChange={e => updateRow(idx, { month: e.target.value })} style={{ ...inp, width: 70 }} />
                  : <div style={{ fontSize: 11, color: G.muted, fontFamily: "'JetBrains Mono', monospace" }}>{fmtMk(row.month)}</div>}

                {/* Note */}
                {isEdit
                  ? <input value={row.note} onChange={e => updateRow(idx, { note: e.target.value })} style={{ ...inp, width: "92%" }} />
                  : <div style={{ fontSize: 12, color: G.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 8 }}>{row.note}</div>}

                {/* Category */}
                <select value={row.category}
                  onChange={e => {
                    const cat = e.target.value;
                    updateRow(idx, { category: cat, type: INCOME_CATS.some(c => c.name === cat) ? "income" : "expense" });
                  }}
                  style={{ ...inp, width: "95%", cursor: "pointer" }}>
                  <optgroup label="── Income ──">
                    {INCOME_CATS.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </optgroup>
                  <optgroup label="── Expenses ──">
                    {EXPENSE_CATS.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </optgroup>
                </select>

                {/* Amount */}
                {isEdit
                  ? <input value={row.amount} type="number" onChange={e => updateRow(idx, { amount: parseFloat(e.target.value) || 0 })} style={{ ...inp, width: 80, textAlign: "right" }} />
                  : <div style={{ fontSize: 12, color: rowClr, fontFamily: "'JetBrains Mono', monospace", textAlign: "right", fontWeight: 500, paddingRight: 4 }}>{row.type === "income" ? "+" : "-"}{fmtN(row.amount)}</div>}

                {/* Type badge */}
                <div onClick={() => updateRow(idx, { type: row.type === "income" ? "expense" : "income" })}
                  style={{ textAlign: "center", fontSize: 9, letterSpacing: "0.08em", color: rowClr, cursor: "pointer", fontFamily: "'JetBrains Mono', monospace", padding: "3px 0", borderRadius: 4, background: row.type === "income" ? G.greenDim : G.redDim, border: `0.5px solid ${rowClr}50`, userSelect: "none", margin: "0 4px" }}>
                  {row.type === "income" ? "↑ INC" : "↓ EXP"}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: 3, justifyContent: "center" }}>
                  <button onClick={() => setEditIdx(isEdit ? null : idx)}
                    style={{ background: "none", border: "none", color: isEdit ? G.gold : G.dim, cursor: "pointer", fontSize: 12, padding: "2px 3px" }} title={isEdit ? "Done" : "Edit"}>
                    {isEdit ? "✓" : "✎"}
                  </button>
                  <button onClick={() => removeRow(idx)}
                    style={{ background: "none", border: "none", color: G.dim, cursor: "pointer", fontSize: 12, padding: "2px 3px" }} title="Remove"
                    onMouseEnter={e => e.currentTarget.style.color = G.red}
                    onMouseLeave={e => e.currentTarget.style.color = G.dim}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom CTA */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={handleImport} disabled={importing || sel.length === 0}
          style={{ padding: "12px 32px", background: sel.length === 0 ? G.bg4 : G.goldDim, border: `0.5px solid ${G.gold}`, borderRadius: 10, color: G.gold, fontSize: 14, fontWeight: 500, cursor: sel.length === 0 ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: sel.length === 0 ? 0.5 : 1 }}>
          {importing ? "◌  Saving transactions…" : `Import ${sel.length} transaction${sel.length !== 1 ? "s" : ""} →`}
        </button>
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SUCCESS
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 20, textAlign: "center", animation: "fadeIn 0.4s ease" }}>
      <div style={{ fontSize: 52, color: G.green }}>✓</div>
      <div style={{ fontSize: 28, fontWeight: 300, color: G.text, fontFamily: "Fraunces, serif" }}>{doneCount} transactions imported</div>
      <div style={{ fontSize: 13, color: G.muted, maxWidth: 400, lineHeight: 1.7 }}>All selected transactions have been saved. Head to the Dashboard to see your updated cash flow.</div>
      <button onClick={reset}
        style={{ padding: "12px 28px", background: G.bg2, border: `0.5px solid ${G.border}`, borderRadius: 10, color: G.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
        Import another statement
      </button>
    </div>
  );
};
// ─── Strategic Scenarios — the 5 named freedom questions ─────────────────────
const StrategicScenarios = ({ client, monthly, transactions, plan }) => {
  const G = useG();
  const { fmt, sym } = useFmt();

  if (plan < 1) return <UpgradeGate tier={plan} requiredTier={1} feature="Strategic Freedom Map" />;
  if (!monthly.length) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"50vh", gap:14, textAlign:"center" }}>
      <div style={{ fontSize:32 }}>◆</div>
      <div style={{ fontSize:16, color:G.gold, fontFamily:"Fraunces, serif" }}>Strategic Freedom Map</div>
      <div style={{ fontSize:13, color:G.muted, maxWidth:360 }}>Add at least one month of data to see your 5-question freedom map.</div>
    </div>
  );

  const n = monthly.length;
  const avgInc = monthly.reduce((s,m)=>s+m.income,0)/n;
  const avgExp = monthly.reduce((s,m)=>s+m.expenses,0)/n;
  const avgSav = monthly.reduce((s,m)=>s+m.savings,0)/n;
  const avgInv = monthly.reduce((s,m)=>s+m.investments,0)/n;
  const baseContrib = avgSav + avgInv;
  const discCats = ["Shopping","Entertainment","Food & Dining","Travel","Subscriptions"];
  const discMonthly = transactions.filter(t => t.type==="expense" && discCats.includes(t.category)).reduce((s,t)=>s+t.amount,0) / n;

  const baseNW = transactions.filter(t => t.category==="Investments"||t.category==="Savings").reduce((s,t)=>s+t.amount,0) + 50000;
  const rate = 0.08 / 12;
  const project = (contrib, months=84) => { let v=baseNW; for(let i=0;i<months;i++) v = v*(1+rate) + contrib; return v; };
  const annualExp = avgExp * 12;
  const fiTarget = annualExp * 25;
  const monthsToFI = (contrib) => { if (contrib<=0) return null; let v=baseNW, m=0; while (v<fiTarget && m<600) { v = v*(1+rate) + contrib; m++; } return m<600 ? m : null; };

  const cut = Math.round(discMonthly * 0.15);
  const lift = Math.round(avgInc * 0.125);
  const baseMonths = monthsToFI(baseContrib);

  const scenarios = [
    { q:"If I keep doing what I'm doing, where will I be?", label:"Status Quo Baseline", contrib:baseContrib, color:G.blue,
      note:`At your current pace of ${sym}${Math.round(baseContrib).toLocaleString()}/mo in wealth building.` },
    { q:"What happens if I cut discretionary spending by ~15%?", label:"Lean Mode", contrib:baseContrib + cut, color:G.gold,
      note:`Reclaims ${sym}${cut.toLocaleString()}/mo from shopping, dining, entertainment, travel & subscriptions.` },
    { q:"What if I increase my investments by ~12%?", label:"Wealth Building Lift", contrib:baseContrib + lift, color:G.purple,
      note:`Adds ${sym}${lift.toLocaleString()}/mo to investments (12.5% of avg income).` },
    { q:"How fast can I reach financial freedom at my current pace?", label:"Freedom Timeline", contrib:baseContrib, color:G.green,
      note:`FI target: ${fmt(fiTarget)} (25× annual spend of ${fmt(annualExp)}, 4% withdrawal).`, isFI:true },
    { q:"What changes would accelerate my timeline?", label:"Combined Acceleration", contrib:baseContrib + cut + lift, color:G.orange,
      note:`Both levers together: discretionary cut + investment lift.` },
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ background:G.bg2, border:`0.5px solid ${G.borderGold}`, borderRadius:12, padding:"18px 22px" }}>
        <div style={{ fontSize:11, letterSpacing:"0.14em", color:G.goldD, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:6 }}>5-Question Freedom Map</div>
        <div style={{ fontSize:13, color:G.text, lineHeight:1.65 }}>Each card answers a strategic question about your path to financial freedom — anchored on your actual data, not generic assumptions.</div>
      </div>

      {scenarios.map((s,i) => {
        const months = monthsToFI(s.contrib);
        const yrs = months ? months/12 : null;
        const savedYrs = baseMonths && months ? (baseMonths - months)/12 : 0;
        const proj = project(s.contrib);
        return (
          <div key={i} style={{ background:G.bg1, border:`0.5px solid ${s.color}40`, borderRadius:12, padding:"20px 24px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:14, flexWrap:"wrap" }}>
              <div style={{ flex:1, minWidth:240 }}>
                <div style={{ fontSize:10, letterSpacing:"0.14em", color:s.color, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:6 }}>Question {i+1} · {s.label}</div>
                <div style={{ fontSize:17, fontWeight:300, color:G.text, fontFamily:"Fraunces, serif", marginBottom:8, fontStyle:"italic" }}>"{s.q}"</div>
                <div style={{ fontSize:12, color:G.muted, lineHeight:1.6 }}>{s.note}</div>
              </div>
              <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:9, letterSpacing:"0.12em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:3 }}>7-Year Net Worth</div>
                  <div style={{ fontSize:20, fontWeight:300, color:s.color, fontFamily:"Fraunces, serif" }}>{fmt(proj)}</div>
                </div>
                <div style={{ textAlign:"right", borderLeft:`1px solid ${G.border}`, paddingLeft:16 }}>
                  <div style={{ fontSize:9, letterSpacing:"0.12em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:3 }}>Time to Freedom</div>
                  <div style={{ fontSize:20, fontWeight:300, color:s.color, fontFamily:"Fraunces, serif" }}>{yrs ? `${yrs.toFixed(1)} yrs` : "—"}</div>
                  {savedYrs > 0.5 && <div style={{ fontSize:10, color:G.green, marginTop:3, fontFamily:"'JetBrains Mono', monospace" }}>↓ {savedYrs.toFixed(1)} yrs faster</div>}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div style={{ background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:12, padding:"14px 20px", fontSize:12, color:G.dim, lineHeight:1.6, textAlign:"center" }}>
        Projections assume 8% annual return and consistent monthly contributions. FI corpus = 25× annual expenses (4% safe withdrawal rule).
      </div>
    </div>
  );
};
// ─── Monthly Behavioral Report (9-section spec template) ──────────────────────
const MONTHLY_REPORT_PROMPT = `You are a Financial Behavior Intelligence Assistant.

CRITICAL RULES:
- DO NOT give financial advice. Insights are educational only.
- Focus on behavior patterns, not just numbers.
- Calm, clear, non-judgmental. 8th-grade language. No jargon.
- Instead of "you overspent" say "spending increased beyond your typical range".

OUTPUT FORMAT — Return ONLY valid JSON, no markdown, no preamble:
{
  "executive_summary": "2-3 plain sentences with the bottom line",
  "insights": [
    { "title": "Short pattern title", "whats_happening": "1-2 sentence observation", "why_it_matters": "1-2 sentence impact", "what_to_consider": "1 simple non-advisory consideration" },
    { ... }, { ... }
  ],
  "snapshot": { "income": <number>, "spending": <number>, "net_flow": <number>, "wealth_building_pct": <0-100>, "lifestyle_pct": <0-100> },
  "behavior_patterns": ["statement 1", "statement 2", "statement 3"],
  "trend_overview": { "income_trend":"Up|Down|Stable", "spending_trend":"Up|Down|Stable", "net_flow_trend":"Up|Down|Stable", "direction":"Improving|Needs Attention|Stable" },
  "scenario_snapshot": { "if_continues": "1 sentence", "if_adjusts": "1 sentence" },
  "focus_next_month": "ONE specific behavior to improve",
  "reflection_prompt": "ONE thoughtful reflection question",
  "closing_insight": "1-2 closing sentences. End with: 'Your income is not the issue. Your patterns shape your progress — and patterns can be refined.'"
}`;

const ReportSection = ({ num, title, children }) => {
  const G = useG();
  return (
    <div style={{ marginBottom:24, pageBreakInside:"avoid" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
        <div style={{ width:24, height:24, borderRadius:"50%", background:G.goldDim, border:`0.5px solid ${G.gold}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, color:G.gold, fontFamily:"'JetBrains Mono', monospace" }}>{num}</div>
        <div style={{ fontSize:14, fontWeight:500, color:G.text, letterSpacing:"0.02em" }}>{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
};

const MonthlyReport = ({ client, transactions, monthly, plan }) => {
  const G = useG();
  const { fmtN, sym } = useFmt();
  const [month, setMonth] = useState(thisMonth());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);

  if (plan < 1) return <UpgradeGate tier={plan} requiredTier={1} feature="Monthly Behavioral Report" />;

  const monthOpts = getMonthOpts();
  const patterns = detectPatterns(monthly, transactions);

  const generate = async () => {
    setError(null); setReport(null);
    const v = validateMonthData(transactions, month);
    if (!v.valid) { setError(v.errors); return; }
    setLoading(true);

    const monthTx = transactions.filter(t => t.month === month);
    const inc = monthTx.filter(t => t.type === "income").reduce((s,t)=>s+t.amount, 0);
    const exp = monthTx.filter(t => t.type === "expense").reduce((s,t)=>s+t.amount, 0);
    const inv = monthTx.filter(t => t.category === "Investments").reduce((s,t)=>s+t.amount, 0);
    const sav = monthTx.filter(t => t.category === "Savings").reduce((s,t)=>s+t.amount, 0);
    const lifeCats = ["Food & Dining","Shopping","Entertainment","Travel","Subscriptions"];
    const lifestyle = monthTx.filter(t => t.type === "expense" && lifeCats.includes(t.category)).reduce((s,t)=>s+t.amount, 0);
    const catBreak = {};
    monthTx.filter(t => t.type === "expense").forEach(t => { catBreak[t.category] = (catBreak[t.category]||0) + t.amount; });

    const patternEvidence = patterns.length
      ? patterns.map(p => `${p.name}: ${p.evidence}`).join(" | ")
      : "No strong patterns detected yet — acknowledge data is limited.";

    const userMsg = `Client: Client
Reporting Month: ${fmtMk(month)}
Currency symbol: ${sym}

THIS MONTH:
- Income: ${sym}${inc.toLocaleString()}
- Total spending: ${sym}${exp.toLocaleString()}
- Net flow: ${sym}${(inc-exp).toLocaleString()}
- Wealth building (savings + investments): ${sym}${(sav+inv).toLocaleString()} (${inc>0?((sav+inv)/inc*100).toFixed(1):0}% of income)
- Lifestyle spending (food/shopping/entertainment/travel/subs): ${sym}${lifestyle.toLocaleString()} (${inc>0?(lifestyle/inc*100).toFixed(1):0}% of income)
- Expense breakdown: ${Object.entries(catBreak).map(([c,v])=>`${c}: ${sym}${v.toLocaleString()}`).join(", ")}

DETECTED BEHAVIOR PATTERNS:
${patternEvidence}

LAST ${monthly.length} MONTHS HISTORY:
${monthly.map(m=>`${m.month}: income ${sym}${m.income.toLocaleString()}, exp ${sym}${m.expenses.toLocaleString()}, sav ${sym}${m.savings.toLocaleString()}, inv ${sym}${m.investments.toLocaleString()}`).join("\n")}

Generate the structured monthly intelligence report now as JSON.`;

    try {
      const raw = await callAI(MONTHLY_REPORT_PROMPT, userMsg);
      const clean = raw.replace(/```json|```/g, "").trim();
      setReport(JSON.parse(clean));
    } catch (e) {
      setError(["Report generation failed: " + (e.message || "Bad AI response, retry.")]);
    }
    setLoading(false);
  };

  const exportPDF = async () => {
    if (!reportRef.current) return;
    setDownloading(true);
    try {
      const html2pdf = await getHtml2Pdf();
      await html2pdf().set({
        margin: [12, 14, 12, 14],
        filename: `Financial-Report-${(client.name||"client").replace(/\s+/g,"-")}-${month}.pdf`,
        image: { type: "jpeg", quality: 0.96 },
        html2canvas: { scale: 2, backgroundColor: "#FFFFFF", useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      }).from(reportRef.current).save();
    } catch (e) { alert("PDF export failed: " + e.message); }
    setDownloading(false);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:18 }}>

      {/* Controls */}
      <div style={{ background:G.bg2, border:`0.5px solid ${G.borderGold}`, borderRadius:12, padding:"20px 24px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:14 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:300, color:G.text, fontFamily:"Fraunces, serif" }}>Monthly Behavioral Report</div>
          <div style={{ fontSize:12, color:G.muted, marginTop:4 }}>Structured 9-section intelligence report — exportable as PDF</div>
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <select value={month} onChange={e => { setMonth(e.target.value); setReport(null); setError(null); }}
            style={{ background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:8, padding:"10px 14px", color:G.text, fontSize:13, fontFamily:"'JetBrains Mono', monospace", outline:"none", cursor:"pointer" }}>
            {monthOpts.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <button onClick={generate} disabled={loading}
            style={{ padding:"10px 22px", background:loading?G.bg4:G.goldDim, border:`0.5px solid ${G.gold}`, borderRadius:8, color:G.gold, cursor:loading?"not-allowed":"pointer", fontSize:13, fontFamily:"inherit", fontWeight:500, opacity:loading?0.6:1 }}>
            {loading ? "◌  Generating…" : "⚡  Generate Report"}
          </button>
          {report && <button onClick={exportPDF} disabled={downloading}
            style={{ padding:"10px 22px", background:G.greenDim, border:`0.5px solid ${G.green}`, borderRadius:8, color:G.green, cursor:downloading?"not-allowed":"pointer", fontSize:13, fontFamily:"inherit", fontWeight:500, opacity:downloading?0.6:1 }}>
            {downloading ? "◌  Exporting…" : "📄  Export PDF"}
          </button>}
        </div>
      </div>

      {/* Validation errors */}
      {error && (
        <div style={{ background:G.redDim, border:`0.5px solid ${G.red}40`, borderRadius:12, padding:"16px 20px" }}>
          <div style={{ fontSize:11, letterSpacing:"0.14em", color:G.red, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:10 }}>⚠ Report blocked — incomplete data</div>
          {error.map((e,i) => <div key={i} style={{ fontSize:13, color:G.text, lineHeight:1.6, marginBottom:4 }}>• {e}</div>)}
          <div style={{ fontSize:11, color:G.muted, marginTop:8 }}>Add the missing entries, then retry.</div>
        </div>
      )}

      {loading && (
        <div style={{ background:G.bg2, border:`0.5px solid ${G.borderGold}`, borderRadius:12, padding:"60px 28px", textAlign:"center" }}>
          <div style={{ fontSize:12, color:G.gold, letterSpacing:"0.18em", animation:"pulse 1.5s ease-in-out infinite" }}>BUILDING BEHAVIORAL REPORT</div>
          <div style={{ fontSize:12, color:G.muted, marginTop:10 }}>Analyzing {monthly.length} months of patterns...</div>
        </div>
      )}

      {/* Report body */}
      {report && (
        <div ref={reportRef} style={{ background:G.bg1, border:`0.5px solid ${G.border}`, borderRadius:14, padding:"36px 44px", fontFamily:"Geist, sans-serif" }}>

          <div style={{ borderBottom:`1px solid ${G.borderGold}`, paddingBottom:18, marginBottom:24 }}>
            <div style={{ fontSize:10, letterSpacing:"0.22em", color:G.goldD, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:6 }}>Client Financial Intelligence Report</div>
            <div style={{ fontSize:26, fontWeight:300, color:G.text, fontFamily:"Fraunces, serif", marginBottom:4 }}>{client.name}</div>
            <div style={{ fontSize:12, color:G.muted, fontFamily:"'JetBrains Mono', monospace" }}>{fmtMk(month)}</div>
          </div>

          <ReportSection num="1" title="Executive Summary">
            <div style={{ fontSize:14, color:G.text, lineHeight:1.75 }}>{report.executive_summary}</div>
          </ReportSection>

          <ReportSection num="2" title="Your 3 Key Insights">
            {report.insights?.map((ins,i) => (
              <div key={i} style={{ background:G.bg2, border:`0.5px solid ${G.border}`, borderLeft:`3px solid ${G.gold}`, borderRadius:8, padding:"16px 20px", marginBottom:12 }}>
                <div style={{ fontSize:13, fontWeight:500, color:G.gold, marginBottom:10 }}>Insight #{i+1}: {ins.title}</div>
                {[["What's happening", ins.whats_happening], ["Why it matters", ins.why_it_matters], ["What to consider", ins.what_to_consider]].map(([lbl, val]) => (
                  <div key={lbl} style={{ marginBottom:8 }}>
                    <div style={{ fontSize:10, letterSpacing:"0.12em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:4 }}>{lbl}</div>
                    <div style={{ fontSize:13, color:G.text, lineHeight:1.6 }}>{val}</div>
                  </div>
                ))}
              </div>
            ))}
          </ReportSection>

          <ReportSection num="3" title="Financial Snapshot">
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10 }}>
              {[
                { label:"Total Income",   value:fmtN(report.snapshot?.income||0),   color:G.green },
                { label:"Total Spending", value:fmtN(report.snapshot?.spending||0), color:G.red },
                { label:"Net Cash Flow",  value:fmtN(report.snapshot?.net_flow||0), color:(report.snapshot?.net_flow||0)>=0 ? G.green : G.red },
                { label:"Wealth Building",value:`${report.snapshot?.wealth_building_pct||0}%`, color:G.gold },
                { label:"Lifestyle",      value:`${report.snapshot?.lifestyle_pct||0}%`,       color:G.blue },
              ].map(s => (
                <div key={s.label} style={{ background:G.bg2, borderRadius:8, padding:"12px 14px", border:`0.5px solid ${G.border}` }}>
                  <div style={{ fontSize:9, letterSpacing:"0.12em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:5 }}>{s.label}</div>
                  <div style={{ fontSize:15, fontWeight:500, color:s.color, fontFamily:"'JetBrains Mono', monospace" }}>{s.value}</div>
                </div>
              ))}
            </div>
          </ReportSection>

          <ReportSection num="4" title="Behavior Patterns Observed">
            {report.behavior_patterns?.map((p,i) => (
              <div key={i} style={{ display:"flex", gap:10, marginBottom:8 }}>
                <span style={{ color:G.gold, marginTop:2, flexShrink:0 }}>▸</span>
                <span style={{ fontSize:13, color:G.text, lineHeight:1.6 }}>{p}</span>
              </div>
            ))}
          </ReportSection>

          <ReportSection num="5" title="Trend Overview">
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
              {[
                { label:"Income",   value:report.trend_overview?.income_trend },
                { label:"Spending", value:report.trend_overview?.spending_trend },
                { label:"Net Flow", value:report.trend_overview?.net_flow_trend },
                { label:"Direction",value:report.trend_overview?.direction, highlight:true },
              ].map(t => {
                const c = (t.value==="Up"||t.value==="Improving") ? G.green
                        : (t.value==="Down"||t.value==="Needs Attention") ? G.red : G.muted;
                return (
                  <div key={t.label} style={{ background:t.highlight?G.goldDim:G.bg2, borderRadius:8, padding:"12px 14px", border:`0.5px solid ${t.highlight?G.borderGold:G.border}` }}>
                    <div style={{ fontSize:9, letterSpacing:"0.12em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:5 }}>{t.label}</div>
                    <div style={{ fontSize:13, fontWeight:500, color:c, fontFamily:"'JetBrains Mono', monospace" }}>{t.value||"—"}</div>
                  </div>
                );
              })}
            </div>
          </ReportSection>

          <ReportSection num="6" title="Scenario Snapshot">
            <div style={{ background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:8, padding:"14px 18px", marginBottom:10 }}>
              <div style={{ fontSize:10, letterSpacing:"0.12em", color:G.red, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:6 }}>If current behavior continues</div>
              <div style={{ fontSize:13, color:G.text, lineHeight:1.6 }}>{report.scenario_snapshot?.if_continues}</div>
            </div>
            <div style={{ background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:8, padding:"14px 18px" }}>
              <div style={{ fontSize:10, letterSpacing:"0.12em", color:G.green, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:6 }}>If small adjustments are made</div>
              <div style={{ fontSize:13, color:G.text, lineHeight:1.6 }}>{report.scenario_snapshot?.if_adjusts}</div>
            </div>
          </ReportSection>

          <ReportSection num="7" title="Focus for Next Month">
            <div style={{ background:G.goldDim, border:`0.5px solid ${G.gold}`, borderRadius:10, padding:"16px 20px" }}>
              <div style={{ fontSize:10, letterSpacing:"0.14em", color:G.goldD, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:8 }}>Primary Focus</div>
              <div style={{ fontSize:15, color:G.text, lineHeight:1.6 }}>{report.focus_next_month}</div>
            </div>
          </ReportSection>

          <ReportSection num="8" title="Reflection Prompt">
            <div style={{ padding:"14px 18px", borderLeft:`2px solid ${G.gold}` }}>
              <div style={{ fontSize:14, color:G.text, fontStyle:"italic", lineHeight:1.7, fontFamily:"Fraunces, serif" }}>"{report.reflection_prompt}"</div>
            </div>
          </ReportSection>

          <ReportSection num="9" title="Closing Insight">
            <div style={{ fontSize:13, color:G.muted, lineHeight:1.75 }}>{report.closing_insight}</div>
          </ReportSection>

          <div style={{ marginTop:30, paddingTop:18, borderTop:`0.5px solid ${G.border}`, fontSize:10, color:G.dim, fontFamily:"'JetBrains Mono', monospace", letterSpacing:"0.08em", textAlign:"center" }}>
            All insights are educational only · Not financial advice
          </div>
        </div>
      )}

      {/* Empty state — show detected patterns as preview */}
      {!report && !loading && !error && (
        <div style={{ background:G.bg2, border:`0.5px solid ${G.border}`, borderRadius:12, padding:"40px 28px" }}>
          <div style={{ textAlign:"center", marginBottom:patterns.length?28:0 }}>
            <div style={{ fontSize:30, color:G.goldD, marginBottom:14 }}>📄</div>
            <div style={{ fontSize:15, color:G.text, fontFamily:"Fraunces, serif", fontWeight:300, marginBottom:6 }}>Ready to generate this month's report</div>
            <div style={{ fontSize:12, color:G.muted }}>Select a month and click Generate.</div>
          </div>
          {patterns.length > 0 && (
            <div style={{ maxWidth:560, margin:"0 auto" }}>
              <div style={{ fontSize:10, letterSpacing:"0.14em", color:G.muted, textTransform:"uppercase", fontFamily:"'JetBrains Mono', monospace", marginBottom:10, textAlign:"center" }}>Patterns detected in your data</div>
              {patterns.map((p,i) => (
                <div key={i} style={{ display:"flex", gap:10, padding:"10px 14px", background:G.bg3, borderRadius:8, marginBottom:6, alignItems:"flex-start", borderLeft:`2px solid ${p.severity==="warning"?G.red:G.gold}` }}>
                  <span style={{ fontSize:16 }}>{p.icon}</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:500, color:G.text }}>{p.name}</div>
                    <div style={{ fontSize:11, color:G.muted, marginTop:2, lineHeight:1.5 }}>{p.evidence}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const THEATER_LINES = [
  { headline: "The vault is silent.", sub: "Feed it data. It's hungry.", glyph: "◎" },
  { headline: "Your financial story\nhasn't started yet.", sub: "Add your first income source.", glyph: "◐" },
  { headline: "Nothing here yet.", sub: "Every empire starts with one entry.", glyph: "△" },
  { headline: "The river has no water.", sub: "Log your first transaction to begin.", glyph: "≋" },
  { headline: "Silence is not a strategy.", sub: "Start tracking. Your future self will thank you.", glyph: "✦" },
];
 
const EmptyStateTheater = ({ message, sub, size = "md" }) => {
    const AURORA = useAURORA();

  const idx = Math.floor(Math.random() * THEATER_LINES.length);
  const line = THEATER_LINES[idx];
  const headline = message || line.headline;
  const subtitle = sub || line.sub;
  const isLg = size === "lg";
 
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: isLg ? "80px 40px" : "48px 28px",
      textAlign: "center", position: "relative",
    }}>
      {/* Animated glyph */}
      <div style={{
        fontSize: isLg ? 64 : 44,
        color: `${AURORA.amber}30`,
        marginBottom: 20,
        animation: "auroraPulse 3s ease-in-out infinite",
        fontFamily: "Fraunces, serif",
        lineHeight: 1,
      }}>
        {line.glyph}
      </div>
      {/* Headline */}
      <div style={{
        fontFamily: "Fraunces, 'Cormorant Garamond', serif",
        fontSize: isLg ? 28 : 20,
        fontWeight: 300,
        color: AURORA.ink,
        letterSpacing: "-0.02em",
        lineHeight: 1.35,
        marginBottom: 12,
        whiteSpace: "pre-line",
        maxWidth: 380,
      }}>
        {headline}
      </div>
      {/* Subtitle */}
      <div style={{
        fontSize: isLg ? 14 : 12,
        color: AURORA.inkFaint,
        lineHeight: 1.6,
        maxWidth: 320,
        fontStyle: "italic",
      }}>
        {subtitle}
      </div>
    </div>
  );
};

// Generates 4-6 brutal, data-anchored insight cards from real transactions.
// Each card has a severity (red/amber/green), an icon, and a share button.
 
function computeShockInsights(transactions, monthly) {
  const insights = [];
  if (!monthly.length || !transactions.length) return insights;
 
  const n = monthly.length;
  const avgInc = monthly.reduce((s, m) => s + m.income, 0) / n;
  const totals = {};
  transactions.filter(t => t.type === "expense")
    .forEach(t => { totals[t.category] = (totals[t.category] || 0) + t.amount; });
  const totalExp = Object.values(totals).reduce((s, v) => s + v, 0);
 
  // Helper
  const sym = "$"; // consumer of this uses useFmt, but for static compute we default
  const fN = v => `${sym}${Math.round(v).toLocaleString()}`;
  const pct = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;
 
  // ── Shock 1: Subscriptions > Investments
  const subs = totals["Subscriptions"] || 0;
  const inv = totals["Investments"] || 0;
  if (subs > 0 && inv === 0) {
    insights.push({
      id: "subs_no_inv",
      severity: "red",
      icon: "📱",
      headline: `You pay for ${Math.round(subs / avgInc * 100)}% of your income in subscriptions`,
      sub: "but invest nothing. Streaming services are compounding for their shareholders, not you.",
      stat: fN(subs) + "/mo",
      statLabel: "subscriptions",
    });
  } else if (subs > inv && inv > 0) {
    insights.push({
      id: "subs_gt_inv",
      severity: "red",
      icon: "⚡",
      headline: "You spend more on subscriptions than investing.",
      sub: `${fN(subs)} in subscriptions. Only ${fN(inv)} invested. That's a priority mismatch.`,
      stat: fN(subs - inv),
      statLabel: "gap per month",
    });
  }
 
  // ── Shock 2: Weekend spending spike
  const weekendTxs = transactions.filter(t => {
    if (!t.created_at) return false;
    const d = new Date(t.created_at).getDay();
    return (d === 0 || d === 6) && t.type === "expense";
  });
  const weekdayTxs = transactions.filter(t => {
    if (!t.created_at) return false;
    const d = new Date(t.created_at).getDay();
    return d > 0 && d < 6 && t.type === "expense";
  });
  if (weekendTxs.length >= 3 && weekdayTxs.length >= 5) {
    const wkendAvg = weekendTxs.reduce((s, t) => s + t.amount, 0) / weekendTxs.length;
    const wkdayAvg = weekdayTxs.reduce((s, t) => s + t.amount, 0) / weekdayTxs.length;
    const spike = Math.round(((wkendAvg - wkdayAvg) / wkdayAvg) * 100);
    if (spike > 30) {
      insights.push({
        id: "weekend_spike",
        severity: "amber",
        icon: "📅",
        headline: `Your weekend spending spikes ${spike}% above weekdays.`,
        sub: `Avg ${fN(wkendAvg)}/transaction on weekends vs ${fN(wkdayAvg)} on weekdays. Weekends are expensive.`,
        stat: `+${spike}%`,
        statLabel: "weekend spike",
      });
    }
  }
 
  // ── Shock 3: Post-salary splurge (spending surge in first 5 days)
  const earlyTxs = transactions.filter(t => {
    if (!t.created_at) return false;
    const day = new Date(t.created_at).getDate();
    return day <= 5 && t.type === "expense";
  });
  const lateTxs = transactions.filter(t => {
    if (!t.created_at) return false;
    const day = new Date(t.created_at).getDate();
    return day > 15 && t.type === "expense";
  });
  if (earlyTxs.length >= 3 && lateTxs.length >= 3) {
    const earlyAvg = earlyTxs.reduce((s, t) => s + t.amount, 0) / earlyTxs.length;
    const lateAvg = lateTxs.reduce((s, t) => s + t.amount, 0) / lateTxs.length;
    const splurge = Math.round(((earlyAvg - lateAvg) / lateAvg) * 100);
    if (splurge > 25) {
      insights.push({
        id: "salary_splurge",
        severity: "amber",
        icon: "💸",
        headline: "Your savings rate collapses after salary day.",
        sub: `You spend ${splurge}% more in the first 5 days of the month. Money burns fastest when the account looks full.`,
        stat: `+${splurge}%`,
        statLabel: "post-payday spike",
      });
    }
  }
 
  // ── Shock 4: Lifestyle inflation (rising expenses with rising income)
  if (monthly.length >= 3) {
    const first = monthly[0], last = monthly[monthly.length - 1];
    const incGrowth = first.income > 0 ? ((last.income - first.income) / first.income) * 100 : 0;
    const expGrowth = first.expenses > 0 ? ((last.expenses - first.expenses) / first.expenses) * 100 : 0;
    if (incGrowth >= 8 && expGrowth >= incGrowth * 0.8) {
      const leakPerMonth = Math.round((last.expenses - first.expenses) * 0.5);
      insights.push({
        id: "lifestyle_inflation",
        severity: "red",
        icon: "📈",
        headline: `Your future self is losing ${fN(leakPerMonth)}/mo to lifestyle inflation.`,
        sub: `Income grew ${Math.round(incGrowth)}% but expenses grew ${Math.round(expGrowth)}% — almost in lockstep. Your raise isn't building wealth.`,
        stat: fN(leakPerMonth) + "/mo",
        statLabel: "leaked to inflation",
      });
    }
  }
 
  // ── Shock 5: Food delivery addiction
  const foodTotal = (totals["Food & Dining"] || 0);
  const savTotal = (totals["Savings"] || 0) + (totals["Investments"] || 0);
  if (foodTotal > savTotal && savTotal > 0) {
    insights.push({
      id: "food_vs_savings",
      severity: "amber",
      icon: "🍽️",
      headline: "You spend more feeding yourself than building wealth.",
      sub: `${fN(foodTotal)} on food vs ${fN(savTotal)} invested or saved. One is a habit. The other is a future.`,
      stat: fN(foodTotal - savTotal),
      statLabel: "food > savings gap",
    });
  }
 
  // ── Shock 6: Positive shock — high savings discipline
  const savRate = avgInc > 0
    ? (monthly.reduce((s, m) => s + m.savings + m.investments, 0) / (n * avgInc)) * 100
    : 0;
  if (savRate >= 18) {
    insights.push({
      id: "top_saver",
      severity: "green",
      icon: "🏆",
      headline: `Your savings discipline ranks above 78% of users.`,
      sub: `A ${Math.round(savRate)}% savings rate puts you in elite territory. Most people never cross 10%.`,
      stat: `${Math.round(savRate)}%`,
      statLabel: "savings rate",
    });
  }
 
  // ── Shock 7: Annual waste projection
  const regretTxs = transactions.filter(t => t.regret !== undefined && t.regret >= 2);
  if (regretTxs.length >= 2) {
    const monthlyWaste = regretTxs.reduce((s, t) => s + t.amount, 0) /
      Math.max(1, [...new Set(regretTxs.map(t => t.month))].length);
    const annual = monthlyWaste * 12;
    insights.push({
      id: "annual_waste",
      severity: "red",
      icon: "🗑️",
      headline: `You lost ${fN(annual)} this year to regret spending.`,
      sub: "These are purchases you yourself rated as regret-worthy. That money could have been an investment.",
      stat: fN(monthlyWaste) + "/mo",
      statLabel: "avg regret spend",
    });
  }
 
  return insights.slice(0, 6);
}
 
const SEVERITY_STYLES = {
  red:   { bg: "rgba(192,90,90,0.08)",   border: "rgba(192,90,90,0.25)",  accent: "#C05A5A", badge: "rgba(192,90,90,0.15)" },
  amber: { bg: "rgba(255,181,97,0.06)",  border: "rgba(255,181,97,0.22)", accent: "#FFB561", badge: "rgba(255,181,97,0.15)" },
  green: { bg: "rgba(111,233,181,0.06)", border: "rgba(111,233,181,0.22)",accent: "#6FE9B5", badge: "rgba(111,233,181,0.15)" },
};
 
// Individual shock card — with share/screenshot support
const ShockCard = ({ insight, sym, onShare }) => {
    const AURORA = useAURORA();
  const [hov, setHov] = useState(false);
  const s = SEVERITY_STYLES[insight.severity] || SEVERITY_STYLES.amber;
  // Replace placeholder sym in strings
  const headline = insight.headline.replace(/\$/g, sym);
  const sub = insight.sub.replace(/\$/g, sym);
  const stat = insight.stat.replace(/\$/g, sym);
 
  return (
    <div
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? s.bg : "rgba(255,255,255,0.015)",
        border: `1px solid ${hov ? s.border : AURORA.line}`,
        borderRadius: 16, padding: "22px 24px",
        transition: "all 0.25s", cursor: "default",
        position: "relative", overflow: "hidden",
      }}>
      {/* Background glow on hover */}
      <div style={{
        position: "absolute", top: -40, right: -40, width: 160, height: 160,
        background: `radial-gradient(circle, ${s.accent}18, transparent 65%)`,
        pointerEvents: "none", opacity: hov ? 1 : 0, transition: "opacity 0.3s",
      }}/>
 
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", position: "relative" }}>
        {/* Icon */}
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: s.badge, border: `1px solid ${s.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20,
        }}>{insight.icon}</div>
 
        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: "Fraunces, 'Cormorant Garamond', serif",
            fontSize: 17, fontWeight: 400, color: AURORA.ink,
            letterSpacing: "-0.01em", lineHeight: 1.35, marginBottom: 8,
          }}>{headline}</div>
          <div style={{ fontSize: 12, color: AURORA.inkSoft, lineHeight: 1.65 }}>{sub}</div>
        </div>
 
        {/* Stat badge */}
        <div style={{
          flexShrink: 0, textAlign: "center", padding: "8px 14px",
          background: s.badge, border: `1px solid ${s.border}`,
          borderRadius: 10,
        }}>
          <div style={{
            fontSize: 18, fontWeight: 300, color: s.accent,
            fontFamily: "Fraunces, 'Cormorant Garamond', serif",
            letterSpacing: "-0.02em", lineHeight: 1,
          }}>{stat}</div>
          <div style={{
            fontSize: 9, color: AURORA.inkFaint, marginTop: 4,
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace",
            letterSpacing: "0.12em", textTransform: "uppercase",
          }}>{insight.statLabel}</div>
        </div>
      </div>
 
      {/* Share button — appears on hover */}
      <div style={{
        display: "flex", gap: 8, marginTop: 16,
        opacity: hov ? 1 : 0, transform: hov ? "translateY(0)" : "translateY(4px)",
        transition: "all 0.2s",
      }}>
        {["Instagram", "X (Twitter)"].map(platform => (
          <button key={platform} onClick={() => onShare(insight, platform)}
            style={{
              padding: "5px 14px", fontSize: 10, cursor: "pointer",
              background: "transparent", border: `1px solid ${s.border}`,
              borderRadius: 20, color: s.accent,
              fontFamily: "'JetBrains Mono', 'DM Mono', monospace",
              letterSpacing: "0.1em", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = s.badge; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
            Share → {platform}
          </button>
        ))}
        <button onClick={() => onShare(insight, "copy")}
          style={{
            padding: "5px 14px", fontSize: 10, cursor: "pointer",
            background: "transparent", border: `1px solid ${AURORA.line}`,
            borderRadius: 20, color: AURORA.inkFaint,
            fontFamily: "'JetBrains Mono', 'DM Mono', monospace",
            letterSpacing: "0.1em",
          }}>
          Copy text
        </button>
      </div>
    </div>
  );
};
 
// Full shock insights panel
const ShockInsightsPanel = ({ transactions, monthly, currency }) => {
      const AURORA = useAURORA();

  const sym = currency?.symbol || "$";
  const insights = computeShockInsights(transactions, monthly);
 
  const handleShare = (insight, platform) => {
    const headline = insight.headline.replace(/\$/g, sym);
    const text = `${headline}\n\n${insight.sub.replace(/\$/g, sym)}\n\nTracked with Purely Automated — purelyautomated.com`;
    if (platform === "X (Twitter)") {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
    } else if (platform === "Instagram") {
      navigator.clipboard?.writeText(text).then(() =>
        alert("Caption copied! Open Instagram and paste it with your screenshot.")
      );
    } else {
      navigator.clipboard?.writeText(text).then(() =>
        alert("Insight copied to clipboard!")
      );
    }
  };
 
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Header */}
      <div style={{ marginBottom: 4 }}>
        <div style={{
          fontSize: 9, letterSpacing: "0.28em", color: AURORA.amberD,
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 6,
        }}>⚡ SHOCK INSIGHTS</div>
        <div style={{
          fontFamily: "Fraunces, 'Cormorant Garamond', serif",
          fontSize: 26, fontWeight: 300, color: AURORA.ink,
          letterSpacing: "-0.02em", lineHeight: 1.2,
        }}>
          What your data is trying to{" "}
          <span style={{ fontStyle: "italic", color: AURORA.amber }}>tell you</span>
        </div>
      </div>
 
      {insights.length === 0 ? (
        <EmptyStateTheater
          message={"Insights emerge from patterns.\nPatterns need data."}
          sub="Add 2+ months of transactions to unlock your shock insights."
        />
      ) : (
        insights.map(ins => (
          <ShockCard key={ins.id} insight={ins} sym={sym} onShare={handleShare} />
        ))
      )}
    </div>
  );
};
 
 
// ─── 3. VIRAL SHARE CARD GENERATOR ───────────────────────────────────────────
// Generates a beautiful canvas-rendered card for sharing.
// Uses the Canvas API — no external deps.
 
const generateShareCardCanvas = (text, subtext, score, accentColor = "#FFB561") => {
  const canvas = document.createElement("canvas");
  canvas.width = 1080; canvas.height = 1080;
  const ctx = canvas.getContext("2d");
 
  // Background
  const bg = ctx.createLinearGradient(0, 0, 1080, 1080);
  bg.addColorStop(0, "#08070C");
  bg.addColorStop(0.5, "#0E0C14");
  bg.addColorStop(1, "#08070C");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, 1080, 1080);
 
  // Radial glow
  const glow = ctx.createRadialGradient(540, 380, 0, 540, 380, 500);
  glow.addColorStop(0, `${accentColor}22`);
  glow.addColorStop(1, "transparent");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, 1080, 1080);
 
  // Border
  ctx.strokeStyle = `${accentColor}30`;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(32, 32, 1016, 1016);
 
  // Brand
  ctx.fillStyle = `${accentColor}80`;
  ctx.font = "500 22px 'DM Mono', monospace";
  ctx.letterSpacing = "6px";
  ctx.fillText("UNBROKE", 64, 100);
 
  // Main text — word wrap
  ctx.fillStyle = "#F5EFE6";
  ctx.font = "300 72px 'Cormorant Garamond', Georgia, serif";
  ctx.letterSpacing = "-2px";
  const words = text.split(" ");
  let line = "", y = 380;
  const maxW = 900;
  for (const word of words) {
    const test = line + (line ? " " : "") + word;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, 64, y);
      line = word; y += 88;
    } else { line = test; }
  }
  if (line) ctx.fillText(line, 64, y);
 
  // Subtext
  ctx.fillStyle = "#B8B0A8";
  ctx.font = "300 32px 'Outfit', sans-serif";
  ctx.letterSpacing = "0px";
  const subY = y + 64;
  const subWords = subtext.split(" ");
  let subLine = "", subLineY = subY;
  for (const word of subWords) {
    const test = subLine + (subLine ? " " : "") + word;
    if (ctx.measureText(test).width > 900 && subLine) {
      ctx.fillText(subLine, 64, subLineY);
      subLine = word; subLineY += 44;
    } else { subLine = test; }
  }
  if (subLine) ctx.fillText(subLine, 64, subLineY);
 
  // Score pill
  if (score) {
    ctx.fillStyle = `${accentColor}18`;
    ctx.beginPath();
    ctx.roundRect(64, subLineY + 56, 220, 68, 34);
    ctx.fill();
    ctx.strokeStyle = `${accentColor}50`;
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = accentColor;
    ctx.font = "500 28px 'DM Mono', monospace";
    ctx.letterSpacing = "2px";
    ctx.fillText(score, 84, subLineY + 100);
  }
 
  // Bottom watermark
  ctx.fillStyle = "#2E2A38";
  ctx.font = "400 20px 'DM Mono', monospace";
  ctx.letterSpacing = "3px";
  ctx.fillText("purelyautomated.com", 64, 1016);
 
  return canvas.toDataURL("image/png");
};
 
const ViralShareCard = ({ client, scores, monthly, transactions, currency }) => {
      const AURORA = useAURORA();

  const sym = currency?.symbol || "$";
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
 
  const n = monthly.length;
  const avgInc = n > 0 ? monthly.reduce((s, m) => s + m.income, 0) / n : 0;
  const savRate = avgInc > 0
    ? Math.round((monthly.reduce((s, m) => s + m.savings + m.investments, 0) / (n * avgInc)) * 100)
    : 0;
  const overall = scores.length > 0
    ? Math.round(scores.reduce((s, sc) => s + sc.score, 0) / scores.length)
    : 0;
 
  // Compute regret waste
  const regretWaste = transactions.filter(t => t.regret !== undefined && t.regret >= 2)
    .reduce((s, t) => s + t.amount, 0);
  const annualWaste = Math.round(regretWaste * (12 / Math.max(n, 1)));
 
  const CARD_TEMPLATES = [
    {
      id: "savings_rank",
      label: "Savings Rank",
      headline: `My savings discipline ranks higher than ${Math.min(95, Math.max(30, savRate * 3))}% of people.`,
      sub: `${savRate}% savings rate. Most people never cross 10%.`,
      score: `${savRate}% SAVINGS`,
      accent: "#6FE9B5",
      available: savRate > 0,
    },
    {
      id: "score_flex",
      label: "Intelligence Score",
      headline: `My Behavioral Intelligence Score is ${overall}/100.`,
      sub: "Tracked every rupee. Understood every pattern. Built the discipline.",
      score: `${overall} / 100`,
      accent: "#FFB561",
      available: overall > 0,
    },
    {
      id: "waste_reveal",
      label: "Waste Reveal",
      headline: `I discovered I lost ${sym}${annualWaste.toLocaleString()} this year to impulse spending.`,
      sub: "Now I know. And knowing changes everything.",
      score: `${sym}${annualWaste.toLocaleString()} / YEAR`,
      accent: "#FF8E7C",
      available: annualWaste > 0,
    },
    {
      id: "wealth_journey",
      label: "Wealth Journey",
      headline: `${n} months of financial clarity. The numbers don't lie.`,
      sub: `${client?.name || "I"} chose data over denial. Now I see every rupee.`,
      score: `${n} MONTHS`,
      accent: "#B098FF",
      available: n > 0,
    },
  ].filter(c => c.available);
 
  const generate = (card) => {
    setGenerating(true);
    setSelectedCard(card);
    setTimeout(() => {
      try {
        const img = generateShareCardCanvas(card.headline, card.sub, card.score, card.accent);
        setPreview(img);
      } catch (e) {
        alert("Card generation failed: " + e.message);
      }
      setGenerating(false);
    }, 100);
  };
 
  const download = () => {
    if (!preview) return;
    const a = document.createElement("a");
    a.href = preview;
    a.download = `purely-${selectedCard?.id || "card"}.png`;
    a.click();
  };
 
  const shareToX = () => {
    if (!selectedCard) return;
    const text = `${selectedCard.headline}\n\n${selectedCard.sub}\n\n#PurelyAutomated #PersonalFinance`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
  };
 
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <div style={{
          fontSize: 9, letterSpacing: "0.28em", color: AURORA.amberD,
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 6,
        }}>✦ SHARE YOUR STORY</div>
        <div style={{
          fontFamily: "Fraunces, 'Cormorant Garamond', serif",
          fontSize: 24, fontWeight: 300, color: AURORA.ink, letterSpacing: "-0.02em",
        }}>
          Turn your data into{" "}
          <span style={{ fontStyle: "italic", color: AURORA.amber }}>content</span>
        </div>
        <div style={{ fontSize: 12, color: AURORA.inkFaint, marginTop: 6 }}>
          Beautiful cards generated from your actual numbers. Download and share.
        </div>
      </div>
 
      {/* Card picker */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        {CARD_TEMPLATES.length === 0 ? (
          <div style={{ gridColumn: "1/-1" }}>
            <EmptyStateTheater
              message="Your story needs data first."
              sub="Add transactions to generate shareable insight cards."
            />
          </div>
        ) : CARD_TEMPLATES.map(card => (
          <button key={card.id} onClick={() => generate(card)}
            style={{
              padding: "16px 18px", background: selectedCard?.id === card.id
                ? `${card.accent}12` : "rgba(255,255,255,0.02)",
              border: `1px solid ${selectedCard?.id === card.id ? card.accent + "50" : AURORA.line}`,
              borderRadius: 12, cursor: "pointer", textAlign: "left",
              transition: "all 0.18s", fontFamily: "Geist, Geist, sans-serif",
            }}>
            <div style={{
              fontSize: 9, letterSpacing: "0.18em", color: card.accent,
              fontFamily: "'JetBrains Mono', monospace", marginBottom: 8,
              textTransform: "uppercase",
            }}>{card.label}</div>
            <div style={{
              fontSize: 13, color: AURORA.ink, lineHeight: 1.45,
              fontFamily: "Fraunces, serif", fontWeight: 300, fontStyle: "italic",
            }}>"{card.headline.slice(0, 80)}{card.headline.length > 80 ? "…" : ""}"</div>
          </button>
        ))}
      </div>
 
      {/* Preview */}
      {generating && (
        <div style={{
          padding: "40px", textAlign: "center",
          color: AURORA.inkFaint, fontSize: 11,
          fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.14em",
          animation: "auroraPulse 1.2s ease-in-out infinite",
        }}>RENDERING CARD…</div>
      )}
 
      {preview && !generating && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <img src={preview} alt="Share card preview"
            style={{ width: "100%", borderRadius: 14, border: `1px solid ${AURORA.line}` }}/>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={download} style={{
              flex: 1, padding: "11px",
              background: `${AURORA.amber}12`,
              border: `1px solid ${AURORA.amber}40`,
              borderRadius: 9, color: AURORA.amber, fontSize: 12,
              cursor: "pointer", fontFamily: "inherit", fontWeight: 500,
            }}>⬇ Download PNG</button>
            <button onClick={shareToX} style={{
              flex: 1, padding: "11px",
              background: "rgba(255,255,255,0.04)",
              border: `1px solid ${AURORA.line}`,
              borderRadius: 9, color: AURORA.inkSoft, fontSize: 12,
              cursor: "pointer", fontFamily: "inherit",
            }}>Share to X →</button>
          </div>
        </div>
      )}
    </div>
  );
};
 
// Anonymous cohort benchmarking. Benchmarks are realistic static ranges
// (no real user data needed — these reflect actual population statistics).
 
const BENCHMARKS = {
  savingsRate: [
    { label: "Bottom 25%",  pct: 0,   color: "#C05A5A" },
    { label: "Average",      pct: 9,   color: "#E8894A" },
    { label: "Top 25%",      pct: 18,  color: "#C9A84C" },
    { label: "Top 10%",      pct: 30,  color: "#4EAD8A" },
    { label: "Top 5%",       pct: 40,  color: "#6FE9B5" },
  ],
  investRate: [
    { label: "Bottom 25%",  pct: 0,   color: "#C05A5A" },
    { label: "Average",      pct: 5,   color: "#E8894A" },
    { label: "Top 25%",      pct: 12,  color: "#C9A84C" },
    { label: "Top 10%",      pct: 20,  color: "#4EAD8A" },
    { label: "Top 5%",       pct: 30,  color: "#6FE9B5" },
  ],
  expenseRatio: [
    { label: "Overspenders",  pct: 90,  color: "#C05A5A" },
    { label: "Average",        pct: 70,  color: "#E8894A" },
    { label: "Disciplined",    pct: 55,  color: "#C9A84C" },
    { label: "Top 25%",        pct: 45,  color: "#4EAD8A" },
    { label: "Top 10%",        pct: 35,  color: "#6FE9B5" },
  ],
  consistency: [
    { label: "Sporadic",     months: 1,  color: "#C05A5A" },
    { label: "Average",      months: 3,  color: "#E8894A" },
    { label: "Dedicated",    months: 6,  color: "#C9A84C" },
    { label: "Top 10%",      months: 12, color: "#4EAD8A" },
  ],
};
 
const getRank = (benchmarks, userVal, key) => {
  const arr = [...benchmarks].reverse();
  for (const b of arr) {
    const bVal = b.pct !== undefined ? b.pct : b.months;
    if (key === "expenseRatio") {
      if (userVal <= bVal) return b;
    } else {
      if (userVal >= bVal) return b;
    }
  }
  return benchmarks[0];
};
 
const BenchmarkBar = ({ label, userVal, benchmarks, unit = "%", invert = false }) => {
      const AURORA = useAURORA();
  const [animated, setAnimated] = useState(false);
  useEffect(() => { setTimeout(() => setAnimated(true), 200); }, []);
 
  const maxVal = Math.max(...benchmarks.map(b => b.pct || b.months || 0), userVal, 1);
  const rank = getRank(benchmarks, userVal, invert ? "expenseRatio" : "normal");
  const userPct = Math.min(100, (userVal / maxVal) * 100);
 
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: AURORA.inkSoft }}>{label}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            padding: "2px 10px", background: `${rank.color}18`,
            border: `1px solid ${rank.color}40`,
            borderRadius: 20, fontSize: 10, color: rank.color,
            fontFamily: "'JetBrains Mono', monospace",
          }}>{rank.label}</div>
          <div style={{
            fontSize: 18, fontWeight: 300, color: AURORA.ink,
            fontFamily: "Fraunces, serif",
          }}>{userVal}{unit}</div>
        </div>
      </div>
 
      {/* Track */}
      <div style={{ position: "relative", height: 10, background: AURORA.bg4, borderRadius: 100 }}>
        {/* Benchmark ticks */}
        {benchmarks.map((b, i) => {
          const tickPct = Math.min(100, ((b.pct || b.months || 0) / maxVal) * 100);
          return (
            <div key={i} title={`${b.label}: ${b.pct || b.months}${unit}`}
              style={{
                position: "absolute", left: `${tickPct}%`, top: -3,
                width: 2, height: 16, background: `${b.color}60`,
                transform: "translateX(-50%)", borderRadius: 1,
              }}/>
          );
        })}
        {/* User bar */}
        <div style={{
          height: "100%", borderRadius: 100,
          width: animated ? `${userPct}%` : "0%",
          background: `linear-gradient(90deg, ${rank.color}66, ${rank.color})`,
          transition: "width 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: `0 0 12px ${rank.color}60`,
        }}/>
      </div>
 
      {/* Legend */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
        {benchmarks.map((b, i) => (
          <div key={i} style={{ fontSize: 9, color: AURORA.inkFaint,
            fontFamily: "'JetBrains Mono', monospace", textAlign: "center" }}>
            <div style={{ color: b.color }}>{b.pct !== undefined ? b.pct : b.months}{unit}</div>
            <div style={{ fontSize: 8 }}>{b.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
 
const CompareYourselfPanel = ({ monthly, transactions, currency }) => {
      const AURORA = useAURORA();

  const sym = currency?.symbol || "$";
  const n = Math.max(monthly.length, 1);
  const avgInc = monthly.reduce((s, m) => s + m.income, 0) / n;
  const avgSav = monthly.reduce((s, m) => s + m.savings, 0) / n;
  const avgInv = monthly.reduce((s, m) => s + m.investments, 0) / n;
  const avgExp = monthly.reduce((s, m) => s + m.expenses, 0) / n;
 
  const savRate = avgInc > 0 ? Math.round(((avgSav) / avgInc) * 100) : 0;
  const invRate = avgInc > 0 ? Math.round((avgInv / avgInc) * 100) : 0;
  const expRatio = avgInc > 0 ? Math.round((avgExp / avgInc) * 100) : 0;
  const months = monthly.length;
 
  // Top 10% comparison text
  const top10InvRate = 20;
  const invMultiple = invRate > 0 ? (top10InvRate / invRate).toFixed(1) : null;
 
  const callouts = [];
  if (invMultiple && parseFloat(invMultiple) > 1.2) {
    callouts.push(`Top 10% users invest ${invMultiple}× more consistently than you.`);
  }
  if (savRate < 9) {
    callouts.push(`People your income bracket save ~18% on average. You save ${savRate}%.`);
  } else if (savRate >= 18) {
    callouts.push(`You save more than the average person your age. Most save ~9%.`);
  }
  if (expRatio > 80) {
    callouts.push(`Your expense ratio (${expRatio}%) exceeds 75% of tracked users.`);
  }
 
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Header */}
      <div>
        <div style={{
          fontSize: 9, letterSpacing: "0.28em", color: AURORA.amberD,
          fontFamily: "'JetBrains Mono', monospace", marginBottom: 6,
        }}>◎ COMPARE YOURSELF</div>
        <div style={{
          fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 300,
          color: AURORA.ink, letterSpacing: "-0.02em",
        }}>
          Where do you stand{" "}
          <span style={{ fontStyle: "italic", color: AURORA.amber }}>among peers?</span>
        </div>
        <div style={{ fontSize: 11, color: AURORA.inkFaint, marginTop: 6, lineHeight: 1.6 }}>
          Anonymous benchmarks based on global personal finance data.
          No individual data is shared.
        </div>
      </div>
 
      {/* Callout pills */}
      {callouts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {callouts.map((c, i) => (
            <div key={i} style={{
              padding: "11px 16px",
              background: "rgba(255,181,97,0.06)",
              border: "1px solid rgba(255,181,97,0.2)",
              borderRadius: 10, fontSize: 13, color: AURORA.ink,
              fontFamily: "Fraunces, serif", fontWeight: 300, fontStyle: "italic",
              lineHeight: 1.5,
            }}>
              "{c}"
            </div>
          ))}
        </div>
      )}
 
      {/* Benchmark bars */}
      {monthly.length === 0 ? (
        <EmptyStateTheater
          message="Benchmarks need your numbers."
          sub="Add income and expenses to see how you compare."
        />
      ) : (
        <Glass padding="24px 26px" accent={AURORA.violet}>
          <BenchmarkBar
            label="Savings Rate"
            userVal={savRate}
            benchmarks={BENCHMARKS.savingsRate}
            unit="%"
          />
          <BenchmarkBar
            label="Investment Rate"
            userVal={invRate}
            benchmarks={BENCHMARKS.investRate}
            unit="%"
          />
          <BenchmarkBar
            label="Expense Ratio (lower = better)"
            userVal={expRatio}
            benchmarks={BENCHMARKS.expenseRatio}
            unit="%"
            invert
          />
          <BenchmarkBar
            label="Tracking Consistency"
            userVal={months}
            benchmarks={BENCHMARKS.consistency}
            unit=" mo"
          />
          
        </Glass>
      )}
 
      {/* Anonymous note */}
      <div style={{
        fontSize: 10, color: AURORA.inkGhost || AURORA.inkFaint,
        textAlign: "center", fontStyle: "italic",
        fontFamily: "Fraunces, serif",
      }}>
        Benchmarks are population-level averages, not individual user data.
        All comparisons are anonymous and educational.
      </div>
    </div>
  );
};
 
 
// ─── 5. ENHANCED MONEY PERSONALITY SHARE CARD ────────────────────────────────
// Adds a "Share my type" button to existing MoneyPersonality view.
// Generates a styled canvas card showing archetype + tagline.
 
const PersonalityShareCard = ({ archetype, confidence, scores, clientName }) => {
      const AURORA = useAURORA();

  const [preview, setPreview] = useState(null);
  const [gen, setGen] = useState(false);
 
  const ARCH_ACCENTS = {
    guardian: "#5A8EC0", achiever: "#C9A84C",
    explorer: "#4EAD8A", architect: "#9B7ECF",
  };
 
  const generate = () => {
    setGen(true);
    setTimeout(() => {
      try {
        const arch = archetype;
        const accent = ARCH_ACCENTS[arch.type] || AURORA.amber;
        const img = generateShareCardCanvas(
          `I'm a ${arch.name}.`,
          `"${arch.tagline}" — ${confidence}% match based on my actual spending data.`,
          arch.emoji + " " + arch.name.toUpperCase(),
          accent,
        );
        setPreview(img);
      } catch (e) { console.error(e); }
      setGen(false);
    }, 100);
  };
 
  if (preview) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <img src={preview} alt="Personality card"
        style={{ width: "100%", borderRadius: 12, border: `1px solid ${AURORA.line}` }}/>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => {
          const a = document.createElement("a");
          a.href = preview; a.download = "my-money-type.png"; a.click();
        }} style={{
          flex: 1, padding: "10px",
          background: `${AURORA.amber}12`,
          border: `1px solid ${AURORA.amber}40`,
          borderRadius: 8, color: AURORA.amber, fontSize: 12,
          cursor: "pointer", fontFamily: "inherit",
        }}>⬇ Download</button>
        <button onClick={() => {
          const text = `I'm a ${archetype.name}. "${archetype.tagline}"\n\nDiscover your money personality at purelyautomated.com`;
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
        }} style={{
          flex: 1, padding: "10px",
          background: "transparent", border: `1px solid ${AURORA.line}`,
          borderRadius: 8, color: AURORA.inkSoft, fontSize: 12,
          cursor: "pointer", fontFamily: "inherit",
        }}>Share to X</button>
        <button onClick={() => setPreview(null)} style={{
          padding: "10px 14px", background: "transparent",
          border: `1px solid ${AURORA.line}`,
          borderRadius: 8, color: AURORA.inkFaint, fontSize: 12,
          cursor: "pointer",
        }}>✕</button>
      </div>
    </div>
  );
 
  return (
    <button onClick={generate} disabled={gen}
      style={{
        width: "100%", padding: "12px",
        background: "transparent",
        border: `1px solid ${AURORA.line}`,
        borderRadius: 10, cursor: gen ? "wait" : "pointer",
        color: AURORA.inkSoft, fontSize: 12,
        fontFamily: "inherit", transition: "all 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = AURORA.amber + "60"; e.currentTarget.style.color = AURORA.amber; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = AURORA.line; e.currentTarget.style.color = AURORA.inkSoft; }}>
      {gen ? "Generating…" : "✦ Generate shareable personality card →"}
    </button>
  );
};
 
const PrivacyDashboard = ({ client, transactions, exportData, handleDeleteAccount, isDemo }) => {
  const AURORA = useAURORA();
  return (
  <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

    <div style={{ fontFamily:"Fraunces, 'Cormorant Garamond', serif", fontSize:28,
      fontWeight:300, color:AURORA.ink, letterSpacing:"-0.02em", marginBottom:4 }}>
      Privacy & <span style={{ fontStyle:"italic", color:AURORA.amber }}>Data</span>
    </div>

    {/* What we store */}
    <Glass padding="24px 28px">
      <div style={{ fontSize:9, letterSpacing:"0.22em", color:AURORA.amberD,
        fontFamily:"'JetBrains Mono', 'DM Mono', monospace", marginBottom:12 }}>
        WHAT WE STORE
      </div>
      {[
        { item:"Transactions (amount, category, note, month)", where:"Supabase — encrypted at rest" },
        { item:"Email address", where:"Supabase Auth only" },
        { item:"AI chat history", where:"Not stored — session memory only" },
        { item:"Bank statement PDFs", where:"Never stored — parsed in browser, discarded" },
        { item:"Your name in AI prompts", where:"Stripped before sending — anonymized" },
      ].map(({ item, where }) => (
        <div key={item} style={{ display:"flex", justifyContent:"space-between",
          padding:"10px 0", borderBottom:`1px solid ${AURORA.line}`,
          fontSize:12, gap:20, alignItems:"baseline" }}>
          <span style={{ color:AURORA.ink }}>{item}</span>
          <span style={{ color:AURORA.inkFaint, fontFamily:"'JetBrains Mono', monospace",
            fontSize:10, textAlign:"right", flexShrink:0 }}>{where}</span>
        </div>
      ))}
    </Glass>

    {/* What we DON'T do */}
    <Glass padding="24px 28px" accent={AURORA.mint}>
      <div style={{ fontSize:9, letterSpacing:"0.22em", color:AURORA.amberD,
        fontFamily:"'JetBrains Mono', 'DM Mono', monospace", marginBottom:12 }}>
        WHAT WE DON'T DO
      </div>
      {[
        "Sell or share your data with any third party",
        "Use your financial data to train AI models",
        "Track you across other websites",
        "Store your bank passwords or credentials",
        "Run ads based on your spending patterns",
      ].map(item => (
        <div key={item} style={{ display:"flex", gap:10, padding:"8px 0",
          borderBottom:`1px solid ${AURORA.line}`, fontSize:12, color:AURORA.ink }}>
          <span style={{ color:AURORA.mint, flexShrink:0 }}>✓</span>
          <span>{item}</span>
        </div>
      ))}
    </Glass>

    {/* Footprint */}
    <Glass padding="20px 24px">
      <div style={{ fontSize:9, letterSpacing:"0.22em", color:AURORA.amberD,
        fontFamily:"'JetBrains Mono', 'DM Mono', monospace", marginBottom:12 }}>
        YOUR DATA FOOTPRINT
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16 }}>
        {[
          { label:"Transactions stored", value:transactions.length },
          { label:"Account created", value:client?.createdAt?.slice(0,10) || "—" },
          { label:"Third-party trackers", value:"0" },
        ].map(s => (
          <div key={s.label}>
            <div style={{ fontSize:28, fontWeight:300, color:AURORA.ink,
              fontFamily:"Fraunces, serif" }}>{s.value}</div>
            <div style={{ fontSize:10, color:AURORA.inkFaint, marginTop:4 }}>{s.label}</div>
          </div>
        ))}
      </div>
    </Glass>

    {/* Export + Delete */}
    <Glass padding="20px 24px" accent={AURORA.coral}>
      <div style={{ fontSize:9, letterSpacing:"0.22em", color:AURORA.amberD,
        fontFamily:"'JetBrains Mono', 'DM Mono', monospace", marginBottom:12 }}>
        YOUR RIGHTS
      </div>
      <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
        <button onClick={() => exportData(transactions, "csv")}
          style={{ padding:"10px 20px", background:`${AURORA.mint}12`,
            border:`1px solid ${AURORA.mint}40`, borderRadius:9,
            color:AURORA.mint, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
          ⬇ Export all data (CSV)
        </button>
        <button onClick={() => exportData(transactions, "json")}
          style={{ padding:"10px 20px", background:`${AURORA.mint}12`,
            border:`1px solid ${AURORA.mint}40`, borderRadius:9,
            color:AURORA.mint, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
          ⬇ Export all data (JSON)
        </button>
        {!isDemo && (
          <button onClick={handleDeleteAccount}
            style={{ padding:"10px 20px", background:`${AURORA.coral}12`,
              border:`1px solid ${AURORA.coral}40`, borderRadius:9,
              color:AURORA.coral, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>
            ✕ Delete my account + all data
          </button>
        )}
      </div>
    </Glass>
    
  </div>
    );
};
// ─── Financial Age Card ───────────────────────────────────────────────────────
const FinancialAgeCard = ({ financialAge }) => {
  const AURORA = useAURORA();
  return (
    <div style={{
      display:"grid",
      gridTemplateColumns:"repeat(auto-fit,minmax(320px,1fr))",
      gap:24,
      marginTop:28,
      marginBottom:28,
    }}>
      <Glass glow>
        <div style={{
          fontSize:12,
          letterSpacing:"0.2em",
          color: AURORA.inkFaint,
          fontFamily:"'JetBrains Mono', 'DM Mono', monospace",
        }}>
          FINANCIAL AGE
        </div>
        <div style={{
          fontSize:72,
          fontWeight:300,
          marginTop:10,
          color: AURORA.amber,
          fontFamily:"Fraunces, serif",
          letterSpacing:"-0.04em",
          lineHeight:1,
        }}>
          {financialAge}
        </div>
        <div style={{
          color: AURORA.inkSoft,
          marginTop:10,
          lineHeight:1.6,
          fontSize:13,
        }}>
          Your money habits resemble a disciplined {financialAge}-year-old investor.
        </div>
      </Glass>
    </div>
  );
};

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [screen, setScreen] = useState("landing"); // landing | register | login | app
  const [client, setClient] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [view, setView] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [themeKey, setThemeKey] = useState("unbroke");
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [showSettings, setShowSettings] = useState(false);
  const [debts, setDebts] = useState([]);
  const initialized = useRef(false);

  const theme = THEMES[themeKey] || THEMES.unbroke;
  const T = theme;
  const AURORA = {
  bg0: T.au_bg0, bg1: T.au_bg1, bg2: T.au_bg2, bg3: T.au_bg3, bg4: T.au_bg4,
  line: T.au_line, lineStrong: T.au_lineStrong,
  ink: T.au_ink, inkSoft: T.au_inkSoft, inkFaint: T.au_inkFaint, inkGhost: T.au_inkGhost,
  amber: T.au_amber, amberD: T.au_amberD, amberL: T.au_amberL,
  mint: T.au_mint, mintD: T.au_mintD,
  coral: T.au_coral, coralD: T.au_coralD,
  cyan: T.au_cyan, cyanD: T.au_cyanD,
  violet: T.au_violet, violetD: T.au_violetD,
};
  const currency = CURRENCIES.find(c => c.code === currencyCode) || CURRENCIES[0];

  // Inject fonts + CSS
  useEffect(()=>{
    if (!initialized.current) {
      const s = document.createElement("style");
      s.textContent = `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300&family=DM+Mono:wght@300;400;500&family=Outfit:wght@300;400;500;600&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;1,9..144,300;1,9..144,400&family=Geist:wght@300;400;500;600&family=JetBrains+Mono:wght@300;400;500&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; background: transparent; }
        ::-webkit-scrollbar-thumb { background: #2A2438; border-radius: 100px; }
        @keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes auroraSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes auroraPulse { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
        @keyframes auroraFloat { 0% { transform: translate(0,0); } 100% { transform: translate(-30px, 20px); } }
        @keyframes auroraShift { 0% { transform: scale(1) rotate(0deg); } 100% { transform: scale(1.08) rotate(2deg); } }
        @keyframes auroraRise { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        input[type=number]::-webkit-outer-spin-button,input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        select option { background: #131318; color: #EDEDE6; }
      `;
      document.head.appendChild(s);
      // Load persisted prefs
      const savedTheme = localStorage.getItem("pa_theme");
      const savedCurrency = localStorage.getItem("pa_currency");
      if (savedTheme && THEMES[savedTheme]) setThemeKey(savedTheme);
      if (savedCurrency && CURRENCIES.find(c=>c.code===savedCurrency)) setCurrencyCode(savedCurrency);
      const savedSession = localStorage.getItem("pa_session");
      if (savedSession) {
        try {
          const user = JSON.parse(savedSession);
          if (user?.clientId) handleLogin(user);
        } catch {}
      }
      initialized.current = true;
    }
  },[]);

  // Handle Google OAuth redirect — fires once on SIGNED_IN, guarded against duplicate runs
  const oauthProcessing = useRef(false);
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session?.user && screen !== "app" && !oauthProcessing.current) {
        oauthProcessing.current = true;
        try {
          const user = await findOrCreateUserByEmail(
            session.user.email,
            session.user.user_metadata?.full_name || session.user.user_metadata?.name
          );
          if (user) handleLogin(user);
        } finally {
          setTimeout(() => { oauthProcessing.current = false; }, 3000);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [screen]);

  useEffect(()=>{ localStorage.setItem("pa_theme", themeKey); }, [themeKey]);
  useEffect(()=>{ localStorage.setItem("pa_currency", currencyCode); }, [currencyCode]);

  // ── Demo & Free-tier helpers (declared early so handlers can close over them)
  const isDemo = !!client?.isDemo;

  // Load transactions after login — from Supabase
const handleLogin = async (user) => {
  localStorage.setItem("pa_session", JSON.stringify(user));
  setClient(user); setLoading(true);
  const [txs, dbs] = await Promise.all([
  sbGetTxs(user.clientId),
  sbGetDebts(user.clientId)  // ← was missing
]);
  setTransactions(txs);
  setDebts(dbs);
  setScreen("app"); setLoading(false);
  setView("dashboard");
};

  // Demo entry — instant logged-in app with sample data; zero Supabase calls
  const handleStartDemo = () => {
    setClient(DEMO_CLIENT);
    setTransactions(generateDemoData());
    setDebts([]);
    setScreen("app");
    setView("dashboard");
  };

  const handleAddTx = async (tx) => {
    // Free tier — 50/month cap (skipped in demo, since demo is plan 3)
    const userPlan = client?.plan ?? 0;
    if (userPlan === 0 && !isDemo) {
      const thisM = thisMonth();
      const cnt = transactions.filter(t => t.month === thisM).length;
      if (cnt >= 100) {
        alert("Free plan caps at 100 transactions/month. Upgrade to keep tracking — your existing data stays safe.");
        return;
      }
    }
    const txWithClient = { ...tx, client_id: client.clientId };
    setTransactions(prev => [...prev, txWithClient]);
    if (!isDemo) await sbInsertTx(txWithClient);
  };

  const handleDeleteTx = async (id) => {
    setTransactions(prev => prev.filter(t => t.id !== id));
    if (!isDemo) await sbDeleteTx(id);
  };

  const handleUpdateTx = async (id, patch) => {
    setTransactions(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
    if (!isDemo) await sbUpdateTx(id, patch);
  };

  // Poll Supabase every 30s for new WhatsApp-sourced transactions (skipped in demo)
  useEffect(() => {
    if (!client || isDemo) return;
    const interval = setInterval(async () => {
      const txs = await sbGetTxs(client.clientId);
      if (txs.length !== transactions.length) setTransactions(txs);
    }, 30000);
    return () => clearInterval(interval);
  }, [client, isDemo, transactions.length]);

  const handleLogout = async () => {
    if (isDemo) {
      // Demo session — just reset state, no Supabase touch
      setClient(null); setTransactions([]); setDebts([]);
      setScreen("landing"); setView("dashboard");
      return;
    }
    localStorage.removeItem("pa_session");
    await supabase.auth.signOut().catch(()=>{});
    setClient(null); setTransactions([]); setDebts([]); setScreen("landing"); setView("dashboard");
  };

  // Demo banner CTA — exit demo and head to register
  const handleDemoToSignup = () => {
    setClient(null); setTransactions([]); setDebts([]);
    setScreen("register"); setView("dashboard");
  };

  // Computed data
  const plan = client?.plan ?? 0;                          // default Free
  const tier = TIERS.find(t => t.id === plan) || TIERS[0];

  // Free tier: visible data hidden beyond last 3 months (raw data stays in state
  // and Supabase — they get it back on upgrade)
  const visibleTransactions = (() => {
    if (plan !== 0 || isDemo) return transactions;
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    const cutoffKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return transactions.filter(t => (t.month || "") >= cutoffKey);
  })();

  const monthly = computeMonthly(visibleTransactions);
  const expenses = computeExpensePie(visibleTransactions);
  const scores = computeScores(monthly);
  const financialAge = calculateFinancialAge(scores);
  const overallScore = scores.length>0?(scores.reduce((s,sc)=>s+sc.score,0)/scores.length).toFixed(1):client?.score||"—";
  const exportData = (transactions, format = "csv") => {
    if (format === "csv") {
      const header = "Date,Type,Category,Amount,Note,Month\n";
      const rows = transactions.map(t =>
        `${t.created_at?.slice(0,10) || t.month},${t.type},${t.category},${t.amount},"${(t.note || "").replace(/"/g, '""')}",${t.month}`
      ).join("\n");
      const blob = new Blob([header + rows], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `unbroke-export-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
    } else {
      const blob = new Blob(
        [JSON.stringify(transactions, null, 2)],
        { type: "application/json" }
      );
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `unbroke-export-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
    }
  };
  
  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      "This permanently deletes all your transactions, debts, and settings. Export your data first if you want a copy. This cannot be undone."
    );
    if (!confirmed) return;
  
    await Promise.all([
      fetch(`${SUPABASE_URL}/rest/v1/transactions?client_id=eq.${client.clientId}`,
        { method: "DELETE", headers: sbHeaders() }),
      fetch(`${SUPABASE_URL}/rest/v1/debts?client_id=eq.${client.clientId}`,
        { method: "DELETE", headers: sbHeaders() }),
      fetch(`${SUPABASE_URL}/rest/v1/fire_settings?client_id=eq.${client.clientId}`,
        { method: "DELETE", headers: sbHeaders() }),
      fetch(`${SUPABASE_URL}/rest/v1/users?client_id=eq.${client.clientId}`,
        { method: "DELETE", headers: sbHeaders() }),
    ]);
  
    await supabase.auth.signOut().catch(() => {});
    handleLogout();
  };
  const VIEWS = { dashboard:"Financial Overview",privacy: "Privacy & Data", income:"Add Income", expenses:"Add Expense", analysis:"Behavioral Intelligence",shockinsights: "Shock Insights"
,benchmark:"Compare Yourself",sharecards:"Share Cards", scenarios:"Scenario Modeling", assistant:"AI Assistant", personality:"Money Personality", regret:"Regret Tracker", debt:"Debt Repayment", fire:"FIRE Calculator", bankimport: "Bank Statement Import",howto:"How to Use",report: "Monthly Behavioral Report",strategic: "Strategic Freedom Map" };

  if (screen==="landing") return (
  <UnbrokeLanding
    onGetStarted={()=>setScreen("register")}
    onSignIn={()=>setScreen("login")}
    onTryDemo={handleStartDemo}
  />
);
if (screen==="register") return <AppCtx.Provider value={{currency,theme:T,themeKey,setThemeKey,currencyCode,setCurrencyCode}}><RegisterScreen onLogin={handleLogin} onBack={()=>setScreen("login")}/></AppCtx.Provider>;
  if (screen==="login") return <AppCtx.Provider value={{currency,theme,themeKey,setThemeKey,currencyCode,setCurrencyCode}}><LoginScreen onLogin={handleLogin} onRegister={()=>setScreen("register")}/></AppCtx.Provider>;

  if (loading) return (
    <div style={{ minHeight: "100vh", background: AURORA.bg0, position: "relative",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "Geist, Geist, sans-serif", overflow: "hidden" }}>
      <AuroraBackground/>
      <div style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
        <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 28,
          fontWeight: 300, color: AURORA.amber, fontStyle: "italic", letterSpacing: "-0.01em",
          marginBottom: 8 }}>
          loading your wealth
        </div>
        <div style={{ fontSize: 10, color: AURORA.inkFaint, letterSpacing: "0.22em",
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace",
          animation: "pulse 1.5s ease-in-out infinite" }}>
          Unbroke Financial Intelligence
        </div>
      </div>
    </div>
  );

  return (
    <AppCtx.Provider value={{currency,theme:T,themeKey,setThemeKey,currencyCode,setCurrencyCode}}>
    
    <div style={{
      minHeight: "100vh",
      background: AURORA.bg0,
      color: AURORA.ink,
      fontFamily: "Geist, Geist, sans-serif",
      position: "relative",
      overflow: "hidden",
    }}>
      <AuroraBackground/>

      {/* Aurora Sidebar */}
    <AuroraSidebar
      view={view} setView={setView}
      client={client} tier={tier} plan={plan}
      overallScore={overallScore}
      onSettings={() => setShowSettings(s => !s)}
      onSignOut={handleLogout}
      settingsOpen={showSettings}
    />

      {/* Settings Overlay — glass styled, positioned next to AuroraSidebar */}
      {showSettings && (
        <div style={{
          position: "fixed", top: 18, bottom: 18, left: 268, width: 296, zIndex: 19,
          animation: "auroraRise 0.25s cubic-bezier(0.2, 0.8, 0.2, 1) both",
        }}>
          <div style={{
            height: "100%", overflowY: "auto",
            background: "linear-gradient(180deg, rgba(20,17,28,0.72), rgba(14,12,20,0.88))",
            backdropFilter: "blur(24px) saturate(140%)",
            WebkitBackdropFilter: "blur(24px) saturate(140%)",
            border: `1px solid ${AURORA.line}`, borderRadius: 22, padding: "26px 22px",
          }}>
            <div style={{ fontSize: 9, letterSpacing: "0.28em", color: AURORA.amberD,
              fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 4 }}>
              PREFERENCES
            </div>
            <div style={{ fontFamily: "Fraunces, 'Cormorant Garamond', serif", fontSize: 22,
              fontWeight: 300, color: AURORA.ink, fontStyle: "italic", marginBottom: 24 }}>
              fine-tune <span style={{ color: AURORA.amber }}>the vault</span>
            </div>

            {/* WhatsApp Link */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 9, letterSpacing: "0.22em", color: AURORA.inkFaint,
                fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 8 }}>
                WHATSAPP PHONE LINK
              </div>
              <div style={{ fontSize: 11, color: AURORA.inkFaint, marginBottom: 10, lineHeight: 1.6 }}>
                Link your WhatsApp number so transactions sent via the bot auto-appear here.
              </div>
              <WhatsAppPhoneLink clientId={client?.clientId} theme={T}/>
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{
                fontSize: 9, letterSpacing: "0.22em", color: AURORA.inkFaint,
                fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 8,
              }}>
                YOUR DATA
              </div>
              <div style={{ fontSize: 11, color: AURORA.inkFaint, marginBottom: 10, lineHeight: 1.6 }}>
                Export everything you've logged. Your data, your file.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button
                  onClick={() => exportData(transactions, "csv")}
                  style={{
                    padding: "9px 12px", background: "rgba(111,233,181,0.06)",
                    border: `1px solid rgba(111,233,181,0.25)`, borderRadius: 8,
                    color: "#6FE9B5", fontSize: 11, cursor: "pointer",
                    fontFamily: "Geist, Geist, sans-serif", textAlign: "left",
                  }}>
                  ⬇ Export as CSV
                </button>
                <button
                  onClick={() => exportData(transactions, "json")}
                  style={{
                    padding: "9px 12px", background: "rgba(111,233,181,0.06)",
                    border: `1px solid rgba(111,233,181,0.25)`, borderRadius: 8,
                    color: "#6FE9B5", fontSize: 11, cursor: "pointer",
                    fontFamily: "Geist, Geist, sans-serif", textAlign: "left",
                  }}>
                  ⬇ Export as JSON
                </button>
                <button
                  onClick={handleDeleteAccount}
                  style={{
                    padding: "9px 12px", background: "rgba(192,90,90,0.06)",
                    border: `1px solid rgba(192,90,90,0.25)`, borderRadius: 8,
                    color: "#C05A5A", fontSize: 11, cursor: "pointer",
                    fontFamily: "Geist, Geist, sans-serif", textAlign: "left",
                  }}>
                  ✕ Delete account + all data
                </button>
              </div>
            </div>
            {/* Currency */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 9, letterSpacing: "0.22em", color: AURORA.inkFaint,
                fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 12 }}>
                CURRENCY
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {CURRENCIES.map(c => (
                  <button key={c.code} onClick={() => setCurrencyCode(c.code)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                      background: currencyCode === c.code ? `${AURORA.amber}12` : "rgba(255,255,255,0.02)",
                      border: `1px solid ${currencyCode === c.code ? `${AURORA.amber}40` : AURORA.line}`,
                      borderRadius: 10, cursor: "pointer", textAlign: "left",
                      transition: "all 0.15s", fontFamily: "Geist, Geist, sans-serif",
                    }}>
                    <span style={{ fontSize: 15, fontFamily: "'JetBrains Mono', monospace",
                      color: currencyCode === c.code ? AURORA.amber : AURORA.inkSoft, minWidth: 28 }}>
                      {c.symbol}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: currencyCode === c.code ? AURORA.ink : AURORA.inkSoft,
                        fontWeight: currencyCode === c.code ? 500 : 400 }}>{c.code}</div>
                      <div style={{ fontSize: 10, color: AURORA.inkFaint }}>{c.name}</div>
                    </div>
                    {currencyCode === c.code && <span style={{ fontSize: 10, color: AURORA.amber }}>✓</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Theme (affects non-dashboard views) */}
            <div>
              <div style={{ fontSize: 9, letterSpacing: "0.22em", color: AURORA.inkFaint,
                fontFamily: "'JetBrains Mono', 'DM Mono', monospace", marginBottom: 6 }}>
                THEME · NON-DASHBOARD VIEWS
              </div>
              <div style={{ fontSize: 10, color: AURORA.inkFaint, marginBottom: 12, lineHeight: 1.55,
                fontStyle: "italic", fontFamily: "Fraunces, 'Cormorant Garamond', serif" }}>
                The dashboard stays Aurora; other views adopt your theme.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {Object.entries(THEMES).map(([key, th]) => (
                  <button key={key} onClick={() => setThemeKey(key)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                      background: themeKey === key ? `${AURORA.amber}12` : "rgba(255,255,255,0.02)",
                      border: `1px solid ${themeKey === key ? `${AURORA.amber}40` : AURORA.line}`,
                      borderRadius: 10, cursor: "pointer", textAlign: "left",
                      transition: "all 0.15s", fontFamily: "Geist, Geist, sans-serif",
                    }}>
                    <span style={{ fontSize: 14 }}>{th.icon}</span>
                    <span style={{ fontSize: 12, color: themeKey === key ? AURORA.ink : AURORA.inkSoft,
                      fontWeight: themeKey === key ? 500 : 400, flex: 1 }}>{th.name}</span>
                    <div style={{ display: "flex", gap: 3 }}>
                      {[th.gold, th.green, th.blue, th.red].map((c, i) => (
                        <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: c }}/>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main style={{
        marginLeft: showSettings ? 586 : 270,
        padding: "32px 40px 60px",
        position: "relative", zIndex: 2,
        minHeight: "100vh",
        transition: "margin-left 0.25s ease",
        animation: "auroraRise 0.7s cubic-bezier(0.2, 0.8, 0.2, 1) both",
      }}>
        {isDemo && <DemoBanner onSignup={handleDemoToSignup} onExit={handleLogout}/>}

        <AuroraTopBar
          client={client} monthly={monthly} currency={currency}
          setCurrencyCode={setCurrencyCode}
          viewTitle={VIEWS[view]}
        />
        <FinancialAgeCard financialAge={financialAge}/>
        {view==="dashboard" && <AuroraDashboardTimeAware
          monthly={monthly} expenses={expenses} scores={scores}
          transactions={visibleTransactions} currency={currency}
                                 client={client}/>}
        {view==="income"&&<AddTransaction transactions={visibleTransactions.filter(t=>t.type==="income")} onAdd={handleAddTx} onDelete={handleDeleteTx}/>}
        {view==="expenses"&&<AddTransaction transactions={visibleTransactions.filter(t=>t.type==="expense")} onAdd={handleAddTx} onDelete={handleDeleteTx}/>}
      
        {view==="analysis"&&<Analysis client={client} monthly={monthly} scores={scores} plan={plan}/>}
        {view==="scenarios"&&<Scenarios client={client} monthly={monthly} plan={plan}/>}
        {view==="personality"&&<MoneyPersonality client={client} transactions={visibleTransactions} monthly={monthly}/>}
        {view==="regret"&&<RegretTracker transactions={visibleTransactions} onUpdate={handleUpdateTx}/>}
        {view==="shockinsights" && (
          <ShockInsightsPanel transactions={visibleTransactions} monthly={monthly} currency={currency}/>
        )}
        {view==="benchmark" && (
          <CompareYourselfPanel monthly={monthly} transactions={visibleTransactions} currency={currency}/>
        )}
        {view==="sharecards" && (
          <ViralShareCard client={client} scores={scores} monthly={monthly}
            transactions={visibleTransactions} currency={currency}/>
        )}
        {view==="assistant"&&<Assistant client={client} monthly={monthly} scores={scores} plan={plan}/>}
        {view==="debt"&&<DebtTracker clientId={client?.clientId} transactions={visibleTransactions}/>}
        {view==="fire"&&<FIRECalculator client={client} monthly={monthly} transactions={visibleTransactions}/>}
        {view==="bankimport" && <BankImport onAdd={handleAddTx} transactions={visibleTransactions}/>}
        {view==="report" && <MonthlyReport client={client} transactions={visibleTransactions} monthly={monthly} plan={plan}/>}
        {view==="strategic" && <StrategicScenarios client={client} monthly={monthly} transactions={visibleTransactions} plan={plan}/>}
        {view==="howto"&&<HowToUse/>}
        {view==="privacy" && (
          <PrivacyDashboard
            client={client}
            transactions={transactions}
            exportData={exportData}
            handleDeleteAccount={handleDeleteAccount}
            isDemo={isDemo}
          />
        )}
        {/* Footer */}
        <div style={{ marginTop: 60, paddingTop: 24, borderTop: `1px solid ${AURORA.line}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontSize: 10, color: AURORA.inkFaint,
          fontFamily: "'JetBrains Mono', 'DM Mono', monospace", letterSpacing: "0.14em" }}>
          <span>UNBROKE · WEALTH INTELLIGENCE</span>
          <span>v2.0 · AURORA · {new Date().getFullYear()}</span>
        </div>
      </main>
    </div>
    </AppCtx.Provider>
  );
}
